/* eslint-disable no-self-assign */
/* eslint-disable no-control-regex */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
/* eslint-disable no-unused-expressions */
/* eslint-disable jsx-a11y/accessible-emoji */
// ════════════════════════════════════════════════════════════════
// SelfAssessment.jsx — GSA Rewrite (Chat Container)
//
// Shell: app shell (header, progress, chat area, input bar),
//        conversationHistory accumulator, step router,
//        QnA strip system, input bar routing
// Screens: Intro → ProblemArea → PainLocation →
//          SafetyScreening → CPG → Subtype → SelfAssessmentQ2 →
//          PreNPRS → TestIntro → MovementTest1 →
//          SelfReportROM → SelfReportTempo → RPE → FeelAffect →
//          TestIntro2 → MovementTest2 → Feedback2 →
//          Personalize → RecoveryScore → Plan
// Cross-cutting: ExitHandler, InactivityModal, HardStop
// Imported screens: PainLocationScreen, SafetyScreeningScreen
// ════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAssessment } from "../self-assesment/AssessmentContext";
import { useScreenVoice } from "./useScreenVoice";
import { generatePayloadForScreen, parseDataAfterInfo } from "./generatePayloadForScreen";
import { QnAOverlay } from "./QnAOverlay";
import { MYRA_MESSAGES, resolveMessage } from "./GSA_MYRA_MESSAGES";
import * as CONST from "./GSA_CONSTANTS";
import PainLocationScreen from "./PainLocationScreen";
import SafetyScreeningScreen from "./SafetyScreeningScreen";
import styles from "./SelfAssessment.module.css";
import { AnalyticsService } from "../self-assesment/SelfAssesment";
import MyraInteraction from "../self-assesment/MyraInteractionController";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";
import MyraChatService from "../voice-agent/MyraChatService";

// ════════════════════════════════════════════════════════════════
// UTILITY CLASSES
// ════════════════════════════════════════════════════════════════

class TimeTracker {
  startGSATimer() {
    this._gsaStart = Date.now();
  }
  getGSADuration() {
    return Math.round((Date.now() - this._gsaStart) / 1000);
  }
}

class ScreenFlowTracker {
  constructor() {
    this._flow = [];
    this._current = null;
  }
  enterScreen(name) {
    this._current = { screen: name, enter: Date.now() };
  }
  exitScreen(name, meta = {}) {
    if (this._current) {
      this._current.exit = Date.now();
      this._current.duration = this._current.exit - this._current.enter;
      Object.assign(this._current, meta);
      this._flow.push(this._current);
      this._current = null;
    }
  }
  getScreenFlow() {
    return this._flow;
  }
}

// ════════════════════════════════════════════════════════════════
// SHELL CONSTANTS & HELPERS
// ════════════════════════════════════════════════════════════════

const STEP_CONFIG = {
  intro: { label: "Getting Started", progress: 5 },
  problem_area: { label: "Problem Area", progress: 10 },
  pain_location: { label: "Pinpoint Your Pain", progress: 18 },
  safety_screening: { label: "Safety Check", progress: 22 },
  cpg_questions: { label: "Pain Character", progress: 30 },
  subtype_questioning: { label: "Pain Pattern", progress: 36 },
  self_assessment: { label: "Pain Pattern", progress: 40 },
  nprs_symptoms: { label: "Pain Level", progress: 45 },
  test1_demo: { label: "Movement Test 1", progress: 50 },
  mode_selection: { label: "Choose Mode", progress: 51 },
  camera_permission: { label: "Camera Access", progress: 52 },
  movement_test_1: { label: "Movement Test 1", progress: 55 },
  self_report_rom: { label: "Self-Report", progress: 58 },
  self_report_tempo: { label: "Self-Report", progress: 60 },
  rpe: { label: "How Challenging", progress: 65 },
  feel_affect: { label: "Symptom Response", progress: 70 },
  test2_demo: { label: "Movement Test 2", progress: 74 },
  movement_test_2: { label: "Movement Test 2", progress: 78 },
  feedback2: { label: "Movement Comparison", progress: 82 },
  personalize: { label: "Personalize Your Plan", progress: 88 },
  recovery_score: { label: "Recovery Compass", progress: 94 },
  plan: { label: "Your Recovery Plan", progress: 100 },
  hard_stop: { label: "Safety Recommendation", progress: 100 },
};

const AREA_MAP = { "lower back": "LBP", back: "LBP", hip: "HIP", thigh: "THIGH", knee: "KNEE", leg: "KNEE" };
function detectAreaKeyword(text) {
  const lower = text.toLowerCase();
  for (const [kw, id] of Object.entries(AREA_MAP)) {
    if (lower.includes(kw)) return id;
  }
  return null;
}

const ADVANCE_KEYWORDS = ["start", "ready", "let's go", "begin"];
function isAdvanceCommand(text) {
  const lower = text.toLowerCase();
  if (lower === "no" || /^no[,.]?\s/i.test(lower)) return true;
  return ADVANCE_KEYWORDS.some((kw) => lower.includes(kw));
}

function _delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

let _historyId = 0;
function nextId() {
  return ++_historyId;
}

// ── SVG icons ──
const SEND_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const CHAT_ICON_SVG = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const VOICE_ICON_SVG = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
  </svg>
);
const CHEVRON_DOWN = (
  <svg className={styles.accordionChevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ── Intro CTA config (data-driven, extensible) ──
const INTRO_CTAS = [
  { id: "video", label: "Watch a Video", type: "info" },
  { id: "assessment", label: "What's the Assessment?", type: "info" },
  { id: "myra", label: "Who is Myra?", type: "info" },
  { id: "start", label: "Let's get Started...", type: "start" },
];

function getAssessmentSteps() {
  if (MYRA_MESSAGES.intro?.assessmentSteps) return MYRA_MESSAGES.intro.assessmentSteps;
  return [
    { title: "A few questions about your pain", body: "I'll ask where your pain started and a few targeted questions about what it feels like — sharp, dull, radiating. This helps me understand what's going on so I can pick the right movement checks for you." },
    { title: "A quick safety check", body: "Before any movements, I'll run through a short checklist to make sure it's safe to continue. If anything comes up, I'll recommend you see a provider first — your safety always comes first." },
    { title: "1–2 simple movement checks", body: "I'll pick 1 or 2 gentle movements based on your answers. I'll show you a demo video first, then guide you through it. The goal is never to push through pain — it's to help me understand your starting point." },
    { title: "A few optional personal details", body: "After the movement checks, you can share details like your age, activity level, and goals. The more I know, the more accurate your plan. If you skip this, I'll still build a plan with conservative assumptions." },
    { title: "Your personalized recovery plan", body: "Based on everything I've learned, I'll create a recovery plan with exercises tailored to you — including what to do, how often, and why each exercise was chosen." },
  ];
}

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════

export default function SelfAssessment2({ displayMode = "fullscreen", onStartAssessment }) {
  const { assessmentData, updateAssessmentData } = useAssessment();
  const [currentStep, setCurrentStep] = useState("intro");

  // ── Conversation history (message accumulator) ──
  const [conversationHistory, setConversationHistory] = useState([]);
  const [typingVisible, setTypingVisible] = useState(false);

  // ── Input bar ──
  const [inputText, setInputText] = useState("");
  const [introProminent, setIntroProminent] = useState(false);
  const [placeholderText, setPlaceholderText] = useState("Ask me anything or share what's going on");

  // ── Shell UI ──
  const [progressPercent, setProgressPercent] = useState(5);
  const [stepLabel, setStepLabel] = useState("Getting Started");
  const [mode, setMode] = useState(MyraInteraction.getMode() || "chat");

  // ── Intent detection (stubbed) ──
  const [detectedArea, setDetectedArea] = useState(null);
  const [detectedDuration, setDetectedDuration] = useState(null); // eslint-disable-line
  const [detectedTrigger, setDetectedTrigger] = useState(null); // eslint-disable-line
  // Intent matching logic deferred — separate project

  // ── Scroll management ──
  const chatAreaRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const scrollSentinelRef = useRef(null);

  // ── QnA overlay ──
  const [qaOpen, setQaOpen] = useState(false);
  const qaRef = useRef(null);

  // ── Cross-cutting overlays ──
  const [showExitHandler, setShowExitHandler] = useState(false);
  const [showInactivityModal, setShowInactivityModal] = useState(false);

  // ── Screen-specific command refs (for input bar routing) ──
  const introAdvanceRef = useRef(null);
  const paCommandRef = useRef(null);

  // ── QA strip tracking ──
  const qaStripIdsRef = useRef(new Set());

  // ── Embedded → fullscreen transition tracking ──
  const prevDisplayModeRef = useRef(displayMode);

  const isVoiceMode = MyraInteraction.getMode() === "voice";

  // ═══════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    window.trSessionId = localStorage?.getItem("trSessionId") || null;
    if (!window?.trSessionId && AnalyticsService) AnalyticsService?.startSession();
    window.timeTracker = new TimeTracker();
    window.timeTracker?.startGSATimer();
    window.screenTracker = new ScreenFlowTracker();
    localStorage.setItem("cameFromGsaV2", "false");

    window.globalCrossClicked = () => setShowExitHandler(true);
    window.updateMicModalCancel = () => {
      const updated = { ...assessmentData, userCancelMic: true };
      updateAssessmentData(updated);
      if (AnalyticsService) AnalyticsService?.trackStep(updated);
    };

    return () => {
      window.globalCrossClicked = null;
      window.updateMicModalCancel = null;
    };
  }, []);
  useEffect(() => {
    if (displayMode === "fullscreen" && currentStep === "intro") {
      setCurrentStep("problem_area");
      pushToHistory({ type: "section-header", label: STEP_CONFIG.problem_area.label, id: nextId() });
    }
  }, [displayMode, currentStep]);

  // ── Global AUTO_PAUSE command ──
  useEffect(() => {
    const timer = setTimeout(() => {
      const existing = window.currentCommandsAudio || {};
      window.currentCommandsAudio = {
        ...existing,
        AUTO_PAUSE: () => {
          if (isVoiceMode) {
            VoiceService?.doSay("I've paused. Take your time...");
          } else {
            setShowInactivityModal(true);
          }
        },
      };
    }, 200);
    return () => clearTimeout(timer);
  }, [currentStep]);

  // ── Embedded → fullscreen deferred advance ──
  useEffect(() => {
    if (prevDisplayModeRef.current === "embedded" && displayMode === "fullscreen" && currentStep === "intro") {
      if (!window.trSessionId && AnalyticsService) AnalyticsService.startSession();
      localStorage.setItem("startedSelf", "1");
      if (window.gtag) window.gtag("event", "conversion", { send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD", value: 1.0, currency: "USD" });
      setCurrentStep("problem_area");
    }
    prevDisplayModeRef.current = displayMode;
  }, [displayMode]);

  const pushToHistory = useCallback((...entries) => {
    setConversationHistory((prev) => [...prev, ...entries]);
  }, []);

  // ═══════════════════════════════════════════════════════════
  // PERSISTENCE
  // ═══════════════════════════════════════════════════════════

  const persistAndNavigate = useCallback(
    (newFields, nextStep) => {
      const updatedData = {
        ...assessmentData,
        ...newFields,
        screenFlow: window.screenTracker?.getScreenFlow() || [],
        session_last_update: new Date().toISOString(),
      };
      updatedData.analyticsId = window.trSessionId;
      updateAssessmentData(updatedData);
      localStorage.setItem("assessmentData", JSON.stringify(updatedData));
      if (AnalyticsService) AnalyticsService.trackStep(updatedData);

      // Push divider + section header for the incoming screen
      // Skip section header if the next step shares the same label as the current step
      const nextConfig = STEP_CONFIG[nextStep];
      const skipDividerForSteps = ["test1_demo", "mode_selection"];
      const shouldSkipDivider = skipDividerForSteps.includes(nextStep);
      const skipHeader = nextStep === "feedback2";
      if (nextConfig && nextStep !== "hard_stop") {
        const currentConfig = STEP_CONFIG[currentStep];
        if (window.test2WasSkipped) {
          console.log("SKIPPING HEADER AND DIVIDER , 2nd movement test moves to plan-recommendation");
        } else if (shouldSkipDivider) {
          console.log("SKIPPING HEADER AND DIVIDER , SETTING IT FROM THE PRE_NPRS_SCREEN");
        } else if (currentConfig && currentConfig.label === nextConfig.label) {
          pushToHistory({ type: "divider", id: nextId() });
        } else {
          pushToHistory({ type: "divider", id: nextId() }, { type: "section-header", label: nextConfig.label, id: nextId() });
        }
      }

      setCurrentStep(nextStep);
    },
    [assessmentData, updateAssessmentData, pushToHistory, currentStep],
  );
  useEffect(() => {
    if (mode === "chat") {
      VoiceService._releaseMic();
      VoiceService.stopAllVoiceActivity();
    }
  }, [mode, qaOpen]);

  // ═══════════════════════════════════════════════════════════
  // SCROLL MANAGEMENT
  // ═══════════════════════════════════════════════════════════

  // const scrollToBottom = useCallback(() => {
  //   if (userScrolledUpRef.current) return;
  //   requestAnimationFrame(() => {
  //     const el = chatAreaRef.current;
  //     if (el) el.scrollTop = el.scrollHeight;
  //   });
  // }, []);

  const scrollToBottom = useCallback(() => {
    if (userScrolledUpRef.current) return;

    requestAnimationFrame(() => {
      scrollSentinelRef.current?.scrollIntoView({ block: "end" });
    });
  }, []);

  // Re-scroll after images in the chat finish loading. Widgets like the body
  // outline include images whose height isn't known until load, so an earlier
  // scrollToBottom call can land short of the true bottom.
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    const handleLoad = () => scrollToBottom();
    el.addEventListener("load", handleLoad, { capture: true });
    return () => el.removeEventListener("load", handleLoad, { capture: true });
  }, [scrollToBottom]);

  // Re-scroll after videos report metadata. Videos don't know their height
  // until `loadedmetadata` fires — the `load` listener above doesn't catch
  // that. Used by TestIntroScreen and TestIntro2Screen.
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    const handleLoadedMetadata = (e) => {
      if (e.target && e.target.tagName === "VIDEO") {
        scrollToBottom();
        setTimeout(scrollToBottom, 100);
      }
    };
    el.addEventListener("loadedmetadata", handleLoadedMetadata, { capture: true });
    return () => el.removeEventListener("loadedmetadata", handleLoadedMetadata, { capture: true });
  }, [scrollToBottom]);

  // Re-scroll whenever the chat area's content changes. Catches state-driven
  // reveals (CameraPermissionScreen's `setReady(true)`, TestIntroScreen's
  // mode-select card, RecoveryScoreScreen's async API data) that don't push
  // to `conversationHistory` and so escape the second-pass effect below.
  // Debounced to 100ms; delayed re-passes at 800ms and 1500ms catch content
  // that resolves height after the initial mutation settles.
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    let mutationTimer = null;
    let delayedTimer1 = null;
    let delayedTimer2 = null;
    const observer = new MutationObserver(() => {
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(() => {
        scrollToBottom();
        clearTimeout(delayedTimer1);
        clearTimeout(delayedTimer2);
        delayedTimer1 = setTimeout(scrollToBottom, 800);
        delayedTimer2 = setTimeout(scrollToBottom, 1500);
      }, 100);
    });
    observer.observe(el, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      clearTimeout(mutationTimer);
      clearTimeout(delayedTimer1);
      clearTimeout(delayedTimer2);
    };
  }, [scrollToBottom]);

  // ═══════════════════════════════════════════════════════════
  // STEP LABEL + PROGRESS SYNC
  // ═══════════════════════════════════════════════════════════

  useEffect(() => {
    const config = STEP_CONFIG[currentStep];
    if (config) {
      setStepLabel(config.label);
      setProgressPercent(config.progress);
    }
    // Reset scroll guard so new screens always scroll into view
    userScrolledUpRef.current = false;
    scrollToBottom();
  }, [currentStep, scrollToBottom]);

  useEffect(() => {
    scrollToBottom();
    // Second pass after layout settles. The first scrollToBottom fires before
    // the live widget (rendered by renderScreen) has finished mounting —
    // especially when a screen uses setReady(true) to reveal its widget after
    // the Myra prompt. This second call catches the final content height.
    const t = setTimeout(() => scrollToBottom(), 300);
    return () => clearTimeout(t);
  }, [conversationHistory, typingVisible, scrollToBottom]);

  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    function onScroll() {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
      userScrolledUpRef.current = !atBottom;
    }
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // ═══════════════════════════════════════════════════════════
  // HISTORY HELPERS
  // ═══════════════════════════════════════════════════════════

  const showMyraMessage = useCallback(
    async (text, typingMs = 800) => {
      setTypingVisible(true);
      await _delay(typingMs);
      setTypingVisible(false);
      const entry = { type: "myra", text, id: nextId() };
      pushToHistory(entry);
      return entry;
    },
    [pushToHistory],
  );

  // ═══════════════════════════════════════════════════════════
  // QNA OVERLAY PAYLOAD
  // ═══════════════════════════════════════════════════════════

  const screenPayload = useMemo(() => {
    const keyMap = {
      intro: "landing",
      problem_area: "problem_area",
      pain_location: "pain_location",
      safety_screening: "SafetyScreeningScreen",
      cpg_questions: "cpg_questions",
      subtype_questioning: "subtype_questioning",
      self_assessment: "self_assessment",
      nprs_symptoms: "nprs_symptoms",
      test1_demo: "test1_demo",
      movement_test_1: "movement_test_1",
      self_report_rom: "self_report_compare",
      self_report_tempo: "self_report_speed",
      rpe: "test1_rpe",
      feel_affect: "test1_symptoms",
      test2_demo: "test2_demo",
      movement_test_2: "movement_test_2",
      feedback2: "symptom2",
      personalize: "personalize",
      recovery_score: "recovery_score",
      plan: "plan_screen",
    };
    const key = keyMap[currentStep];
    if (!key) return {};
    return generatePayloadForScreen(key, assessmentData);
  }, [currentStep, assessmentData]);

  const inlineChatHistoryRef = useRef([]);

  async function handleInlineChat(text) {
    // Build history for context
    const newHistory = [...inlineChatHistoryRef.current, { role: "user", content: text }];

    // Show typing indicator
    setTypingVisible(true);

    try {
      const screenPayloadForChat = generatePayloadForScreen("SafetyScreeningScreen", {
        ...assessmentData,
        safetySelections: window.safetySelections,
        safetySelectionIds: window.safetySelectionIds,
      });

      const response = await MyraChatService.sendMessage(text, window.trSessionId, newHistory, newHistory.length, screenPayloadForChat, currentStep);

      setTypingVisible(false);

      // const answerText = response?.response || response?.speech || "I'm not sure how to answer that right now.";
      const answerText = response?.response || response?.speech || "";

      // Push Myra's response to main chat history
      if (answerText) {
        pushToHistory({ type: "myra", text: answerText, id: nextId() });
      }

      // Update inline history for multi-turn context
      inlineChatHistoryRef.current = [...newHistory, { role: "assistant", content: answerText }];

      // Execute any command the backend returned (SAFETY_CONTINUE, SAFETY_STOP, etc.)
      if (response?.action && window.currentCommandsAudio?.[response.action]) {
        setTimeout(() => {
          window.currentCommandsAudio[response.action](response.value, response.speech, response.intent);
        }, 500);
      }
    } catch (err) {
      setTypingVisible(false);
      pushToHistory({
        type: "myra",
        text: "Sorry, something went wrong. Please try again.",
        id: nextId(),
      });
    } finally {
      setTypingVisible(false);
    }
  }

  // ═══════════════════════════════════════════════════════════
  // INPUT BAR ROUTING (Rule 1)
  // ═══════════════════════════════════════════════════════════

  async function handleInputSubmit() {
    const text = inputText.trim();
    if (!text) return;
    setInputText("");
    if (window.notOpenQNA === true) {
      pushToHistory({ type: "user", text, id: nextId() });
      await handleInlineChat(text);
      return;
    }

    // Intro
    if (currentStep === "intro") {
      if (isAdvanceCommand(text)) {
        pushToHistory({ type: "user", text, id: nextId() });
        introAdvanceRef.current?.(text, null);
        return;
      }
      // const area = detectAreaKeyword(text);
      // if (area) {
      //   pushToHistory({ type: "user", text, id: nextId() });
      //   setDetectedArea(area);
      //   await showMyraMessage("Thanks for sharing that. I'll keep that in mind as we go. Let me start by confirming where your pain began.", 900);
      //   introAdvanceRef.current?.(text, area);
      //   return;
      // }
      // Non-command → overlay (no user bubble in main chat)
      detectIntents(text);
      setTypingVisible(true);
      qaRef.current?.addExchange?.(text, null, "typed", true, () => setTypingVisible(false));
      window.audioPlayer?.unlockAudioForChat?.();
      // setQaOpen(true);
      setPlaceholderText("Ask a question or type 'ready'…");
      setIntroProminent(false);
      return;
    }

    // Problem Area
    if (currentStep === "problem_area") {
      const area = detectAreaKeyword(text);
      if (area) {
        pushToHistory({ type: "user", text, id: nextId() });
        paCommandRef.current?.(area);
        return;
      }
    }

    // All other screens → overlay
    setTypingVisible(true);
    qaRef.current?.addExchange?.(text, null, "typed", true, () => setTypingVisible(false));
    window.audioPlayer?.unlockAudioForChat?.();
    // setQaOpen(true);
  }

  function detectIntents(text) {
    // Intent matching logic deferred — separate project
    const area = detectAreaKeyword(text);
    if (area) setDetectedArea(area);
  }

  // ═══════════════════════════════════════════════════════════
  // Q&A STRIP SYSTEM (Rule 6)
  // ═══════════════════════════════════════════════════════════

  function handleQaOverlayClose() {
    setQaOpen(false);
    const exchanges = qaRef.current?.getExchanges?.() || [];
    const newStrips = [];
    exchanges.forEach((ex) => {
      if (!qaStripIdsRef.current.has(ex.id)) {
        qaStripIdsRef.current.add(ex.id);
        newStrips.push({ type: "qa-strip", id: nextId(), qaId: ex.id, question: ex.question });
      }
    });
    if (newStrips.length > 0) pushToHistory(...newStrips);
  }

  function handleStripClick(qaId) {
    qaRef.current?.focusExchange?.(qaId);
    window.audioPlayer?.unlockAudioForChat?.();
    setQaOpen(true);
  }

  // ═══════════════════════════════════════════════════════════
  // HISTORY RENDERER
  // ═══════════════════════════════════════════════════════════

  function renderHistoryEntry(entry) {
    const isProblemAreaHeading = entry.label === STEP_CONFIG.problem_area.label;
    switch (entry.type) {
      case "myra":
        return (
          <div key={entry.id} className={styles.msgRowMyra}>
            <div className={styles.msgAvatar}>M</div>
            <div className={styles.msgBubbleMyra} dangerouslySetInnerHTML={{ __html: entry.text }} />
          </div>
        );
      case "user":
        return (
          <div key={entry.id} className={styles.msgRowUser}>
            <div className={styles.msgBubbleUser}>{entry.text}</div>
          </div>
        );
      case "divider":
        return <div key={entry.id} className={styles.sectionDivider} />;
      case "section-header":
        return (
          <div key={entry.id} className={`${styles.sectionHeader}${isProblemAreaHeading ? ` ${styles.sectionHeaderProblemArea}` : ""}`}>
            {entry.label}
          </div>
        );
      case "voice-indicator":
        return (
          <div key={entry.id} className={styles.inlineUi}>
            <div className={styles.myraVoiceIndicator}>
              {VOICE_ICON_SVG}
              <span>Myra will announce this screen</span>
            </div>
          </div>
        );
      case "qa-strip":
        return (
          <div key={entry.id} className={styles.qaStrip} onClick={() => handleStripClick(entry.qaId)}>
            <span className={styles.qaStripIcon}>💬</span>
            <span className={styles.qaStripText}>You asked: {entry.question}</span>
            <span className={styles.qaStripArrow}>›</span>
          </div>
        );
      case "frozen-widget":
        return renderFrozenWidget(entry);
      case "pain-location-summary":
        return <PainLocationScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "safety_screening")} readOnly={true} entry={entry} />;
      case "safety-summary":
        return <SafetyScreeningScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "cpg_questions")} onHardStop={(source) => persistAndNavigate({ safetyScreenResult: "FLAGGED", hardStopSource: source }, "hard_stop")} readOnly={true} entry={entry} />;
      default:
        return null;
    }
  }

  function renderFrozenWidget(entry) {
    if (entry.widgetType === "problem_area") {
      const { areas, selectedArea, ageConfirmed } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          {/* <p className={styles.paSubtitle}>You can pinpoint multiple locations on the next screen.</p> */}
          <div className={styles.paCards}>
            {areas.map(([id, path]) => (
              <div key={id} className={`${styles.paCard} ${selectedArea === id ? styles.selected : ""} ${styles.locked}`}>
                {path.name}
              </div>
            ))}
          </div>
          <div className={styles.ageConfirm}>
            <input type="checkbox" checked={ageConfirmed} disabled readOnly />
            <label>I confirm I am 18 years of age or older</label>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "cpg_options" || entry.widgetType === "option_cards") {
      const { options, selectedId } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.optionCards}>
            {options.map((o) => (
              <div key={o.id} className={`${styles.optionCard} ${selectedId === o.id ? styles.selected : ""} ${styles.locked}`}>
                {o.label}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (entry.widgetType === "yesno") {
      const { selectedAnswer } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.yesnoRow}>
            <button className={`${styles.yesnoBtn} ${selectedAnswer === "yes" ? styles.selected : ""}`} disabled>
              Yes
            </button>
            <button className={`${styles.yesnoBtn} ${selectedAnswer === "no" ? styles.selected : ""}`} disabled>
              No / Unsure
            </button>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "pre_nprs_pain") {
      const { selectedValue } = entry.data;
      const painColor = (val) => (val <= 3 ? "var(--green)" : val <= 7 ? "var(--amber)" : "var(--red)");
      const painTextColor = (val) => (val <= 3 ? "var(--green-text)" : val <= 7 ? "var(--amber-text)" : "var(--red-text)");
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.painGrid}>
            {Array.from({ length: 11 }, (_, i) => {
              const isSelected = selectedValue === i;
              return (
                <div
                  key={i}
                  className={`${styles.painCircle} ${isSelected ? styles.selected : ""}`}
                  style={{
                    borderColor: painColor(i),
                    backgroundColor: isSelected ? painColor(i) : "white",
                    color: isSelected ? "white" : painTextColor(i),
                    transform: isSelected ? "scale(1.1)" : "scale(1)",
                    pointerEvents: "none",
                  }}
                >
                  {i}
                </div>
              );
            })}
          </div>
          <div className={styles.painLabels}>
            <span className={styles.noPain}>No Pain</span>
            <span className={styles.worstPain}>Worst Pain</span>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "pre_nprs_duration") {
      const { selectedValue } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.durationOptions} style={{ marginTop: "8px" }}>
            {CONST.DURATION_OPTIONS.map((o) => (
              <div key={o.id} className={`${styles.durationOption} ${selectedValue === o.value ? styles.selected : ""}`} style={{ pointerEvents: "none" }}>
                {o.label}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (entry.widgetType === "voice_warning") {
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.voiceWarning}>
            <span className={styles.voiceWarningIcon}>🔊</span>
            <p>Myra will speak out loud during movement sections</p>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "camera_permission") {
      const { selectedAnswer } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.modeSelectCard}>
            <div className={styles.modeSelectIconRow}>
              <div className={styles.cameraAccessIcon}>📷</div>
              <p className={styles.modeSelectLabel}>Camera Access</p>
            </div>
            <p className={styles.paSubtitle}>Your camera stays on your device — we don't record or store anything</p>
            <div className={styles.feedback2Options}>
              <button className={`${styles.feedback2Btn} ${selectedAnswer === "decline" ? styles.selected : ""} ${styles.locked}`} disabled>
                No camera
              </button>
              <button className={`${styles.videoBtnPrimary} ${selectedAnswer === "allow" ? styles.selected : ""} ${styles.locked}`} disabled>
                Use camera
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "mode_selection") {
      const { selectedMode } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.modeSelectCard}>
            <p className={styles.modeSelectPrompt}>{MYRA_MESSAGES.modeSelection?.prompt}</p>
            <div className={styles.modeSelectOptions}>
              <button className={`${styles.modeSelectBtn} ${selectedMode === "voice" ? styles.selected : ""} ${styles.locked}`} disabled>
                <span className={styles.modeSelectIcon}>🎤</span>
                <span className={styles.modeSelectLabel}>{MYRA_MESSAGES.modeSelection?.talkLabel}</span>
                <span className={styles.modeSelectDesc}>{MYRA_MESSAGES.modeSelection?.talkDesc}</span>
              </button>
              <button className={`${styles.modeSelectBtn} ${selectedMode === "chat" ? styles.selected : ""} ${styles.locked}`} disabled>
                <span className={styles.modeSelectIcon}>💬</span>
                <span className={styles.modeSelectLabel}>{MYRA_MESSAGES.modeSelection?.chatLabel}</span>
                <span className={styles.modeSelectDesc}>{MYRA_MESSAGES.modeSelection?.chatDesc}</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "demo_video") {
      const { testName: demoTestName, videoUrl } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <p className={styles.paSubtitle}>
            Demo: <strong>{demoTestName}</strong>
          </p>
          <div className={styles.videoContainer}>
            <video src={videoUrl} controls playsInline preload="metadata" />
          </div>
        </div>
      );
    }

    if (entry.widgetType === "sr_cards") {
      const { heading, options, selectedId, subheading } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.srScreen}>
            <h3 className={styles.srHeading}>{heading}</h3>
            <p className={styles.srSubheading}>{subheading}</p>
            <div className={styles.srOptions}>
              {options.map((opt) => (
                <div key={opt.id} className={`${styles.srCard} ${selectedId === opt.id ? styles.selected : ""} ${styles.locked}`}>
                  <h4 className={styles.srCardTitle}>{opt.title}</h4>
                  <p className={styles.srCardSubtitle}>{opt.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "rpe_options") {
      const { selectedValue } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.rpeOptions}>
            {CONST.RPE_OPTIONS.map((o) => (
              <button key={o.value} className={`${styles.rpeBtn} ${selectedValue === o.value ? styles.selected : ""} ${styles.locked}`} disabled>
                {o.label}
              </button>
            ))}
          </div>
          <div className={styles.rpeLabels}>
            <span className={styles.rpeLow}>No Effort</span>
            <span className={styles.rpeHigh}>Max Effort</span>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "affect_options") {
      const { selectedValue } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.affectOptions}>
            {CONST.FEEL_AFFECT_OPTIONS.map((o) => (
              <div key={o.id} className={`${styles.affectBtn} ${selectedValue === o.value ? styles.selected : ""} ${styles.locked}`}>
                {o.label}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (entry.widgetType === "hard_stop_card") {
      const { detail } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.hardStopCard}>
            <div className={styles.hardStopIcon}>🩺</div>
            <div className={styles.hardStopTitle}>We recommend in-person care</div>
            <p className={styles.hardStopText}>{detail}</p>
          </div>
        </div>
      );
    }
    if (entry.widgetType === "safety_stop_card") {
      const { detail } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.hardStopCard}>
            <div className={styles.hardStopIcon}>🩺</div>
            <div className={styles.hardStopTitle}>We recommend waiting</div>
            <p className={styles.hardStopText}>{detail}</p>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "safety_advisory_card") {
      const { detail, choice } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #f59e0b",
              borderRadius: "0.75rem",
              padding: "1.25rem",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>⚠️</div>
            <p style={{ fontSize: "0.875rem", color: "#4b5563", lineHeight: 1.5 }}>{detail}</p>
            <p style={{ fontSize: "0.8rem", color: "#9ca3af", marginTop: "0.75rem", fontStyle: "italic" }}>{choice === "leave" ? "You chose to come back later." : ""}</p>
          </div>
        </div>
      );
    }

    if (entry.widgetType === "pain_location_text") {
      const { zoneText } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <p style={{ fontSize: "13px", color: "#6b7280", textAlign: "center" }}>Selected: {zoneText}</p>
        </div>
      );
    }

    if (entry.widgetType === "safety_checklist") {
      const { result, labels } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <p style={{ fontSize: "13px", color: "#6b7280", textAlign: "center" }}>{result === "none" ? "No symptoms checked" : `Checked: ${labels}`}</p>
        </div>
      );
    }

    if (entry.widgetType === "feedback2") {
      const { selectedValue } = entry.data;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.feedback2Options}>
            {CONST.FEEDBACK2_OPTIONS.map((o) => (
              <div key={o.id} className={`${styles.feedback2Btn} ${selectedValue === o.value ? styles.selected : ""} ${styles.locked}`}>
                {o.label}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (entry.widgetType === "personalize_group") {
      const { groupId, result, formData: savedFormData = {}, selectedGoals: savedGoals = [] } = entry.data;
      const group = CONST.PERSONALIZE_GROUPS.find((g) => g.id === groupId);
      if (!group) return null;

      const numberFields = group.fields.filter((f) => f.type === "number");
      const otherFields = group.fields.filter((f) => f.type !== "number");
      const wasSkipped = result === "skipped";

      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.pzGroup} style={{ pointerEvents: "none", opacity: wasSkipped ? 0.55 : 0.9 }}>
            {/* Number fields in pairs — read-only reproduction */}
            {numberFields.length > 0 &&
              (() => {
                const rows = [];
                for (let i = 0; i < numberFields.length; i += 2) {
                  rows.push(
                    <div className={styles.pzFieldRow} key={numberFields[i].id}>
                      <div className={styles.pzField}>
                        <label className={styles.formLabel}>{numberFields[i].label}</label>
                        <input type="number" className={styles.formInput} value={savedFormData[numberFields[i].id] || ""} disabled readOnly />
                      </div>
                      {i + 1 < numberFields.length && (
                        <div className={styles.pzField}>
                          <label className={styles.formLabel}>{numberFields[i + 1].label}</label>
                          <input type="number" className={styles.formInput} value={savedFormData[numberFields[i + 1].id] || ""} disabled readOnly />
                        </div>
                      )}
                    </div>,
                  );
                }
                return rows;
              })()}

            {/* Option and goal fields — read-only reproduction */}
            {otherFields.map((field) => {
              if (field.type === "goals") {
                return (
                  <div className={styles.pzField} key={field.id}>
                    <label className={styles.formLabel}>{field.label}</label>
                    <div className={styles.pzGoalWrapper}>
                      <div className={styles.pzGoalRow}>
                        {field.options.map((opt) => (
                          <div key={opt.id} className={`${styles.pzGoalItem} ${savedGoals.includes(opt.value) ? styles.selected : ""}`}>
                            <span className={styles.pzGoalLabel}>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              if (field.type === "options") {
                return (
                  <div className={styles.pzField} key={field.id}>
                    <label className={styles.formLabel}>{field.label}</label>
                    <div className={styles.pzOptionRow}>
                      {field.options.map((opt) => (
                        <button key={opt.id} className={`${styles.pzOptBtn} ${savedFormData[field.id] === opt.value ? styles.selected : ""}`} disabled>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }

    if (entry.widgetType === "rcs") {
      const { score = 0, diagnosis = {} } = entry.data;
      const circumferenceFrozen = 2 * Math.PI * 65;
      return (
        <div key={entry.id} className={styles.inlineUi}>
          <div className={styles.rcsScreen} style={{ pointerEvents: "none", opacity: 0.9 }}>
            <h2 className={styles.rcsTitle}>Recovery Compass</h2>
            <p className={styles.rcsSubtitle}>The Starting Point of Your Recovery Journey</p>

            <div className={styles.rcsScoreSection}>
              <span className={styles.rcsScoreLabel}>Your Score</span>
              <div className={styles.rcsRingContainer}>
                <svg viewBox="0 0 160 160" className={styles.rcsRingSvg}>
                  <defs>
                    <linearGradient id={`scoreGradFrozen-${entry.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#16a34a" />
                      <stop offset="100%" stopColor="#15803d" />
                    </linearGradient>
                  </defs>
                  <circle cx="80" cy="80" r="65" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                  <circle cx="80" cy="80" r="65" fill="none" stroke={`url(#scoreGradFrozen-${entry.id})`} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumferenceFrozen} strokeDashoffset={circumferenceFrozen * (1 - score / 100)} transform="rotate(-90 80 80)" />
                </svg>
                <span className={styles.rcsScoreNumber}>{score}</span>
              </div>
            </div>

            <p className={styles.rcsExplanation}>Your Recovery Compass Score starts at {score}, reflecting your current recovery stage. As you follow your personalized plan over the next 8 weeks, this score is designed to improve steadily, with a goal of reaching closer to 100.</p>

            <div className={styles.rcsDiagnosisCard}>
              <h3 className={styles.rcsDiagnosisHeadline}>{diagnosis.headline}</h3>
              <p className={styles.rcsDiagnosisRelief}>{diagnosis.relief}</p>
            </div>

            <div className={styles.rcsPersonalizationCard}>
              <p className={styles.rcsPersonalizationTitle}>How We've Personalized Your Plan</p>
              <div className={styles.rcsFactors}>
                <div className={styles.rcsFactor}>
                  <span>📊</span>
                  <span>Your Score</span>
                </div>
                <span className={styles.rcsPlus}>+</span>
                <div className={styles.rcsFactor}>
                  <span>📋</span>
                  <span>Your History</span>
                </div>
                <span className={styles.rcsPlus}>+</span>
                <div className={styles.rcsFactor}>
                  <span>👤</span>
                  <span>Your Info</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Fallback for unknown frozen widget types
    return null;
  }

  // ═══════════════════════════════════════════════════════════
  // STEP ROUTER
  // ═══════════════════════════════════════════════════════════

  function renderScreen() {
    switch (currentStep) {
      case "intro":
        return (
          <IntroScreen
            pushToHistory={pushToHistory}
            showMyraMessage={showMyraMessage}
            setIntroProminent={setIntroProminent}
            setPlaceholderText={setPlaceholderText}
            introAdvanceRef={introAdvanceRef}
            detectedArea={detectedArea}
            qaRef={qaRef}
            setQaOpen={setQaOpen}
            qaStripIdsRef={qaStripIdsRef}
            scrollToBottom={scrollToBottom}
            onContinue={(area) => {
              setPlaceholderText("Ask Myra a question…");
              setIntroProminent(false);
              if (area) setDetectedArea(area);
              if (displayMode === "embedded" && onStartAssessment) {
                // Phase 1: signal landing page to fade out — do NOT advance yet
                onStartAssessment();
              } else {
                // Fullscreen (direct entry): advance immediately
                if (!window.trSessionId && AnalyticsService) AnalyticsService.startSession();
                localStorage.setItem("startedSelf", "1");
                if (window.gtag) window.gtag("event", "conversion", { send_to: "AW-18002481001/kXlOCNefkpccEOmeoIhD", value: 1.0, currency: "USD" });
                setCurrentStep("problem_area");
              }
            }}
          />
        );

      case "problem_area":
        return <ProblemAreaScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} paCommandRef={paCommandRef} detectedArea={detectedArea} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "pain_location")} />;

      case "pain_location":
        return <PainLocationScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "safety_screening")} />;

      case "safety_screening":
        return <SafetyScreeningScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "cpg_questions")} onHardStop={(source) => persistAndNavigate({ safetyScreenResult: "FLAGGED", hardStopSource: source }, "hard_stop")} />;

      case "cpg_questions":
        return <CPGQuestionScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields, nextStep) => persistAndNavigate(fields, nextStep)} onHardStop={(source) => persistAndNavigate({ hardStopSource: source }, "hard_stop")} />;

      case "subtype_questioning":
        return <SubtypeQuestionScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} detectedTrigger={detectedTrigger} onContinue={(fields, nextStep) => persistAndNavigate(fields, nextStep)} />;

      case "self_assessment":
        return <SelfAssessmentQ2Screen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "nprs_symptoms")} />;

      case "nprs_symptoms":
        return <PreNPRSScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} detectedDuration={detectedDuration} onContinue={(fields) => persistAndNavigate(fields, "test1_demo")} />;

      case "test1_demo":
        return <TestIntroScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "mode_selection")} />;

      case "mode_selection":
        return <ModeSelectionScreen assessmentData={assessmentData} pushToHistory={pushToHistory} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "camera_permission")} />;

      case "camera_permission":
        return <CameraPermissionScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "movement_test_1")} />;

      case "movement_test_1":
        return <MovementTest1Screen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields, nextStep) => persistAndNavigate(fields, nextStep)} onHardStop={(source) => persistAndNavigate({ hardStopSource: source }, "hard_stop")} />;

      case "self_report_rom":
        return <SelfReportROMScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "self_report_tempo")} />;

      case "self_report_tempo":
        return <SelfReportTempoScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "rpe")} onHardStop={() => persistAndNavigate({ hardStopSource: "couldnt_finish" }, "hard_stop")} />;

      case "rpe":
        return <RPEScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "feel_affect")} />;

      case "feel_affect":
        return <FeelAffectScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields, nextStep) => persistAndNavigate(fields, nextStep)} />;

      case "test2_demo":
        return <TestIntro2Screen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "movement_test_2")} />;

      case "movement_test_2":
        return <MovementTest2Screen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "feedback2")} />;

      case "feedback2":
        return <Feedback2Screen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "personalize")} />;

      case "personalize":
        return <PersonalizeScreen assessmentData={assessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "recovery_score")} />;

      case "recovery_score":
        return <RecoveryScoreScreen assessmentData={assessmentData} updateAssessmentData={updateAssessmentData} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} onContinue={(fields) => persistAndNavigate(fields, "plan")} />;

      case "plan":
        return (
          <PlanScreen
            assessmentData={assessmentData}
            qaRef={qaRef}
            onOpenQnA={() => {
              window.audioPlayer?.unlockAudioForChat?.();
              setQaOpen(true);
            }}
            pushToHistory={pushToHistory}
            showMyraMessage={showMyraMessage}
            scrollToBottom={scrollToBottom}
          />
        );

      case "hard_stop":
        return <HardStopScreen source={assessmentData.hardStopSource} pushToHistory={pushToHistory} showMyraMessage={showMyraMessage} scrollToBottom={scrollToBottom} />;

      default:
        return (
          <div className={styles.inlineUi}>
            <p style={{ color: "var(--gray-500)", textAlign: "center" }}>Unknown step: {currentStep}</p>
          </div>
        );
    }
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER — APP SHELL
  // ═══════════════════════════════════════════════════════════

  const handleVoiceClicked = () => {
    setMode("voice");
    if (window?.mayraCode) {
      window?.mayraCode.handlePickVoice();
    }
  };
  const handleChatClicked = () => {
    setMode("chat");
    if (window?.mayraCode) {
      window?.mayraCode.handlePickChat();
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleInputSubmit();
    }
  };

  return (
    <div className={`${styles.appShell}${displayMode === "embedded" ? ` ${styles.embedded}` : ""}`}>
      {/* Header */}

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerAvatar}>M</div>
          <div className={styles.headerInfo}>
            <h1>Myra</h1>
            <span>Recovery Coach</span>
          </div>
        </div>
        {!qaOpen && (
          <div className={styles.modeToggle}>
            <button className={`${styles.modeBtn} ${mode === "chat" ? styles.active : ""}`} onClick={handleChatClicked}>
              {CHAT_ICON_SVG}Chat
            </button>
            <button className={`${styles.modeBtn} ${mode === "voice" ? styles.active : ""}`} onClick={handleVoiceClicked}>
              {VOICE_ICON_SVG}Voice
            </button>
          </div>
        )}
        {/* <button
          className={styles.myraFab}
          onClick={() => {
            window.audioPlayer?.unlockAudioForChat?.();
            setQaOpen(true);
          }}
          aria-label="Chat with Myra"
        >
          Myra
        </button> */}
        {currentStep !== "hard_stop" && currentStep !== "plan" && (
          <button className={styles.headerClose} onClick={() => setShowExitHandler(true)} title="Exit assessment">
            ×
          </button>
        )}
      </div>

      {/* Progress */}
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
      </div>
      <div className={styles.stepLabel}>{stepLabel}</div>

      {/* Chat Area */}
      <div className={styles.chatArea} ref={chatAreaRef}>
        {conversationHistory.map((entry) => renderHistoryEntry(entry))}
        {typingVisible && (
          <div className={styles.typingRow}>
            <div className={styles.msgAvatar}>M</div>
            <div className={styles.typingDots}>
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
        {renderScreen()}
        <div ref={scrollSentinelRef} style={{ height: 1, flexShrink: 0 }} />
      </div>

      {/* Input Bar — never disabled, never hidden */}
      <div className={styles.inputBar}>
        <div className={styles.inputChat}>
          <input type="text" className={introProminent ? styles.inputFieldIntroProminent : styles.inputField} value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyDown} placeholder={placeholderText} autoComplete="off" />
          <button className={styles.sendBtn} disabled={!inputText.trim()} onClick={handleInputSubmit}>
            {SEND_ICON}
          </button>
        </div>
      </div>

      {/* QnA Overlay */}
      <QnAOverlay ref={qaRef} isOpen={qaOpen} onClose={handleQaOverlayClose} screenName={currentStep} screenPayload={screenPayload} setQaOpen={setQaOpen} setTypingVisible={setTypingVisible} />

      {/* Exit Handler */}
      {showExitHandler && (
        <ExitHandler
          currentStep={currentStep}
          assessmentData={assessmentData}
          onClose={() => setShowExitHandler(false)}
          onOpenQnA={(question, answer, source) => {
            setShowExitHandler(false);
            qaRef.current?.addExchange?.(question, answer, source);
            window.audioPlayer?.unlockAudioForChat?.();
            setQaOpen(true);
          }}
        />
      )}

      {/* Inactivity Modal */}
      {showInactivityModal && (
        <InactivityModal
          onResume={() => setShowInactivityModal(false)}
          onExit={() => {
            setShowInactivityModal(false);
            setShowExitHandler(true);
          }}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN: INTRO (New)
// Myra greeting → CTA buttons → advance to Problem Area.
// Inline Q&A (Pattern A) lives in local state, collapses to strips on advance.
// ════════════════════════════════════════════════════════════════

function IntroScreen({ pushToHistory, showMyraMessage, setIntroProminent, setPlaceholderText, introAdvanceRef, detectedArea, qaRef, setQaOpen, qaStripIdsRef, scrollToBottom, onContinue }) {
  const [phase, setPhase] = useState("loading");
  const [ctaUsed, setCtaUsed] = useState(new Set());
  const [inlineQa, setInlineQa] = useState([]);
  const [ctaTyping, setCtaTyping] = useState(false);
  const [openAccIdx, setOpenAccIdx] = useState(-1);
  const introQaItemsRef = useRef([]);
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);
  const heroVideoRef = useRef(null);

  // ── Greeting sequence ──
  useEffect(() => {
    let cancelled = false;
    async function greet() {
      await _delay(600);
      if (cancelled) return;
      await showMyraMessage(MYRA_MESSAGES.intro?.welcome || "Hi, I'm Myra — your recovery coach from Virdio Health, powered by AI. I'm here to help you build a personalized recovery plan.", 1200);
      if (cancelled) return;
      await _delay(400);
      await showMyraMessage(MYRA_MESSAGES.intro?.readyPrompt || "Before we begin — is there anything you'd like to know, or are you ready to get started?", 900);
      if (cancelled) return;
      setIntroProminent(true);
      setPhase("ready");
    }
    greet();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Register advance callback for shell input routing ──
  useEffect(() => {
    introAdvanceRef.current = (_text, area) => handleAdvance(area);
    return () => {
      introAdvanceRef.current = null;
    };
  }, []);

  // ── Set flow context for Myra backend during intro ──
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.currentFlow = "flow_landing_page";
      if (VoiceService) {
        VoiceService._currentFlow = "flow_landing_page";
      }
    }
  }, []);

  // ── CTA handler (async for typing pacing) ──
  async function handleCta(ctaId) {
    // VIDEO BUTTON: No state change, video is always visible
    if (ctaId === "video") {
      setShowVideoOverlay(true);
      setTimeout(() => {
        if (heroVideoRef.current) {
          heroVideoRef.current.play().catch((err) => {
            console.error("Video play failed:", err);
            // Most browsers require user interaction first
            // The video will have controls so user can manually play
          });
        }
      }, 100);

      // Track video engagement for analytics
      if (AnalyticsService) {
        AnalyticsService.trackStep({
          event: "intro_video_opened",
          timestamp: new Date().toISOString(),
        });
      }
      return;
    }

    if (ctaId === "start") {
      handleAdvance(null);
      return;
    }
    setCtaUsed((prev) => new Set(prev).add(ctaId));

    // M1: Collapse any existing inline Q&A to strips before showing new response
    if (introQaItemsRef.current.length > 0) {
      const strips = [];
      introQaItemsRef.current.forEach((item) => {
        const exId = qaRef.current?.addExchange?.(item.question, item.answer, "intro_cta");
        const stripId = nextId();
        if (exId) qaStripIdsRef.current.add(exId);
        strips.push({ type: "qa-strip", id: stripId, qaId: exId || stripId, question: item.question });
      });
      pushToHistory(...strips);
      introQaItemsRef.current = [];
      setInlineQa([]);
    }

    let question, answer, type;
    if (ctaId === "assessment") {
      question = "What's the Assessment?";
      answer = MYRA_MESSAGES.intro?.whatsAssessment || "Here's what we'll do together — about 10 minutes total. Tap any step to learn more.";
      type = "assessment";
    } else if (ctaId === "myra") {
      question = "Who is Myra?";
      answer = MYRA_MESSAGES.intro?.whoIsMyra || "I'm Myra, your recovery coach from Virdio Health. I'm powered by AI — not a human — but I'm designed to guide you through understanding and recovering from muscle and joint pain. I work alongside doctors of physical therapy and evidence-based research to help you do recovery at home, under your own guidance. I'm not a doctor, physical therapist, or clinician.";
      type = "myra";
    } else {
      return;
    }

    // M2: Show user bubble immediately, typing dots, then Myra response
    const qaItem = { id: nextId(), question, answer, type };
    setInlineQa([{ ...qaItem, answer: null }]);
    setCtaTyping(true);
    await _delay(400);
    await _delay(1000);
    setCtaTyping(false);
    setInlineQa([qaItem]);
    introQaItemsRef.current.push(qaItem);
  }

  // ── Advance: collapse inline Q&A to strips, proceed ──
  function handleAdvance(preselectedArea) {
    const strips = [];
    introQaItemsRef.current.forEach((item) => {
      const exId = qaRef.current?.addExchange?.(item.question, item.answer, "intro_cta");
      const stripId = nextId();
      if (exId) qaStripIdsRef.current.add(exId);
      strips.push({ type: "qa-strip", id: stripId, qaId: exId || stripId, question: item.question });
    });
    pushToHistory(...strips, { type: "user", text: "Let's get Started...", id: nextId() });
    onContinue(preselectedArea || detectedArea || null);
  }

  useEffect(() => {
    scrollToBottom();
  }, [inlineQa, ctaTyping, scrollToBottom]);

  if (phase === "loading") return null;

  const hasResponded = inlineQa.some((qa) => qa.answer !== null);
  const steps = getAssessmentSteps();

  return (
    <>
      {/* Inline Q&A from CTA taps (Pattern A) */}
      {inlineQa.map((qa) => (
        <React.Fragment key={qa.id}>
          <div className={styles.msgRowUser}>
            <div className={styles.msgBubbleUser}>{qa.question}</div>
          </div>
          {qa.answer && (
            <>
              <div className={styles.msgRowMyra}>
                <div className={styles.msgAvatar}>M</div>
                <div className={styles.msgBubbleMyra}>{qa.answer}</div>
              </div>
              {qa.type === "assessment" && (
                <div className={styles.inlineUi}>
                  <div className={styles.accordion}>
                    {steps.map((s, i) => (
                      <div key={i} className={`${styles.accordionItem} ${openAccIdx === i ? styles.open : ""}`} onClick={() => setOpenAccIdx(openAccIdx === i ? -1 : i)}>
                        <div className={styles.accordionHeader}>
                          <span className={styles.accordionStep}>{i + 1}</span>
                          <span className={styles.accordionTitle}>{s.title}</span>
                          {CHEVRON_DOWN}
                        </div>
                        <div className={styles.accordionBody}>
                          <p>{s.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </React.Fragment>
      ))}

      {/* M2: Typing dots while CTA response is loading */}
      {ctaTyping && (
        <div className={styles.typingRow}>
          <div className={styles.msgAvatar}>M</div>
          <div className={styles.typingDots}>
            <span />
            <span />
            <span />
          </div>
        </div>
      )}

      {/* CTA buttons */}
      <div className={`${styles.inlineUi}`}>
        <div className={styles.introCtaRow}>
          {INTRO_CTAS.filter((c) => (hasResponded ? c.type === "info" : true)).map((cta) => (
            <button key={cta.id} className={`${cta.type === "start" ? styles.introCtaBtnStart : styles.introCtaBtn} ${ctaUsed.has(cta.id) ? styles.introCtaBtnUsed : ""}`} onClick={() => handleCta(cta.id)}>
              {cta.label}
            </button>
          ))}
        </div>
      </div>
      {/* Floating "Let's get started" after CTA response */}
      {hasResponded && (
        <div className={styles.inlineUi}>
          <div className={styles.introCtaRow}>
            <button className={styles.introCtaBtnStartFloat} onClick={() => handleAdvance(null)}>
              Let's get Started...
            </button>
          </div>
        </div>
      )}
      {showVideoOverlay && (
        <div
          className={styles.videoOverlay}
          onClick={(e) => {
            // Close only if clicking outside the video container
            if (e.target === e.currentTarget) {
              setShowVideoOverlay(false);
              if (heroVideoRef.current) {
                heroVideoRef.current.pause();
              }
            }
          }}
        >
          <div className={styles.videoOverlayContent}>
            <button
              className={styles.videoOverlayClose}
              onClick={() => {
                setShowVideoOverlay(false);
                if (heroVideoRef.current) {
                  heroVideoRef.current.pause();
                }
              }}
              aria-label="Close video"
            >
              ×
            </button>

            <div className={styles.videoContainerOverlay}>
              <video
                ref={heroVideoRef}
                src={CONST.HOME_VIDEO_2}
                controls
                playsInline
                preload="metadata"
                onEnded={() => {
                  // Optionally auto-close or show CTA after video ends
                  // setShowVideoOverlay(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 2: PROBLEM AREA (Converted to conversational)
// Myra message pushed to history. Return is inline widget only.
// ════════════════════════════════════════════════════════════════

function ProblemAreaScreen({ assessmentData, pushToHistory, showMyraMessage, paCommandRef, detectedArea, scrollToBottom, onContinue }) {
  const [selectedArea, setSelectedArea] = useState(detectedArea || null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [locked, setLocked] = useState(false);
  const [ageHighlight, setAgeHighlight] = useState(false);
  const [ready, setReady] = useState(false);
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  const areas = Object.entries(CONST.CLINICAL_PATHS);

  // Push Myra message on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      await showMyraMessage(MYRA_MESSAGES.problemArea?.prompt || "Can you start by telling me where you first felt your pain?", 900);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleContinue = useCallback(async () => {
    if (!selectedArea) {
      if (isVoiceMode) {
        VoiceService?.doSay("Please make your selection before we can move to the next screen");
      }
      return;
    }
    if (!ageConfirmed) {
      if (isVoiceMode) {
        VoiceService?.doSay("Please make your selection before we can move to the next screen");
      }
      setAgeHighlight(true);
      setTimeout(() => setAgeHighlight(false), 2000);
      return;
    }
    if (locked) return;
    setLocked(true);

    window.problemArea = selectedArea;
    localStorage.setItem("didGsaFlowBefore", "true");
    if (!window.trSessionId && AnalyticsService) AnalyticsService.startSession();

    const areaName = CONST.CLINICAL_PATHS[selectedArea]?.name || selectedArea;
    pushToHistory({ type: "frozen-widget", widgetType: "problem_area", id: nextId(), data: { areas, selectedArea, ageConfirmed: true } }, { type: "user", text: areaName, id: nextId() });

    // Confirmation message before transition
    const confirmMsg = resolveMessage(MYRA_MESSAGES.problemArea?.selectionConfirm || "{area} — got it.", { area: areaName });
    await showMyraMessage(confirmMsg, 800);

    onContinue({
      problemArea: selectedArea,
      selected_problem_area: CONST.CLINICAL_PATHS[selectedArea].name,
      EngagedOnLanding: window.EngagedOnLanding || false,
    });
  }, [selectedArea, ageConfirmed, locked, areas, pushToHistory, showMyraMessage, onContinue]);

  useEffect(() => {
    window.currentFlow = "flow_gsa";
    VoiceService._currentFlow = "flow_gsa";
    const isReturning = localStorage.getItem("didGsaFlowBefore") === "true";
    const stepId = isReturning ? "flow_gsa_welcome_back" : "flow_gsa_first_time";
    const payload = generatePayloadForScreen("problem_area", assessmentData);
    if (window.mayraCode) {
      window.mayraForwardCode = { midjourneyData: payload };
      // window.mayraCode.startFlow("flow_gsa", stepId);
      VoiceService.startFlowWithoutAnnouncement("flow_gsa");
      setTimeout(() => {
        VoiceService.sendMidJourneyData(payload);
      }, 500);
    }
  }, []);

  useScreenVoice({
    screenName: "problem_area",
    flowName: "flow_gsa",
    payloadKey: "problem_area",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (value === "continue_command") handleContinue();
        if (extracted && extracted.problemArea) {
          console.log("[VOICE] Updating problem area to:", extracted.problemArea);
          setSelectedArea(extracted.problemArea);
        }
      },
      MOVE_ON: () => handleContinue(),
    },
    exitReason: "problem_area_selected",
  });

  useEffect(() => {
    paCommandRef.current = (areaId) => {
      if (locked) return;
      setSelectedArea(areaId);
      if (!ageConfirmed) {
        setAgeHighlight(true);
        setTimeout(() => setAgeHighlight(false), 2000);
      }
    };
    return () => {
      paCommandRef.current = null;
    };
  }, [locked, ageConfirmed]);

  const selectArea = useCallback(
    (id) => {
      if (locked) return;
      setSelectedArea(id);
      if (!ageConfirmed) {
        setAgeHighlight(true);
        setTimeout(() => setAgeHighlight(false), 2000);
      }
    },
    [locked, ageConfirmed],
  );

  useEffect(() => {
    if (selectedArea && ageConfirmed && !locked) handleContinue();
  }, [selectedArea, ageConfirmed, locked, handleContinue]);

  if (!ready) return null;

  return (
    <div className={styles.inlineUi}>
      {!locked ? (
        <>
          {/* <p className={styles.paSubtitle}>You can pinpoint multiple locations on the next screen.</p> */}
          <div className={styles.paCards}>
            {areas.map(([id, path]) => (
              <div key={id} className={`${styles.paCard} ${selectedArea === id ? styles.selected : ""} ${locked ? styles.locked : ""}`} onClick={() => selectArea(id)}>
                {path.name}
              </div>
            ))}
          </div>
          <div className={`${styles.ageConfirm} ${ageHighlight ? styles.ageHighlight : ""}`}>
            <input type="checkbox" id="ageChk" checked={ageConfirmed} onChange={(e) => setAgeConfirmed(e.target.checked)} />
            <label htmlFor="ageChk">I confirm I am 18 years of age or older</label>
          </div>
        </>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 5: CPG QUESTION
// LBP: 2-step branching based on isLegPain.
//   Leg pain → CPG_OPTIONS_LEG → subtype Q1
//   Back only → CPG_OPTIONS_BACK → cpgToSubtype() → Pre-NPRS
// HKT: CLINICAL_PATHS[area].cpgQuestion
//   routesTo "direct" → Pre-NPRS (with Thigh Neural zone routing)
//   routesTo "subtypeQuestion" → Subtype Question
// ════════════════════════════════════════════════════════════════

function CPGQuestionScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [selectedId, setSelectedId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const problemArea = assessmentData.problemArea;
  const isLBP = problemArea === "LBP";
  const isLegPain = assessmentData.isLegPain;
  const isVoiceMode = MyraInteraction.getMode() === "voice";
  window.notOpenQNA = false;

  // Build the question and options
  let prompt, options;
  if (isLBP) {
    options = isLegPain ? CONST.CPG_OPTIONS_LEG : CONST.CPG_OPTIONS_BACK;
    prompt = isLegPain ? MYRA_MESSAGES.cpg?.lbpLegQuestion || "Which of these best describes the feeling in your leg?" : MYRA_MESSAGES.cpg?.lbpBackQuestion || "Which of these best describes your main complaint?";
  } else {
    const cpgQ = CONST.CLINICAL_PATHS[problemArea].cpgQuestion;
    prompt = cpgQ.prompt;
    options = cpgQ.options;
  }

  // Push divider + Myra message on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      await showMyraMessage(prompt, 900);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = useCallback(
    async (id) => {
      if (locked) return;
      setSelectedId(id);
      setLocked(true);

      const opt = options.find((o) => o.id === id);

      // Push frozen widget + user echo to conversation history
      pushToHistory({ type: "frozen-widget", widgetType: "cpg_options", id: nextId(), data: { options, selectedId: id } }, { type: "user", text: opt.label, id: nextId() });

      if (isLBP) {
        // LBP path
        if (isLegPain) {
          // Leg pain → subtype Q1
          await showMyraMessage(MYRA_MESSAGES.cpg?.transitionToSubtype || "Thanks. One more question to narrow things down.", 800);
          onContinue(
            {
              cpgValue: opt.value,
              cpgForBackend: opt.value,
              cpgCategory: opt.value,
            },
            "subtype_questioning",
          );
        } else {
          // Back only → direct subtype via cpgToSubtype
          const subtype = CONST.cpgToSubtype(opt.value);
          await showMyraMessage(MYRA_MESSAGES.cpg?.transitionToPreNprsFromBackOnly || "Thanks. Let me ask about your pain level.", 800);
          onContinue(
            {
              cpgValue: opt.value,
              cpgForBackend: opt.value,
              cpgCategory: opt.value,
              probableSubtype: subtype,
              skipSubtypeQuestion: true,
            },
            "nprs_symptoms",
          );
        }
      } else {
        // HKT path
        if (opt.routesTo === "direct") {
          let subtype = opt.directSubtype;

          // Thigh Neural: zone-based routing
          if (problemArea === "THIGH" && opt.value === "THIGH_NEURAL_INVOLVEMENT") {
            const zones = assessmentData.selectedZones || [];
            const zoneString = zones.join(" ").toLowerCase();
            const singleZone = zones.length === 1;
            if (singleZone && (zoneString.includes("front") || zoneString.includes("anterior"))) {
              subtype = "QUADRICEPS_LOADING_IMPACT_INTOLERANCE";
            } else if (singleZone && (zoneString.includes("outer") || zoneString.includes("lateral"))) {
              subtype = "ITB_FRICTION_SYNDROME";
            } else {
              subtype = "HAMSTRING_LOADING_LENGTHENING_DEFICIT";
            }
          }

          await showMyraMessage(MYRA_MESSAGES.cpg?.transitionToPreNprs || "Thanks. Let me ask about your pain level.", 800);
          onContinue(
            {
              cpgValue: opt.cpgForBackend,
              cpgForBackend: opt.cpgForBackend,
              cpgOption: opt,
              cpgCategory: opt.value,
              probableSubtype: subtype,
              skipSubtypeQuestion: true,
            },
            "nprs_symptoms",
          );
        } else {
          // Routes to subtype question
          await showMyraMessage(MYRA_MESSAGES.cpg?.transitionToSubtype || "Thanks. One more question to narrow things down.", 800);
          onContinue(
            {
              cpgValue: opt.cpgForBackend,
              cpgForBackend: opt.cpgForBackend,
              cpgOption: opt,
              cpgCategory: opt.value,
              subtypeQuestionIndex: opt.subtypeQuestionIndex,
              skipSubtypeQuestion: false,
            },
            "subtype_questioning",
          );
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [locked, options, isLBP, isLegPain, problemArea, assessmentData.selectedZones],
  );

  const validateAndSelect = useCallback(() => {
    if (!selectedId && !locked) {
      if (isVoiceMode) {
        VoiceService?.doSay("Please make your selection before we can move to the next screen");
      }
      return;
    }
  }, [selectedId, locked]);

  useScreenVoice({
    screenName: "cpg_questions",
    flowName: "flow_gsa",
    payloadKey: "cpg_questions",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (value === "continue_command") validateAndSelect();
      },
      MOVE_ON: () => validateAndSelect(),
    },
    exitReason: "cpg_selected",
  });

  if (!ready) return null;

  return (
    <div className={styles.inlineUi}>
      {!locked ? (
        <div className={styles.optionCards}>
          {options.map((o) => (
            <div key={o.id} className={`${styles.optionCard} ${selectedId === o.id ? styles.selected : ""} ${locked ? styles.locked : ""}`} onClick={() => handleSelect(o.id)}>
              {o.label}
            </div>
          ))}
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 6: SUBTYPE QUESTION
// LBP (leg pain): shows Q1_OPTIONS (pain trigger).
// HKT: shows CLINICAL_PATHS[area].subtypeQuestions[index].
// ════════════════════════════════════════════════════════════════

function SubtypeQuestionScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, detectedTrigger, onContinue }) {
  const [selectedId, setSelectedId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const problemArea = assessmentData.problemArea;
  const isLBP = problemArea === "LBP";
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  let prompt, options;
  if (isLBP) {
    prompt = MYRA_MESSAGES.subtypeQ1?.prompt || "Which posiion best describes your lower back pain?";
    options = CONST.Q1_OPTIONS;
  } else {
    const path = CONST.CLINICAL_PATHS[problemArea];
    const qIndex = assessmentData.subtypeQuestionIndex || 0;
    const question = path.subtypeQuestions[qIndex];
    prompt = question.prompt;
    options = question.options;
  }

  // Push divider + Myra message on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      // Intent pre-selection stub: if trigger detected, acknowledge it
      if (isLBP && detectedTrigger) {
        const matchOpt = CONST.Q1_OPTIONS.find((o) => o.value === detectedTrigger);
        if (matchOpt) {
          await showMyraMessage(MYRA_MESSAGES.subtypeQ1?.promptWithPreselect || "Based on what you shared earlier, it sounds like this might apply — does it still sound right?", 900);
          if (!cancelled) {
            setSelectedId(matchOpt.id);
            setReady(true);
          }
          return;
        }
      }
      await showMyraMessage(prompt, 900);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = useCallback(
    async (id) => {
      if (locked) return;
      setSelectedId(id);
      setLocked(true);

      const opt = options.find((o) => o.id === id);

      // Push frozen widget + user echo
      pushToHistory({ type: "frozen-widget", widgetType: "option_cards", id: nextId(), data: { options, selectedId: id } }, { type: "user", text: opt.label, id: nextId() });

      if (isLBP) {
        // LBP Q1 always goes to Q2
        await showMyraMessage(MYRA_MESSAGES.subtypeQ1?.transitionToQ2 || "One more question about this.", 800);
        onContinue(
          {
            subtypeQ1: opt.value,
            subtypeQuestions: { q1: opt },
          },
          "self_assessment",
        );
      } else {
        // HKT: resolve subtype + update cpgForBackend from SUBTYPE_TO_CPG_MAP
        let cpgForBackend = assessmentData.cpgForBackend;
        if (CONST.SUBTYPE_TO_CPG_MAP[problemArea]?.[opt.value]) {
          cpgForBackend = CONST.SUBTYPE_TO_CPG_MAP[problemArea][opt.value];
        }

        await showMyraMessage(MYRA_MESSAGES.hktSubtype?.transitionToPreNprs || "Thanks — that helps narrow things down. Let me ask about your pain level.", 800);
        onContinue(
          {
            probableSubtype: opt.value,
            cpgForBackend,
            subtypeQuestions: { q1: opt },
          },
          "nprs_symptoms",
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [locked, options, isLBP, problemArea, assessmentData.cpgForBackend],
  );

  const validateAndContinue = useCallback(() => {
    if (!selectedId && !locked) {
      if (isVoiceMode) {
        VoiceService?.doSay("Please make your selection before we can move to the next screen");
      }
    }
  }, [selectedId, locked]);

  useScreenVoice({
    screenName: "subtype_questioning",
    flowName: "flow_gsa",
    payloadKey: "subtype_questioning",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (value === "continue_command") validateAndContinue();
      },
      MOVE_ON: () => validateAndContinue(),
    },
    exitReason: "subtype_selected",
  });

  if (!ready) return null;

  return (
    <div className={styles.inlineUi}>
      {!locked ? (
        <div className={styles.optionCards}>
          {options.map((o) => (
            <div key={o.id} className={`${styles.optionCard} ${selectedId === o.id ? styles.selected : ""} ${locked ? styles.locked : ""}`} onClick={() => handleSelect(o.id)}>
              {o.label}
            </div>
          ))}
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 7: SELF-ASSESSMENT Q2 (LBP only)
// Yes/No follow-up to Q1. Resolves final subtype via getSubtype().
// ════════════════════════════════════════════════════════════════

function SelfAssessmentQ2Screen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const q1Value = assessmentData.subtypeQ1;
  const q2Data = CONST.Q2_MAP[q1Value];
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  // Push divider + Myra message on mount
  useEffect(() => {
    if (!q2Data) return;
    let cancelled = false;
    async function init() {
      await showMyraMessage(q2Data.title, 800);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = useCallback(
    async (answer) => {
      if (locked) return;
      setSelectedAnswer(answer);
      setLocked(true);

      const echoText = answer === "yes" ? "Yes" : "No / Unsure";

      // Push frozen widget + user echo
      pushToHistory({ type: "frozen-widget", widgetType: "yesno", id: nextId(), data: { selectedAnswer: answer } }, { type: "user", text: echoText, id: nextId() });

      const subtype = CONST.getSubtype(q1Value, answer);

      await showMyraMessage(MYRA_MESSAGES.q2?.transitionToPreNprs || "Thanks — that helps narrow things down. Now let me ask about your pain level.", 800);
      onContinue({
        selfAssessmentAnswer: echoText,
        selfAssessmentValue: answer,
        subtypeQ2: answer,
        probableSubtype: subtype,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [locked, q1Value],
  );

  const validateAndContinue = useCallback(() => {
    if (!selectedAnswer && !locked) {
      if (isVoiceMode) {
        VoiceService?.doSay("Please make your selection before we can move to the next screen");
      }
    }
  }, [selectedAnswer, locked]);

  useScreenVoice({
    screenName: "self_assessment",
    flowName: "flow_gsa",
    payloadKey: "self_assessment",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (value === "continue_command") validateAndContinue();
      },
      MOVE_ON: () => validateAndContinue(),
    },
    exitReason: "self_assessment_completed",
  });

  // If Q2 data doesn't exist for this Q1 value, auto-skip with default subtype
  useEffect(() => {
    if (!q2Data) {
      onContinue({
        selfAssessmentAnswer: "skipped",
        selfAssessmentValue: null,
        subtypeQ2: null,
        probableSubtype: CONST.getSubtype(q1Value, null),
      });
    }
  }, [q2Data, q1Value, onContinue]);

  if (!q2Data) return null;
  if (!ready) return null;

  return (
    <div className={styles.inlineUi}>
      {!locked ? (
        <div className={styles.yesnoRow}>
          <button className={`${styles.yesnoBtn} ${selectedAnswer === "yes" ? styles.selected : ""}`} onClick={() => handleSelect("yes")} disabled={locked}>
            Yes
          </button>
          <button className={`${styles.yesnoBtn} ${selectedAnswer === "no" ? styles.selected : ""}`} onClick={() => handleSelect("no")} disabled={locked}>
            No / Unsure
          </button>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 8: PRE-NPRS (Pain Rating 0–10 + Duration)
// Two-field validation: both painLevel AND duration required.
// On exit: nulls flow before navigating to test phase.
// ════════════════════════════════════════════════════════════════

function PreNPRSScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, detectedDuration, onContinue, readOnly = false }) {
  const [painLevel, setPainLevel] = useState(null);
  const [duration, setDuration] = useState(detectedDuration || null);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const [painLocked, setPainLocked] = useState(false);
  const [durationReady, setDurationReady] = useState(readOnly);
  const [showDemoGate, setShowDemoGate] = useState(false);
  const durationOptRef = useRef(null);
  const [hideUi, setHideUi] = useState(false);
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  // Push divider + Myra pain prompt on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      await showMyraMessage(MYRA_MESSAGES.preNprs?.painPrompt || "What is your pain level right now? 0 is no pain, 10 is the worst imaginable.", 800);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // Phase 1 → Phase 2: when pain is selected, push echo + ask duration
  const handlePainSelect = useCallback(
    async (val) => {
      if (painLocked) return;
      setPainLocked(true);
      setPainLevel(val);
      pushToHistory({ type: "frozen-widget", widgetType: "pre_nprs_pain", id: nextId(), data: { selectedValue: val } }, { type: "user", text: `Pain level: ${val}/10`, id: nextId() });
      const durationMsg = detectedDuration ? MYRA_MESSAGES.preNprs?.durationPromptPreselect || "You mentioned how long this has been going on — does this match?" : MYRA_MESSAGES.preNprs?.durationPromptDefault || "And how long have you been experiencing these symptoms?";
      await showMyraMessage(durationMsg, 700);
      setDurationReady(true);
    },
    [painLocked, detectedDuration, pushToHistory, showMyraMessage],
  );

  // Phase 2 completion: when duration is selected, show transition messages + demo gate
  const handleDurationSelect = useCallback(
    async (opt) => {
      if (locked) return;
      setDuration(opt.value);
      setLocked(true);
      durationOptRef.current = opt;
      setHideUi(true);

      const durationConfirmationMap = {
        ACUTE: "few weeks",
        SUBACUTE: "little while",
        CHRONIC: "while",
      };
      const mappedDuration = durationConfirmationMap[opt.value] || opt.label?.toLowerCase() || "while";

      // Push frozen duration widget + user echo
      pushToHistory({ type: "frozen-widget", widgetType: "pre_nprs_duration", id: nextId(), data: { selectedValue: opt.value } }, { type: "user", text: opt.label, id: nextId() }, { type: "myra", text: `Got it - your pain level is ${painLevel} and you've had it for a ${mappedDuration}.`, id: nextId() });

      // Transition messages: test intro + voice warning
      const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
      const test = CONST.TEST_MAP[subtype];
      const testName = test?.name || "";
      pushToHistory({ type: "divider", id: nextId() });
      pushToHistory({ type: "section-header", label: "Movement Test 1", id: nextId() });
      const testTransition = resolveMessage(MYRA_MESSAGES.preNprs?.transitionToTest || `Based on what you've shared, I've selected <strong>${testName}</strong> as your first movement check. I'll show you a short demo video first.`, { testName });
      await showMyraMessage(testTransition, 800);
      await showMyraMessage(MYRA_MESSAGES.preNprs?.voiceHeadsUp || "From this point on, I'll be guiding you out loud — so make sure your volume is up and you're in a space where you can move comfortably.", 900);
      pushToHistory({ type: "frozen-widget", widgetType: "voice_warning", id: nextId(), data: {} });

      // Show the demo gate button — do NOT auto-advance
      setShowDemoGate(true);
    },
    [locked, painLevel, assessmentData.probableSubtype, pushToHistory, showMyraMessage],
  );

  // Phase 3: User taps "Show Me the Demo" → null flow and advance
  const handleShowDemo = useCallback(() => {
    pushToHistory({ type: "user", text: "Show me the demo", id: nextId() });

    // Null the flow before transitioning to movement test phase
    if (VoiceService) {
      VoiceService._currentFlow = null;
    }
    window.currentFlow = null;
    if (MyraInteraction) {
      MyraInteraction.switchMode(null);
    }
    window.initFromUserGesture?.();

    const opt = durationOptRef.current;
    onContinue({ painLevel, symptomDuration: opt.value, duration: opt.value });
  }, [painLevel, pushToHistory, onContinue]);

  const canContinue = painLevel !== null && duration !== null;

  const handleContinue = useCallback(() => {
    if (readOnly) {
      return;
    }
    if (!canContinue) {
      if (painLevel === null) {
        if (isVoiceMode) {
          VoiceService?.doSay("Please select your current pain level before we continue.");
        }
      } else {
        if (isVoiceMode) {
          VoiceService?.doSay("Please select how long you've been experiencing symptoms.");
        }
      }
      return;
    }
    if (locked || readOnly) return;
    // Duration selection auto-advances via handleDurationSelect, so this is a fallback
    const opt = CONST.DURATION_OPTIONS.find((o) => o.value === duration);
    if (opt) handleDurationSelect(opt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painLevel, duration, locked, canContinue]);

  useScreenVoice({
    screenName: "nprs_symptoms",
    trackerName: "pre_nprs",
    flowName: "flow_gsa",
    payloadKey: "nprs_symptoms",
    commands: {
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (value === "continue_command") handleContinue();
        if (extracted && Object.keys(extracted).length > 0) {
          console.log("[VOICE] Updating form with extracted data:", extracted);

          // Handle pain level
          if (extracted.painLevel !== undefined && extracted.painLevel !== null) {
            const painValue = typeof extracted.painLevel === "number" ? extracted.painLevel : parseInt(extracted.painLevel);

            if (!isNaN(painValue) && painValue >= 0 && painValue <= 10) {
              if (painLocked) {
                console.log("[VOICE] Pain already locked — ignoring:", painValue);
                return;
              }
              setPainLevel(painValue);
              handlePainSelect(painValue);
              console.log("[VOICE] Updating pain level to:", painValue);
            }
          }

          // Handle symptom duration
          if (extracted.symptomDuration) {
            // Map the duration to the expected values: "ACUTE", "SUBACUTE", "CHRONIC"
            let durationValue = extracted.symptomDuration;

            // If it's a descriptive string, map it to the correct value
            if (typeof durationValue === "string") {
              const durationMap = {
                "less than 6 weeks": "ACUTE",
                acute: "ACUTE",
                "6 weeks to 3 months": "SUBACUTE",
                subacute: "SUBACUTE",
                "more than 3 months": "CHRONIC",
                chronic: "CHRONIC",
                "over 3 months": "CHRONIC",
              };

              // Check if it's a mapped value
              const lowerValue = durationValue.toLowerCase();
              if (durationMap[lowerValue]) {
                durationValue = durationMap[lowerValue];
              } else if (durationValue === "ACUTE" || durationValue === "SUBACUTE" || durationValue === "CHRONIC") {
                // Already in correct format
                durationValue = durationValue;
              } else {
                // Try to determine from the text
                if (lowerValue.includes("week") && parseInt(lowerValue) < 6) {
                  durationValue = "ACUTE";
                } else if (lowerValue.includes("month")) {
                  durationValue = "CHRONIC";
                }
              }
            }

            // Only update if it's a valid value
            if (["ACUTE", "SUBACUTE", "CHRONIC"].includes(durationValue)) {
              setDuration(durationValue);
              handleDurationSelect(durationValue);
              console.log("[VOICE] Updating symptom duration to:", durationValue);
            }
          }
        }
      },
      MOVE_ON: () => handleContinue(),
    },
    exitReason: "pre_nprs_completed",
  });

  // Pain circle color helpers
  const painColor = (val) => (val <= 3 ? "var(--green)" : val <= 7 ? "var(--amber)" : "var(--red)");
  const painTextColor = (val) => (val <= 3 ? "var(--green-text)" : val <= 7 ? "var(--amber-text)" : "var(--red-text)");

  if (!ready) return null;

  return (
    <div className={styles.inlineUi}>
      {/* Phase 1: Pain circles — hide as soon as pain is locked (frozen widget takes over) */}
      {!hideUi && !painLocked && (
        <>
          <div className={styles.painGrid}>
            {Array.from({ length: 11 }, (_, i) => {
              const isSelected = painLevel === i;
              return (
                <div
                  key={i}
                  className={`${styles.painCircle} ${isSelected ? styles.selected : ""}`}
                  style={{
                    borderColor: painColor(i),
                    backgroundColor: isSelected ? painColor(i) : "white",
                    color: isSelected ? "white" : painTextColor(i),
                    transform: isSelected ? "scale(1.1)" : "scale(1)",
                    pointerEvents: locked ? "none" : "auto",
                  }}
                  onClick={() => !readOnly && handlePainSelect(i)}
                >
                  {i}
                </div>
              );
            })}
          </div>
          <div className={styles.painLabels}>
            <span className={styles.noPain}>No Pain</span>
            <span className={styles.worstPain}>Worst Pain</span>
          </div>
        </>
      )}

      {/* Phase 2: Duration options — live after pain locked, until duration select collapses both */}
      {!hideUi && durationReady && (
        <div className={styles.durationOptions} style={{ marginTop: "8px" }}>
          {CONST.DURATION_OPTIONS.map((o) => (
            <div key={o.id} className={`${styles.durationOption} ${duration === o.value ? styles.selected : ""}`} style={{ pointerEvents: locked ? "none" : "auto" }} onClick={() => !readOnly && handleDurationSelect(o)}>
              {o.label}
            </div>
          ))}
        </div>
      )}

      {/* Phase 3: Demo gate button (appears after transition messages) */}
      {showDemoGate && (
        <div className={styles.videoActions} style={{ marginTop: "12px" }}>
          <button className={`${styles.videoBtn} ${styles.videoBtnPrimary}`} onClick={handleShowDemo}>
            Show Me the Demo
          </button>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 9: TEST INTRO 1
// Mode selection card (conditional) → demo video → "Ready" button.
// Most complex voice integration in the GSA:
//   - Manual flow management (not standard useScreenVoice flow)
//   - 4 startFlow paths based on name + returning status
//   - 9 command handlers including GIVE_NAME and video controls
//   - AR handoff with full window.* global setup
//   - Second (and optionally third) test video preloading
// ════════════════════════════════════════════════════════════════

function TestIntroScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [locked, setLocked] = useState(false);
  const videoRef = useRef(null);

  const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
  const problemArea = assessmentData.problemArea || "LBP";
  const test = CONST.TEST_MAP[subtype];
  const testName = test?.name || "";
  const introVideoUrl = CONST.TEST_INTRO_VIDEOS?.[testName] || test?.video || "";
  const [canStartTest, setCanStartTest] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (video.currentTime >= 5) {
        setCanStartTest(true);
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, []);

  // Generate test intro payload (camera + movement test payloads move to CameraPermissionScreen with AR handoff)
  const testIntroPayload = useMemo(() => generatePayloadForScreen("test1_demo", assessmentData), [assessmentData]);

  useEffect(() => {
    // Video controller
    window.videoController = {
      pause: () => videoRef.current?.pause(),
      play: () => videoRef.current?.play(),
      expand: () => {
        document.body.classList.add("videoplay-expanded");
        videoRef.current?.requestFullscreen?.();
      },
      replay: () => {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play();
        }
      },
      isPaused: () => videoRef.current?.paused ?? true,
    };
  }, []);
  const doReplyVideo = () => {
    const video = videoRef.current;
    if (!video) {
      console.error("Video element not found");
      return;
    }
    videoRef.current.currentTime = 0;
    videoRef.current.play().catch((err) => console.log("Replay autoplay prevented:", err));
  };

  // useScreenVoice for boilerplate only — flow handled manually
  useScreenVoice({
    screenName: "test1_demo",
    flowName: "flow_demo_video",
    payloadKey: "test1_demo",
    exitReason: "started_test",
    commands: {
      GIVE_NAME: (value) => {
        if (window.exitHandlerOpen) return;
        localStorage.setItem("self_name", value);
        window.mayraForwardCode = { midjourneyData: testIntroPayload };
        window.mayraCode.startFlow("flow_demo_video", "first_video_play_name", () => {
          doReplyVideo();
          window.videoController?.replay();
        });
      },
      SKIP_NAME: (value) => {
        if (window.exitHandlerOpen) return;
        window.mayraForwardCode = { midjourneyData: testIntroPayload };
        window.mayraCode.startFlow("flow_demo_video", "first_video_play_skip_name", () => {
          doReplyVideo();
          window.videoController?.replay();
        });
      },
      SKIP_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Got it! Skipping the video.");
        // window.mayraCode?.startFlow("flow_demo_video", "first_video_play_skip_name");
        handleContinueToTest();
      },
      REPLAY_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        window.videoController?.replay();
        doReplyVideo();
      },
      PAUSE_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        if (window.videoController?.isPaused()) {
          VoiceService?.doSay("The video is already paused.");
        } else {
          VoiceService?.doSay("Sure! I'll pause the video...");
          window._lastPauseTime = Date.now();
          window.videoController?.pause();
        }
      },
      RESUME_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Sure! I'll resume the video...");
        window.videoController?.play();
      },
      EXPAND_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Sure! I'll expand the video...");
        window.videoController?.expand();
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        handleContinueToTest();
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command") handleContinueToTest();
      },
    },
  });

  // ── Start the correct flow based on name availability ──
  const startCorrectFlow = useCallback(
    (isFirstTime = false) => {
      const mayraCode = window.mayraCode;
      if (!mayraCode) return;

      window.mayraForwardCode = { midjourneyData: testIntroPayload };

      const hasName = localStorage.getItem("self_name");
      if (hasName) {
        window.currentFlow = "flow_demo_video";
        VoiceService._currentFlow = "flow_demo_video";
        const stepId = isFirstTime ? "first_video_play_name" : "first_test_have_name";
        mayraCode.startFlow("flow_demo_video", stepId);
      } else {
        window.currentFlow = "flow_get_name";
        VoiceService._currentFlow = "flow_get_name";
        mayraCode.startFlow("flow_get_name", "first_test_no_name");
      }
    },
    [testIntroPayload],
  );

  // ── Continue to Mode Selection (video ends, user commits to proceeding) ──
  // AR handoff has moved to CameraPermissionScreen (fires when camera choice is made).
  const handleContinueToTest = useCallback(() => {
    if (window.clickedOnceOpen) return;
    window.clickedOnceOpen = true;
    if (locked) return;
    setLocked(true);

    const watchedDuration = videoRef.current?.currentTime || 0;
    const totalDuration = videoRef.current?.duration || 0;

    // Clean up video
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute("src");
      videoRef.current.load();
    }
    document.body.classList.remove("videoplay-expanded");

    // Push frozen video widget + user echo to conversation history
    pushToHistory({ type: "frozen-widget", widgetType: "demo_video", id: nextId(), data: { testName, videoUrl: introVideoUrl } }, { type: "user", text: "Start Movement Test", id: nextId() });

    // Persist payload fields from Demo Video 1 (unchanged). AR fires later from Camera Selection.
    onContinue({
      testName,
      rom_test1_name: testName,
      rom_test1_started: true,
      test1VideoWatched: watchedDuration > 0,
      test1VideoDuration: watchedDuration,
      watched_demo_1: totalDuration > 0 ? watchedDuration / totalDuration > 0.5 : false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, testName, introVideoUrl]);

  // ── Register command handlers ──
  const registerCommands = useCallback(() => {
    window.currentCommandsAudio = {
      GIVE_NAME: (value) => {
        if (window.exitHandlerOpen) return;
        localStorage.setItem("self_name", value);
        window.mayraForwardCode = { midjourneyData: testIntroPayload };
        window.mayraCode.startFlow("flow_demo_video", "first_video_play_skip_name", () => {
          doReplyVideo();
          window.videoController?.replay();
        });
      },
      SKIP_NAME: (value) => {
        if (window.exitHandlerOpen) return;
        window.mayraForwardCode = { midjourneyData: testIntroPayload };
        window.mayraCode.startFlow("flow_demo_video", "first_video_play_skip_name", () => {
          doReplyVideo();
          window.videoController?.replay();
        });
      },
      SKIP_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Got it! Skipping the video.");
        // window.mayraCode?.startFlow("flow_demo_video", "first_video_play_skip_name");
        handleContinueToTest();
      },
      REPLAY_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        window.videoController?.replay();
      },
      PAUSE_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        if (window.videoController?.isPaused()) {
          VoiceService?.doSay("The video is already paused.");
        } else {
          VoiceService?.doSay("Sure! I'll pause the video...");
          window._lastPauseTime = Date.now();
          window.videoController?.pause();
        }
      },
      RESUME_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Sure! I'll resume the video...");
        window.videoController?.play();
      },
      EXPAND_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Sure! I'll expand the video...");
        window.videoController?.expand();
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        handleContinueToTest();
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command") handleContinueToTest();
      },
      _default: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("I didn't catch that one. Here's what I can help with: Skip, Replay, Pause, Expand");
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testIntroPayload]);

  // ── Mount: video controller, preloading, flow start ──
  useEffect(() => {
    // AR initialization function (called from mode selection or app shell)
    window.initAR = () => {
      window.loadROMExercises?.(true);
    };

    // Preload second test video
    const test2 = CONST.getNegativeTest(subtype, problemArea);
    if (test2) {
      const secondVideoUrl = CONST.TEST_INTRO_VIDEOS?.[test2.name] || test2.video;
      if (secondVideoUrl) {
        const secondVideo = document.createElement("video");
        secondVideo.id = "secondTestVideo";
        secondVideo.style.cssText = "width:1px;height:1px;position:absolute;opacity:0";
        secondVideo.src = secondVideoUrl;
        document.body.appendChild(secondVideo);

        // Toe Touch: preload third video if needed
        if (test2.name === "Toe Touch") {
          window.hasSecondVideoRotation = true;
          const thirdUrl = CONST.TEST_INTRO_VIDEOS?.["Toe Touch"];
          if (thirdUrl && thirdUrl !== secondVideoUrl) {
            const thirdVideo = document.createElement("video");
            thirdVideo.id = "secondTestVideo2";
            thirdVideo.style.cssText = "width:1px;height:1px;position:absolute;opacity:0";
            thirdVideo.src = thirdUrl;
            document.body.appendChild(thirdVideo);
          }
        }
      }
    }

    // iOS video unlock function
    window.unlockAllVideos = () => {
      const vid = document.getElementById("secondTestVideo");
      if (vid) {
        vid
          .play()
          .then(() => vid.pause())
          .catch(() => {});
      }
      const vid2 = document.getElementById("secondTestVideo2");
      if (vid2) {
        vid2
          .play()
          .then(() => vid2.pause())
          .catch(() => {});
      }
    };

    // Flow and commands start unconditionally on mount.
    // Session-level communication mode (header Chat/Voice toggle) is preserved from earlier screens.
    window.preloadAllAudios?.();
    startCorrectFlow();
    registerCommands();

    return () => {
      window.videoController = null;
      window.initAR = null;
      window.clickedOnceOpen = false;
      const sv = document.getElementById("secondTestVideo");
      if (sv) sv.remove();
      const sv2 = document.getElementById("secondTestVideo2");
      if (sv2) sv2.remove();
      window.unlockAllVideos = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${styles.inlineUi} ${styles.demovideoui}`}>
      {/* Demo Video — always shown */}
      <p className={styles.paSubtitle}>
        Watch how to do the <strong>{testName}</strong>.
      </p>

      <div className={styles.videoContainer}>
        <video ref={videoRef} src={introVideoUrl} controls playsInline preload="metadata" />
      </div>

      {/* Start Movement Test — always visible */}
      <div className={styles.videoActions}>
        <button className={`${styles.videoBtn} ${styles.videoBtnPrimary}`} onClick={handleContinueToTest} disabled={locked || !canStartTest}>
          Start Movement Test
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 9a: MODE SELECTION
// Silent card — no Myra message. Captures the user's preferred
// communication style for the movement test (when they're away
// from their device and CV needs to see them).
// Two options: Talk (voice + mic permission) / Chat (text).
// Advances to Camera Selection regardless of mic grant/deny.
// ════════════════════════════════════════════════════════════════

function ModeSelectionScreen({ assessmentData, pushToHistory, scrollToBottom, onContinue }) {
  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);

  const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
  const test = CONST.TEST_MAP[subtype];
  const testName = test?.name || assessmentData.testName || "";

  // Mount scroll — ModeSelectionScreen doesn't push to conversationHistory on enter,
  // so the shell's [conversationHistory] effect never fires for this screen. Match
  // the shell's own first-pass + 300ms second-pass pattern.
  useEffect(() => {
    scrollToBottom();
    const t = setTimeout(() => scrollToBottom(), 300);
    return () => clearTimeout(t);
  }, [scrollToBottom]);

  const handleVoiceMode = useCallback(async () => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);

    try {
      window.audioPlayer?.ctx?.resume();
    } catch (e) {
      /* ignore */
    }

    MyraInteraction?.confirmIntroARMode?.("voice");

    await window.requestMicrophonePermission?.({
      onGrant: () => {
        MyraInteraction?.switchMode?.("voice");
        window.mayraCode?.handleMicClick?.(true, true, false, true);
        const currentHealth = VoiceService?._isActuallyListening?.();
        if (currentHealth && !currentHealth.healthy) {
          console.log("[RECONNECT] Starting listening");
          VoiceService._startListening().catch(() => {
            setTimeout(() => {
              VoiceService._startListening().catch((e) => console.error("[RECONNECT] Retry also failed:", e));
            }, 2000);
          });
        }
        MyraInteraction?.closeVoice?.();
      },
      onDeny: () => {
        // Silent fallback to button mode per design
        MyraInteraction?.switchMode?.("button");
        VoiceService?.startFlowWithoutAnnouncement?.(window.currentFlow || VoiceService?._currentFlow);
        const msg = "No problem! We can chat instead. I'm here if you need anything — just type or use the buttons on screen.";
        MyraInteraction?.onMyraMessage?.(msg);
        MyraInteraction?.closeChat?.();
      },
    });

    pushToHistory({ type: "frozen-widget", widgetType: "mode_selection", id: nextId(), data: { selectedMode: "voice" } }, { type: "user", text: "Talk", id: nextId() });

    onContinue({});
  }, [pushToHistory, onContinue]);

  const handleChatMode = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);

    MyraInteraction?.switchMode?.("chat", true);
    VoiceService?.startFlowWithoutAnnouncement?.(window?.currentFlow || VoiceService?._currentFlow || "flow_demo_video");

    const chatAnnouncement = `We're going to try a movement called ${testName}. Tap "I'm Ready" when you want to begin.`;
    MyraInteraction?.onMyraMessage?.(chatAnnouncement);
    MyraInteraction?.closeChat?.();

    pushToHistory({ type: "frozen-widget", widgetType: "mode_selection", id: nextId(), data: { selectedMode: "chat" } }, { type: "user", text: "Chat", id: nextId() });

    onContinue({});
  }, [testName, pushToHistory, onContinue]);

  return (
    <div className={styles.inlineUi}>
      <div className={styles.modeSelectCard}>
        <p className={styles.modeSelectPrompt}>{MYRA_MESSAGES.modeSelection?.prompt}</p>
        <div className={styles.modeSelectOptions}>
          <button className={styles.modeSelectBtn} onClick={handleVoiceMode} disabled={locked}>
            <span className={styles.modeSelectIcon}>🎤</span>
            <span className={styles.modeSelectLabel}>{MYRA_MESSAGES.modeSelection?.talkLabel}</span>
            <span className={styles.modeSelectDesc}>{MYRA_MESSAGES.modeSelection?.talkDesc}</span>
          </button>
          <button className={styles.modeSelectBtn} onClick={handleChatMode} disabled={locked}>
            <span className={styles.modeSelectIcon}>💬</span>
            <span className={styles.modeSelectLabel}>{MYRA_MESSAGES.modeSelection?.chatLabel}</span>
            <span className={styles.modeSelectDesc}>{MYRA_MESSAGES.modeSelection?.chatDesc}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 9b: CAMERA PERMISSION
// Dialog-style camera access choice before movement test.
// ════════════════════════════════════════════════════════════════
function CameraPermissionScreen({ assessmentData, pushToHistory, showMyraMessage, onContinue }) {
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const lockedRef = useRef(false);

  // Derive AR handoff values from assessmentData (persisted by TestIntroScreen)
  const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
  const testName = assessmentData.testName || assessmentData.rom_test1_name || "";
  const cameraPayload = useMemo(() => generatePayloadForScreen("camera_permission", assessmentData), [assessmentData]);
  const movementTestPayload = useMemo(() => generatePayloadForScreen("movement_test_1", assessmentData), [assessmentData]);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      await showMyraMessage(MYRA_MESSAGES.camera?.prompt || "For the best experience, I can use your camera to track your movement in real time. Would you like to try that?", 800);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, [showMyraMessage]);

  const handleSelect = useCallback(
    async (choice) => {
      if (lockedRef.current) return;
      lockedRef.current = true;
      setLocked(true);

      const isAllow = Boolean(choice === "allow");
      const userText = isAllow ? "Use camera" : "No camera";

      pushToHistory({ type: "frozen-widget", widgetType: "camera_permission", id: nextId(), data: { selectedAnswer: choice } }, { type: "user", text: userText, id: nextId() });

      // Requested behavior: set noCameraMode=true when "Allow camera" is selected.
      console.log("isAllow", !isAllow);
      localStorage.setItem("noCameraMode", !isAllow);
      localStorage.setItem("cameFromGsaV2", "true");

      // ── AR handoff (moved here from TestIntroScreen) ──
      // This is the "moving to Movement Test" commit point.
      window.stopAudio?.();
      window.mayraCode?.disableCallback();

      window.selectedSubType = subtype;
      window.movementTestPayload = movementTestPayload;
      window.cameraPayload = cameraPayload;
      localStorage.setItem("runStep", "1");
      localStorage.setItem("loadSelf", testName);

      window.preloadAllAudios?.();
      window.videoUnlockPlay?.();

      if (subtype === "ROTATIONAL_INTOLERANT") {
        window.assessmentChangeExercise = true;
      }

      window.loadROMExercises?.(true);

      const followup = isAllow ? MYRA_MESSAGES.camera?.cameraAllowed : MYRA_MESSAGES.camera?.cameraDeclined;
      await showMyraMessage(followup || (isAllow ? "In the full app, the AR tracking screen would appear here. For this prototype, we'll simulate the camera path." : "No problem at all. I'll ask you a couple of quick questions after the movement instead."), 700);

      onContinue({
        cameraPermissionChoice: choice,
      });
    },
    [pushToHistory, showMyraMessage, onContinue, subtype, testName, cameraPayload, movementTestPayload],
  );

  if (!ready) return null;

  return (
    <div className={styles.inlineUi}>
      {!locked ? (
        <div className={styles.modeSelectCard}>
          <div className={styles.modeSelectIconRow}>
            <div className={styles.cameraAccessIcon}>📷</div>
            <p className={styles.modeSelectLabel}>Camera Access</p>
          </div>
          <p className={styles.paSubtitle}>Your camera stays on your device — we don't record or store anything</p>
          <div className={styles.feedback2Options}>
            <button className={styles.feedback2Btn} onClick={() => handleSelect("decline")}>
              No, I prefer not to
            </button>
            <button className={styles.videoBtnPrimary} onClick={() => handleSelect("allow")}>
              Yes, I'll enable my camera
            </button>
          </div>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 10: MOVEMENT TEST 1 (AR)
// Placeholder screen — AR/Flutter takes over entirely.
// On mount: clears commands, disables Myra, sets completion handler.
// AR writes dataAfterInfo to localStorage and calls
// window.doNextLocation.push() when complete.
// ════════════════════════════════════════════════════════════════

function MovementTest1Screen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue, onHardStop }) {
  const handleARComplete = useCallback(async () => {
    // Re-enable Myra
    window.mayraCode?.setIsVisible(true);
    window.mayraCode?.enableCallback?.();

    // Read AR results from localStorage
    let dataAfterInfo = {};
    try {
      const raw = localStorage.getItem("dataAfterInfo");
      if (raw) dataAfterInfo = JSON.parse(raw);
    } catch (e) {
      /* ignore parse errors */
    }

    // Push completion message with exercise name
    const completedTestName = CONST.TEST_MAP[assessmentData.probableSubtype]?.name || "the movement";
    await showMyraMessage(MYRA_MESSAGES.movementTest?.completionSummary ? resolveMessage(MYRA_MESSAGES.movementTest.completionSummary, { testName: completedTestName }) : `Great job — you completed the ${completedTestName}. Let me ask you a couple of follow-up questions.`, 800);

    // Determine path: camera vs no-camera
    const noCameraMode = localStorage.getItem("noCameraMode") === "true";
    const nextStep = noCameraMode ? "self_report_rom" : "rpe";
    window.currnetScreen = nextStep;

    onContinue(
      {
        ...dataAfterInfo,
        rom_test1_completed: true,
        noCameraMode,
      },
      nextStep,
    );
  }, [onContinue, assessmentData.probableSubtype, showMyraMessage]);

  useEffect(() => {
    // Clear commands and hide Myra during AR
    window.currentCommandsAudio = {};
    window.mayraCode?.disableCallback();
    window.mayraCode?.setIsVisible(false);

    // Push voice indicator to history
    pushToHistory({ type: "voice-indicator", id: nextId() });

    // AR completion handler — AR calls window.doNextLocation.push() when done
    window.doNextLocation = {
      push: () => {
        handleARComplete();
      },
    };

    // Safety hard stop handler — AR calls this if user can't complete reps
    window.handleARHardStop = () => {
      window.mayraCode?.setIsVisible(false);
      window.mayraCode?.forceStop?.();
      onHardStop("test1_pain");
    };

    return () => {
      window.doNextLocation = null;
      window.handleARHardStop = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.doKeepVideoPlay) window.doKeepVideoPlay();
    if (window.preloadAllAudios) {
      window.preloadAllAudios();
    }
    if (window.videoUnlockPlay) {
      window.videoUnlockPlay();
    }
    if (window.clickedOnceOpen) {
      return false;
    }

    document.body.classList.remove("videoplay-expanded");

    if (window.mayraCode) {
      window.mayraCode.disableCallback();
    }
    window.currentCommandsAudio = {};

    localStorage.setItem("runStep", "1");
    if (window.loadROMExercises) {
      window.loadROMExercises(true);
    }

    // Modified on Aug 7th - for user tracking

    if (document.querySelector("#assesmentDiv video")) {
      document.querySelector("#assesmentDiv video").pause();
      document.querySelector("#assesmentDiv video").removeAttribute("autoplay");
    }

    window.clickedOnceOpen = true;

    if (document.querySelector(".rom-test-start-button")) {
      document.querySelector(".rom-test-start-button").innerHTML = "Loading Movement Test…";
    }

    console.log("Tried open");

    if (window.doCameraScreenStart) {
      window.doCameraScreenStart();
      console.log("Tried open - ready");
    } else {
      let openIntervalCheck = setInterval(() => {
        if (window.doCameraScreenStart) {
          window.doCameraScreenStart();
          console.log("Tried open - ready");
          clearInterval(openIntervalCheck);
        } else {
          console.log("Tried open - not ready");
        }
      }, 1000);
    }
  }, []);

  return (
    <div className={styles.inlineUi} style={{ textAlign: "center" }}>
      <p style={{ fontWeight: 600, marginBottom: 8 }}>Movement Test in Progress</p>
      <p style={{ color: "#6b7280", fontSize: "14px" }}>Follow Myra's voice instructions to complete the movement.</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 11: SELF-REPORT STEP 1 — ROM COMPARISON
// No-camera path only. Three option cards (Got smaller / Got bigger /
// Stayed the same). No hard stop on this screen.
// Uses explicit startFlow — not standard useScreenVoice flow.
// ════════════════════════════════════════════════════════════════

function SelfReportROMScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [selectedId, setSelectedId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const lockedRef = useRef(false);
  const ContainerRef = useRef(null);
  const isVoiceMode = MyraInteraction.getMode() === "voice";
  const selectedIdRef = useRef(null);
  if (window?.mayraCode) {
    window.mayraCode?.setIsVisible(false);
  }

  // Push divider on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      // No Myra chat message — the SR card with heading/subheading serves as the prompt
      await showMyraMessage(MYRA_MESSAGES.selfReportRom?.prompt || "Thinking about your range of motion during the movement, did it...", 800);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);
  const completedTestName = CONST.TEST_MAP[assessmentData?.probableSubtype]?.name || "the movement";
  useEffect(() => {
    if (window?.currnetScreen === "self_report_rom" && ready) {
      pushToHistory({
        type: "text",
        text: `Great job — you completed the ${completedTestName}. Let me ask you a couple of follow-up questions.`,
        id: nextId(),
      });
      window.currnetScreen = "";
    }
  }, [pushToHistory, completedTestName, ready]);

  const handleSelect = useCallback(
    async (id, source = "tap") => {
      if (lockedRef.current) return;
      lockedRef.current = true;
      setSelectedId(id);
      selectedIdRef.current = id;
      setLocked(true);

      const opt = CONST.SELF_REPORT_ROM.options.find((o) => o.id === id);

      // Push frozen widget + user echo
      pushToHistory({ type: "frozen-widget", widgetType: "sr_cards", id: nextId(), data: { heading: CONST.SELF_REPORT_ROM.heading, options: CONST.SELF_REPORT_ROM.options, selectedId: id, subheading: CONST.SELF_REPORT_ROM.subheading } }, { type: "user", text: opt.title, id: nextId() });

      const continueWithSelection = async () => {
        await showMyraMessage(MYRA_MESSAGES.selfReportRom?.transitionToTempo || "Thanks for sharing. One more question.", 800);
        onContinue({
          selfReportRom: opt.id,
          selfReportRomPoints: opt.points,
        });
      };
      const replyText = CONST.getSelfReportReplyText("rom", opt.id);
      if (source !== "tap" && replyText && VoiceService && isVoiceMode) {
        // Wait for speech completion to avoid clipping audio on fast screen transition.
        VoiceService.doSay(replyText, continueWithSelection);
      } else {
        continueWithSelection();
      }
    },
    [onContinue, pushToHistory, showMyraMessage],
  );

  // Boilerplate only — skip standard flow
  useScreenVoice({
    screenName: "self_report_compare",
    flowName: "flow_selfreport_compare",
    payloadKey: "self_report_compare",
    exitReason: "self_report_rom_completed",
    skipFlow: true,
  });

  // Manual flow start + command registration
  useEffect(() => {
    const mayraCode = window.mayraCode;
    if (!mayraCode) return;
    window.mayraForwardCode = {
      midjourneyData: generatePayloadForScreen("self_report_compare", assessmentData),
    };
    if (isVoiceMode) {
      mayraCode.startFlow("flow_selfreport_compare", "self_report_compare");
    } else {
      VoiceService.startFlowWithoutAnnouncement("flow_selfreport_compare");
    }

    window.currentCommandsAudio = {
      SELECT_COMPARE: (value) => {
        if (window.exitHandlerOpen) return;
        const normalized = (value || "").toString().trim().toLowerCase();
        console.log("normalized", normalized);
        const opt = CONST.SELF_REPORT_ROM.options.find((o) => o.id === normalized);
        if (opt && !lockedRef.current) {
          handleSelect(opt.id, "voice_or_chat");
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command" && selectedIdRef.current) {
          const opt = CONST.SELF_REPORT_ROM.options.find((o) => o.id === selectedIdRef.current);
          if (opt) {
            onContinue({ selfReportRom: opt.id, selfReportRomPoints: opt.points });
          }
        }
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (selectedIdRef.current) {
          const opt = CONST.SELF_REPORT_ROM.options.find((o) => o.id === selectedIdRef.current);
          if (opt) {
            onContinue({ selfReportRom: opt.id, selfReportRomPoints: opt.points });
          }
        } else {
          if (isVoiceMode) {
            VoiceService?.doSay("Please make your selection before we can move to the next screen");
          }
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ready && ContainerRef.current) {
      setTimeout(() => {
        ContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [ready]);

  return (
    <div className={styles.inlineUi} ref={ContainerRef}>
      {!locked ? (
        <div className={styles.srScreen}>
          <h3 className={styles.srHeading}>{CONST.SELF_REPORT_ROM.heading}</h3>
          <p className={styles.srSubheading}>{CONST.SELF_REPORT_ROM.subheading}</p>

          <div className={styles.srOptions}>
            {CONST.SELF_REPORT_ROM.options.map((opt) => (
              <div key={opt.id} className={`${styles.srCard} ${selectedId === opt.id ? styles.selected : ""} ${locked ? styles.locked : ""}`} onClick={() => handleSelect(opt.id, "tap")}>
                <h4 className={styles.srCardTitle}>{opt.title}</h4>
                <p className={styles.srCardSubtitle}>{opt.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 12: SELF-REPORT STEP 2 — TEMPO / SPEED
// No-camera path only. Three option cards.
// "Couldn't finish" → safety hard stop.
// Sets cameraUsed: false so backend uses self-report Pillar 4 logic.
// ════════════════════════════════════════════════════════════════

function SelfReportTempoScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue, onHardStop }) {
  const [selectedId, setSelectedId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const lockedRef = useRef(false);
  const selectedIdRef = useRef(null);
  const isVoiceMode = MyraInteraction.getMode() === "voice";
  if (window?.mayraCode) {
    window.mayraCode?.setIsVisible(false);
  }

  // Push divider on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      // Show Myra message
      await showMyraMessage(MYRA_MESSAGES.selfReportTempo?.prompt || "Thinking about your pace during the movement, did you...", 800);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = useCallback(
    async (id, source = "tap") => {
      if (lockedRef.current) return;
      lockedRef.current = true;
      setSelectedId(id);
      selectedIdRef.current = id;
      setLocked(true);

      const opt = CONST.SELF_REPORT_TEMPO.options.find((o) => o.id === id);

      // Push frozen widget + user echo
      pushToHistory({ type: "frozen-widget", widgetType: "sr_cards", id: nextId(), data: { heading: CONST.SELF_REPORT_TEMPO.heading, options: CONST.SELF_REPORT_TEMPO.options, selectedId: id, subheading: CONST.SELF_REPORT_TEMPO.subheading } }, { type: "user", text: opt.title, id: nextId() });

      // "Couldn't finish" → hard stop, no voice reply
      if (opt.outcome === "HARD_STOP") {
        onHardStop();
        return;
      }

      const continueWithSelection = async () => {
        await showMyraMessage(MYRA_MESSAGES.selfReportTempo?.transitionToRpe || "Thanks — one more question about the movement.", 800);
        onContinue({
          selfReportSpeed: opt.id,
          selfReportPace: opt.id,
          selfReportSpeedPoints: opt.points,
          cameraUsed: false,
        });
      };

      const replyText = CONST.getSelfReportReplyText("tempo", opt.id);
      console.log("source", source);
      if (source !== "tap" && replyText && VoiceService && isVoiceMode) {
        // Wait for speech completion to avoid clipping audio on fast screen transition.
        VoiceService.doSay(replyText, continueWithSelection);
      } else {
        continueWithSelection();
      }
    },
    [onContinue, onHardStop, pushToHistory, showMyraMessage],
  );

  // Boilerplate only — skip standard flow
  useScreenVoice({
    screenName: "self_report_speed",
    flowName: "flow_selfreport_speed",
    payloadKey: "self_report_speed",
    exitReason: "self_report_tempo_completed",
    skipFlow: true,
  });

  // Manual flow start + command registration
  useEffect(() => {
    const mayraCode = window.mayraCode;
    if (!mayraCode) return;
    window.mayraForwardCode = {
      midjourneyData: generatePayloadForScreen("self_report_speed", assessmentData),
    };
    if (isVoiceMode) {
      mayraCode.startFlow("flow_selfreport_speed", "flow_selfreport_speed");
    } else {
      VoiceService.startFlowWithoutAnnouncement("flow_selfreport_speed");
    }

    window.currentCommandsAudio = {
      SELECT_SPEED: (value) => {
        if (window.exitHandlerOpen) return;
        const normalized = (value || "").toString().trim().toLowerCase();
        console.log("normalized", normalized);
        const opt = CONST.SELF_REPORT_TEMPO.options.find((o) => o.id === normalized);
        if (opt && !lockedRef.current) {
          handleSelect(opt.id, "voice_or_chat");
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command" && selectedIdRef.current) {
          const opt = CONST.SELF_REPORT_TEMPO.options.find((o) => o.id === selectedIdRef.current);
          if (opt && opt.outcome !== "HARD_STOP") {
            onContinue({
              selfReportSpeed: opt.id,
              selfReportPace: opt.id,
              selfReportSpeedPoints: opt.points,
              cameraUsed: false,
            });
          }
        }
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (selectedIdRef.current) {
          const opt = CONST.SELF_REPORT_TEMPO.options.find((o) => o.id === selectedIdRef.current);
          if (opt && opt.outcome !== "HARD_STOP") {
            onContinue({
              selfReportSpeed: opt.id,
              selfReportPace: opt.id,
              selfReportSpeedPoints: opt.points,
              cameraUsed: false,
            });
          }
        } else {
          if (isVoiceMode) {
            VoiceService?.doSay("Please make your selection before we can move to the next screen");
          }
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) return null;

  return (
    <>
      <div className={styles.inlineUi}>
        {!locked ? (
          <div className={styles.srScreen}>
            <h3 className={styles.srHeading}>{CONST.SELF_REPORT_TEMPO.heading}</h3>
            <p className={styles.srSubheading}>{CONST.SELF_REPORT_TEMPO.subheading}</p>

            <div className={styles.srOptions}>
              {CONST.SELF_REPORT_TEMPO.options.map((opt) => (
                <div key={opt.id} className={`${styles.srCard} ${selectedId === opt.id ? styles.selected : ""} ${locked ? styles.locked : ""}`} onClick={() => handleSelect(opt.id, "tap")}>
                  <h4 className={styles.srCardTitle}>{opt.title}</h4>
                  <p className={styles.srCardSubtitle}>{opt.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div></div>
        )}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// UTILITY: calculateStdDev
// Standard deviation for ROM and rep time arrays.
// Used in Feel Affect's enriched mayraForwardCode.
// ════════════════════════════════════════════════════════════════

function calculateStdDev(values) {
  if (!values || values.length < 2) return 0;
  const n = values.length;
  const mean = values.reduce((sum, v) => sum + v, 0) / n;
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / n;
  return Math.sqrt(variance);
}

// ════════════════════════════════════════════════════════════════
// AI PROCESSING LOADER
// Shown while Feel Affect waits for backend customSpeech.
// Blocks UI interaction with an overlay.
// ════════════════════════════════════════════════════════════════

function AIProcessingLoader({ isVisible }) {
  if (!isVisible) return null;

  return (
    <div className={styles.aiProcessingOverlay}>
      <div className={styles.aiProcessingCard}>
        <div className={styles.aiProcessingSpinner} />
        <p className={styles.aiProcessingText}> I'm analyzing your movement, before we continue...</p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 13: RPE (Rate of Perceived Exertion)
// 5 RPE circles (1/3/5/7/9). Uses flow_rpe with manual startFlow.
// Extracts ROM/dur from parseDataAfterInfo() on mount.
// Cleans up window.movementTestPayload.
// ════════════════════════════════════════════════════════════════

function RPEScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [selectedRPE, setSelectedRPE] = useState(null);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const lockedRef = useRef(false);
  const selectedRef = useRef(null);
  const ContainerRef = useRef(null);
  const romDurRef = useRef({ rom: [], dur: [] });
  const isVoiceMode = MyraInteraction.getMode() === "voice";
  if (window?.mayraCode) {
    window.mayraCode?.setIsVisible(false);
  }

  // Extract ROM/dur on mount and clean up AR config
  useEffect(() => {
    romDurRef.current = parseDataAfterInfo();
    window.movementTestPayload = null;
  }, []);
  const completedTestName = CONST.TEST_MAP[assessmentData?.probableSubtype]?.name || "the movement";
  useEffect(() => {
    if (window?.currnetScreen === "rpe" && ready) {
      if (isVoiceMode) {
        pushToHistory({
          type: "voice-indicator",
          text: `Great job — you completed the ${completedTestName}. Let me ask you a couple of follow-up questions.`,
          id: nextId(),
        });
      }
      window.currnetScreen = "";
    }
  }, [pushToHistory, completedTestName, ready]);

  // Push divider + voice indicator + Myra message on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (isVoiceMode) {
        pushToHistory({ type: "voice-indicator", id: nextId() });
      }
      await showMyraMessage(MYRA_MESSAGES.rpe?.prompt || "How challenging did that movement feel?", 800);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleContinue = useCallback(() => {
    if (selectedRef.current === null || lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);

    const { rom, dur } = romDurRef.current;
    onContinue({
      rpe: selectedRef.current,
      rom,
      duration: dur,
    });
  }, [onContinue]);

  useScreenVoice({
    screenName: "test1_rpe",
    flowName: "flow_rpe",
    payloadKey: "test1_rpe",
    exitReason: "rpe_completed",
    skipFlow: false,
    commands: {
      SELECT_RPE: (value) => {
        console.log("useScreenVoice: SELECT_RPE");
        if (window.exitHandlerOpen) return;
        if (lockedRef.current) return;
        const numVal = parseInt(value);
        // if (isNaN(numVal)) return;

        const responseText = CONST.getRpeResponseText(numVal);
        if (MyraInteraction.getMode() === "voice") {
          VoiceService?.doSay(responseText, () => {
            // setSelectedRPE(numVal);
            // selectedRef.current = numVal;
            handleSelect(numVal);
          });
        } else {
          // setSelectedRPE(numVal);
          // selectedRef.current = numVal;
          handleSelect(numVal);
        }
        // lockedRef.current = true;
        // setLocked(true);
        // VoiceService?.doSay(responseText, () => {
        //   setSelectedRPE(numVal);
        //   selectedRef.current = numVal;
        //   setTimeout(() => {
        //     const { rom, dur } = romDurRef.current;
        //     onContinue({ rpe: numVal, rom, duration: dur });
        //   }, 500);
        // });
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command" && selectedRef.current !== null) {
          const { rom, dur } = romDurRef.current;
          onContinue({ rpe: selectedRef.current, rom, duration: dur });
        }
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (selectedRef.current !== null) {
          const { rom, dur } = romDurRef.current;
          onContinue({ rpe: selectedRef.current, rom, duration: dur });
        } else {
          if (isVoiceMode) {
            VoiceService?.doSay("Please select how challenging the exercise was before we continue.");
          }
        }
      },
    },
  });

  // Manual flow start + command registration
  useEffect(() => {
    const mayraCode = window.mayraCode;
    if (!mayraCode) return;

    window.mayraForwardCode = {
      midjourneyData: generatePayloadForScreen("test1_rpe", assessmentData),
    };
    VoiceService._currentFlow = "flow_rpe";
    window.currentFlow = "flow_rpe";
    if (isVoiceMode) {
      mayraCode.startFlow("flow_rpe", "rpe");
    } else {
      VoiceService?.startFlowWithoutAnnouncement?.("flow_rpe");
    }

    window.currentCommandsAudio = {
      SELECT_RPE: (value) => {
        console.log("Window currentCommandsAudio : SELECT_RPE");
        if (window.exitHandlerOpen) return;
        if (lockedRef.current) return;
        const numVal = parseInt(value);
        // if (isNaN(numVal)) return;

        const responseText = CONST.getRpeResponseText(numVal);
        if (MyraInteraction.getMode() === "voice") {
          VoiceService?.doSay(responseText, () => {
            // setSelectedRPE(numVal);
            // selectedRef.current = numVal;
            handleSelect(numVal);
          });
        } else {
          // setSelectedRPE(numVal);
          // selectedRef.current = numVal;
          handleSelect(numVal);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command" && selectedRef.current !== null) {
          const { rom, dur } = romDurRef.current;
          onContinue({ rpe: selectedRef.current, rom, duration: dur });
        }
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (selectedRef.current !== null) {
          const { rom, dur } = romDurRef.current;
          onContinue({ rpe: selectedRef.current, rom, duration: dur });
        } else {
          if (isVoiceMode) {
            VoiceService?.doSay("Please select how challenging the exercise was before we continue.");
          }
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback(
    async (val) => {
      if (lockedRef.current) return;
      lockedRef.current = true;
      setSelectedRPE(val);
      selectedRef.current = val;
      setLocked(true);

      const label = CONST.RPE_OPTIONS.find((o) => o.value === val)?.label || val;

      // Push frozen widget + user echo
      pushToHistory({ type: "frozen-widget", widgetType: "rpe_options", id: nextId(), data: { selectedValue: val } }, { type: "user", text: `Effort: ${label}`, id: nextId() });

      // Confirmation message
      await showMyraMessage(MYRA_MESSAGES.rpe?.transitionToFeelAffect || "Got it. One last question about how that movement affected your symptoms.", 800);

      // Auto-advance
      const { rom, dur } = romDurRef.current;
      onContinue({ rpe: val, rom, duration: dur });
    },
    [onContinue, pushToHistory, showMyraMessage],
  );
  useEffect(() => {
    if (ready && ContainerRef.current) {
      setTimeout(() => {
        ContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [ready]);

  if (!ready) return null;

  return (
    <div className={styles.inlineUi} ref={ContainerRef}>
      {!locked ? (
        <>
          <div className={styles.rpeOptions}>
            {CONST.RPE_OPTIONS.map((o) => {
              const isSelected = selectedRPE === o.value;
              return (
                <button key={o.value} className={`${styles.rpeBtn} ${isSelected ? styles.selected : ""} ${locked ? styles.locked : ""}`} onClick={() => handleSelect(o.value)}>
                  {o.label}
                </button>
              );
            })}
          </div>
          <div className={styles.rpeLabels}>
            <span className={styles.rpeLow}>No Effort</span>
            <span className={styles.rpeHigh}>Max Effort</span>
          </div>
        </>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 14: FEEL AFFECT (Symptom Response 1)
// 3 option cards: IMPROVED / TOLERABLE / WORSENED.
// Enriched mayraForwardCode for backend AI analysis.
// AIProcessingLoader with 60s timeout.
// Subtype override/reclassification logic.
// ════════════════════════════════════════════════════════════════
function FeelAffectScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [symptomResponse, setSymptomResponse] = useState(null);
  const [locked, setLocked] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [ready, setReady] = useState(false);
  const lockedRef = useRef(false);
  const symptomResponseRef = useRef(null);
  const hasContinuedRef = useRef(false);
  const aiCheckRef = useRef(null);
  const aiTimeoutRef = useRef(null);
  const pendingOptRef = useRef(null); // Store opt for when speech arrives
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  const problemArea = assessmentData.problemArea || "LBP";
  const currentSubtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";

  // Push divider + voice indicator + Myra message on mount
  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (isVoiceMode) {
        pushToHistory({ type: "voice-indicator", id: nextId() });
      }
      await showMyraMessage(MYRA_MESSAGES.feelAffect?.prompt || "How did that movement make your symptoms feel?", 800);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fallback speech by symptom value
  const getFallbackSpeech = useCallback((value) => {
    if (value === "IMPROVED") return "That's great to hear — it tells me a lot about what direction works for you.";
    if (value === "TOLERABLE") return "Thanks for letting me know. That's useful information for building your plan.";
    return "Thanks for telling me — that's actually very useful information. It helps me adjust your classification.";
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(aiCheckRef.current);
      clearTimeout(aiTimeoutRef.current);
    };
  }, []);

  // Resolve override and routing
  const resolveAndContinue = useCallback(
    async (opt) => {
      // if (hasContinuedRef.current) return;
      hasContinuedRef.current = true;
      let finalSubtype = currentSubtype;
      let diagnosisChanged = false;
      let degradation = null;
      let timing = null;
      let cpgForBackend = assessmentData.cpgForBackend;
      window.flowSymptomPayload = null;

      if (opt.value === "WORSENED") {
        diagnosisChanged = true;

        if (problemArea === "LBP") {
          finalSubtype = "NEUTRAL_SENSITIVE";
          degradation = "First Test Felt Worse — LBP reclassified to Neutral Sensitive";
        } else {
          const overrideTarget = CONST.CLINICAL_PATHS[problemArea]?.overrideTarget;
          finalSubtype = overrideTarget || currentSubtype;
          degradation = "First Test Felt Worse — reclassified to override target";
          if (CONST.SUBTYPE_TO_CPG_MAP[problemArea]?.[finalSubtype]) {
            cpgForBackend = CONST.SUBTYPE_TO_CPG_MAP[problemArea][finalSubtype];
          }
        }
        timing = "post_test1_symptom";

        await showMyraMessage(MYRA_MESSAGES.feelAffect?.worsenedOverride || "Thanks for telling me — that's actually very useful information. It helps me adjust your classification. Let's move on to personalizing your plan.", 800);
        onContinue(
          {
            symptomResponse: opt.value,
            symptomResponse1: opt.value,
            diagnosisChanged,
            degradation,
            timing,
            finalPainSubtype: finalSubtype,
            probableSubtype: finalSubtype,
            cpgForBackend,
            wasOverridden: true,
            overrideReason: "First Test Felt Worse",
          },
          "personalize",
        );
        return;
      }

      if (problemArea === "LBP" && currentSubtype === "ROTATIONAL_INTOLERANT" && opt.value === "IMPROVED") {
        finalSubtype = "FLEXION_INTOLERANT";
        diagnosisChanged = true;
        degradation = "Directional Preference Overwrite — positive extension response";
        timing = "post_test1_symptom";

        await showMyraMessage(MYRA_MESSAGES.feelAffect?.rotationalImprovedOverride || "That's great feedback — it tells me a lot about what direction works for you. Let's move on.", 800);
        onContinue(
          {
            symptomResponse: opt.value,
            symptomResponse1: opt.value,
            diagnosisChanged,
            degradation,
            timing,
            finalPainSubtype: finalSubtype,
            probableSubtype: finalSubtype,
            wasOverridden: true,
            overrideReason: degradation,
          },
          "personalize",
        );
        return;
      }

      const finalFields = {
        symptomResponse: opt.value,
        symptomResponse1: opt.value,
        diagnosisChanged,
        degradation,
        timing,
        finalPainSubtype: finalSubtype,
        probableSubtype: diagnosisChanged ? finalSubtype : currentSubtype,
        wasOverridden: diagnosisChanged,
        overrideReason: diagnosisChanged ? degradation : null,
      };

      if (diagnosisChanged && cpgForBackend !== assessmentData.cpgForBackend) {
        finalFields.cpgForBackend = cpgForBackend;
      }

      const test2 = CONST.getNegativeTest(finalSubtype, problemArea);

      if (test2) {
        const testName = test2.name || "";
        await showMyraMessage(resolveMessage(MYRA_MESSAGES.feelAffect?.transitionToTest2 || "There's one more movement I'd like you to try — <strong>{testName}</strong>. Let me show you.", { testName }), 800);
        onContinue(finalFields, "test2_demo");
      } else {
        await showMyraMessage(MYRA_MESSAGES.feelAffect?.noTest2 || "That's all the movement testing we need. You're doing great — let's wrap things up.", 800);
        onContinue(finalFields, "personalize");
      }
    },
    [currentSubtype, problemArea, assessmentData.cpgForBackend, onContinue, showMyraMessage],
  );

  // Handle the AI processing and speech announcement
  const processWithAI = useCallback(
    (opt, customSpeech) => {
      const isVoiceMode = MyraInteraction.getMode() === "voice";
      const speech = customSpeech || getFallbackSpeech(opt.value);

      console.log("🤖 processWithAI called:", {
        mode: isVoiceMode ? "voice" : "chat",
        hasSpeech: !!customSpeech,
        value: opt.value,
      });
      setIsAIProcessing(true);

      // Announce the AI processing message
      const processingMessage = "Thanks for your effort — let me take a moment to look at your results.";
      // VoiceService?.doSay(processingMessage);

      setTimeout(() => {
        window.mayraCode?.doSay(speech, () => {
          setIsAIProcessing(false);
          resolveAndContinue(opt);
        });
      }, 500);
    },
    [getFallbackSpeech, resolveAndContinue],
  );
  const announceAndContinue = useCallback(
    (opt, customSpeech) => {
      // if (hasContinuedRef.current) return;

      setIsAIProcessing(true);
      const speech = customSpeech || getFallbackSpeech(opt.value);

      if (window.mayraCode) {
        window.mayraCode.doSay(speech, () => {
          setIsAIProcessing(false);
          resolveAndContinue(opt);
        });
      } else {
        setIsAIProcessing(false);
        resolveAndContinue(opt);
      }
    },
    [getFallbackSpeech, resolveAndContinue],
  );

  const handleSelect = useCallback(
    (id) => {
      if (lockedRef.current || isAIProcessing) return;
      lockedRef.current = true;
      setLocked(true);

      const opt = CONST.FEEL_AFFECT_OPTIONS.find((o) => o.id === id);
      setSymptomResponse(opt.value);
      symptomResponseRef.current = opt.value;

      // STORE the opt for potential async speech - UNCOMMENT THIS
      pendingOptRef.current = opt;

      const symptomData = {
        type: "symptom_selection",
        data: { action: "SELECT_SYMPTOM", value: opt.value },
      };
      if (window.mayraCode && VoiceService._ws && VoiceService._ws.readyState === WebSocket.OPEN) {
        VoiceService._ws.send(JSON.stringify(symptomData));
        console.log(symptomData);
      } else {
        console.error("WebSocket not connected");
      }

      // Push frozen widget + user echo - UNCOMMENT THIS
      pushToHistory(
        {
          type: "frozen-widget",
          widgetType: "affect_options",
          id: nextId(),
          data: { selectedValue: opt.value },
        },
        { type: "user", text: opt.label, id: nextId() },
      );

      // Set timeout for fallback speech
      setTimeout(() => {
        if (pendingOptRef.current && !hasContinuedRef.current) {
          console.log("Using fallback speech for:", pendingOptRef.current.value);
          announceAndContinue(pendingOptRef.current, null);
          pendingOptRef.current = null;
        }
      }, 5000);
    },
    [isAIProcessing, pushToHistory, announceAndContinue],
  );
  // useScreenVoice for boilerplate only — flow handled manually
  useScreenVoice({
    screenName: "test1_symptoms",
    trackerName: "symptom1",
    flowName: "flow_symptom",
    payloadKey: "test1_symptoms",
    exitReason: "symptom1_completed",
    skipFlow: false,
    commands: {
      SELECT_SYMPTOM: (value, speech, intent) => {
        console.log("SELECT_SYMPTOM triggered with:", { value, speech, intent });

        if (window.exitHandlerOpen) return;

        // If we have speech from backend, use it
        if (speech && pendingOptRef.current) {
          // Clear the timeout since we got speech
          if (window._speechTimeout) {
            clearTimeout(window._speechTimeout);
            window._speechTimeout = null;
          }
          // Process with the custom speech
          processWithAI(pendingOptRef.current, speech);
          pendingOptRef.current = null;
          return;
        }

        const opt = CONST.FEEL_AFFECT_OPTIONS.find((o) => o.value === value || o.id === value);
        if (opt && !lockedRef.current) {
          handleSelect(opt.id);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command" && symptomResponseRef.current) {
          const opt = CONST.FEEL_AFFECT_OPTIONS.find((o) => o.value === symptomResponseRef.current);
          if (opt) resolveAndContinue(opt);
        }
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (symptomResponseRef.current) {
          const opt = CONST.FEEL_AFFECT_OPTIONS.find((o) => o.value === symptomResponseRef.current);
          if (opt) resolveAndContinue(opt);
        } else {
          if (isVoiceMode) {
            window.VoiceService?.doSay("Please select how the movement made your symptoms feel.");
          }
        }
      },
    },
  });

  // Manual flow start with enriched mayraForwardCode
  useEffect(() => {
    const mayraCode = window.mayraCode;
    if (!mayraCode) return;

    const { rom, dur } = parseDataAfterInfo();
    const romValues = rom || [];
    const durValues = dur || [];

    window.mayraForwardCode = {
      romStdDev: calculateStdDev(romValues),
      repTimeStdDev: calculateStdDev(durValues),
      rpe: assessmentData.rpe,
      painLocations: assessmentData.selectedZones,
      symptomDuration: assessmentData.symptomDuration,
      preTestNPRS: assessmentData.painLevel,
      midjourneyData: generatePayloadForScreen("test1_symptoms", assessmentData),
      symptomType: "initial",
    };
    window.flowSymptomPayload = {
      romStdDev: calculateStdDev(romValues),
      repTimeStdDev: calculateStdDev(durValues),
      rpe: assessmentData.rpe,
      painLocations: assessmentData.selectedZones,
      symptomDuration: assessmentData.symptomDuration,
      preTestNPRS: assessmentData.painLevel,
      midjourneyData: generatePayloadForScreen("test1_symptoms", assessmentData),
      symptomType: true,
    };

    if (isVoiceMode) {
      mayraCode.startFlow("flow_symptom", "symptom1");
    } else {
      VoiceService.startFlowWithoutAnnouncement("flow_symptom");
    }

    window.currentCommandsAudio = {
      SELECT_SYMPTOM: (value, speech, intent) => {
        console.log("SELECT_SYMPTOM triggered with:", { value, speech, intent });
        if (window.exitHandlerOpen) return;

        // If we have speech from backend, use it
        if (speech && pendingOptRef.current) {
          if (window._speechTimeout) {
            clearTimeout(window._speechTimeout);
            window._speechTimeout = null;
          }
          processWithAI(pendingOptRef.current, speech);
          pendingOptRef.current = null;
          return;
        }

        const opt = CONST.FEEL_AFFECT_OPTIONS.find((o) => o.value === value || o.id === value);
        if (opt && !lockedRef.current) {
          handleSelect(opt.id);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command" && symptomResponseRef.current) {
          const opt = CONST.FEEL_AFFECT_OPTIONS.find((o) => o.value === symptomResponseRef.current);
          if (opt) resolveAndContinue(opt);
        }
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (symptomResponseRef.current) {
          const opt = CONST.FEEL_AFFECT_OPTIONS.find((o) => o.value === symptomResponseRef.current);
          if (opt) resolveAndContinue(opt);
        } else {
          if (isVoiceMode) {
            window.VoiceService?.doSay("Please select how the movement made your symptoms feel.");
          }
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) return null;

  return (
    <>
      {!locked ? (
        <div className={styles.inlineUi}>
          <div className={styles.affectOptions}>
            {CONST.FEEL_AFFECT_OPTIONS.map((o) => (
              <div key={o.id} className={`${styles.affectBtn} ${symptomResponse === o.value ? styles.selected : ""} ${locked ? styles.locked : ""}`} onClick={() => handleSelect(o.id)}>
                {o.label}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div></div>
      )}
      <AIProcessingLoader isVisible={isAIProcessing} />
    </>
  );
}
// ════════════════════════════════════════════════════════════════
// SCREEN 15: TEST INTRO 2
// Demo video for second movement test. Simpler than Test Intro 1:
// no mode selection, no name collection.
// Uses flow_demo_video / second_test.
// ════════════════════════════════════════════════════════════════

function TestIntro2Screen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);

  // Resolve test 2 info from the (possibly overridden) subtype
  const subtype = assessmentData.probableSubtype || assessmentData.finalPainSubtype || "NEUTRAL_SENSITIVE";
  const problemArea = assessmentData.problemArea || "LBP";
  const test2 = CONST.getNegativeTest(subtype, problemArea);
  const testName = test2?.name || "";
  const introVideoUrl = CONST.TEST_INTRO_VIDEOS?.[testName] || test2?.video || "";

  const movementTestPayload = useMemo(() => generatePayloadForScreen("movement_test_2", assessmentData), [assessmentData]);
  window.flowSymptomPayload = null;

  // ── Push divider + Myra message on mount ──
  useEffect(() => {
    let cancelled = false;
    async function init() {
      pushToHistory({ type: "voice-indicator", id: nextId() });
      const msg =
        MYRA_MESSAGES.testIntro2?.prompt ||
        `For your second movement test, I've chosen the <strong>${testName}</strong>. Watch the demo video to see how it's done. When you're ready, tap 'Start Movement Test' to begin.
      If you feel this movement might worsen your pain or seems too difficult, you'll have the option to press 'Skip Exercise' on the next screen. Skipping is always okay—your safety comes first.`;

      await showMyraMessage(msg, 800);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleContinueToTest2 = useCallback(() => {
    window.clickedOnceOpen = true;
    if (locked) return;
    setLocked(true);

    const watchedDuration = videoRef.current?.currentTime || 0;
    const totalDuration = videoRef.current?.duration || 0;

    // Clean up video
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute("src");
      videoRef.current.load();
    }
    document.body.classList.remove("videoplay-expanded");

    // Stop audio and disable Myra before AR
    window.stopAudio?.();
    window.mayraCode?.disableCallback();

    // Set AR configuration globals for Test 2
    window.selectedSubType = subtype;
    window.movementTestPayload = movementTestPayload;
    localStorage.setItem("runStep", "2");
    localStorage.setItem("loadSelf", testName);
    window.blockNextSelfCall = true;

    // Preload audio and unlock video for AR
    window.preloadAllAudios?.();
    window.videoUnlockPlay?.();

    // Trigger AR
    window.loadROMExercises?.(true);

    // Push user echo
    pushToHistory({ type: "frozen-widget", widgetType: "demo_video", id: nextId(), data: { testName, videoUrl: introVideoUrl } }, { type: "user", text: "Start Movement Test", id: nextId() });

    onContinue({
      testName2: testName,
      test2VideoWatched: watchedDuration > 0,
      test2VideoDuration: watchedDuration,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, subtype, testName, movementTestPayload]);

  // useScreenVoice for boilerplate only — flow handled manually
  useScreenVoice({
    screenName: "test2_demo",
    flowName: "flow_demo_video",
    payloadKey: "test2_demo",
    exitReason: "started_test2",
    skipFlow: true,
    commands: {
      SKIP_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Got it! Skipping the video.");
        handleContinueToTest2();
      },
      REPLAY_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        window.videoController?.replay();
      },
      PAUSE_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        if (window.videoController?.isPaused()) {
          VoiceService?.doSay("The video is already paused.");
        } else {
          VoiceService?.doSay("Sure! I'll pause the video...");
          window._lastPauseTime = Date.now();
          window.videoController?.pause();
        }
      },
      RESUME_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Sure! I'll resume the video...");
        window.videoController?.play();
      },
      EXPAND_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Sure! I'll expand the video...");
        window.videoController?.expand();
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        handleContinueToTest2();
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command") handleContinueToTest2();
      },
    },
  });

  // Manual flow start + command registration
  useEffect(() => {
    const mayraCode = window.mayraCode;
    if (!mayraCode) return;

    // Wait for Myra to finish speaking before auto-playing video
    function playVideoWhenReady() {
      if (window.mayraCode?.getState?.()?.isAgentSpeaking) {
        setTimeout(playVideoWhenReady, 500);
        return;
      }
      videoRef.current?.play();
    }

    window.mayraForwardCode = {
      midjourneyData: generatePayloadForScreen("test2_demo", assessmentData),
    };
    mayraCode.startFlow("flow_demo_video", "second_test", () => {
      playVideoWhenReady();
    });

    // Video controller
    window.videoController = {
      pause: () => videoRef.current?.pause(),
      play: () => videoRef.current?.play(),
      expand: () => {
        document.body.classList.add("videoplay-expanded");
        videoRef.current?.requestFullscreen?.();
      },
      replay: () => {
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play();
        }
      },
      isPaused: () => videoRef.current?.paused ?? true,
    };

    window.currentCommandsAudio = {
      SKIP_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Got it! Skipping the video.");
        handleContinueToTest2();
      },
      REPLAY_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        window.videoController?.replay();
      },
      PAUSE_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        if (window.videoController?.isPaused()) {
          VoiceService?.doSay("The video is already paused.");
        } else {
          VoiceService?.doSay("Sure! I'll pause the video...");
          window._lastPauseTime = Date.now();
          window.videoController?.pause();
        }
      },
      RESUME_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Sure! I'll resume the video...");
        window.videoController?.play();
      },
      EXPAND_VIDEO: () => {
        if (window.exitHandlerOpen) return;
        VoiceService?.doSay("Sure! I'll expand the video...");
        window.videoController?.expand();
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        handleContinueToTest2();
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command") handleContinueToTest2();
      },
    };

    return () => {
      window.videoController = null;
      window.clickedOnceOpen = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) return null;

  return (
    <div className={`${styles.inlineUi} ${styles.demovideoui}`}>
      <div className={styles.videoContainer}>
        <video
          ref={videoRef}
          src={introVideoUrl}
          controls
          playsInline
          preload="metadata"
          onEnded={() => {
            if (!locked) handleContinueToTest2();
          }}
        />
      </div>

      <div className={styles.videoActions}>
        <button className={`${styles.videoBtn} ${styles.videoBtnPrimary}`} onClick={handleContinueToTest2} disabled={locked}>
          Start Movement Test
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 16: MOVEMENT TEST 2 (AR)
// Placeholder screen — AR/Flutter takes over entirely.
// Confirmatory test — can be skipped.
// Same pattern as Movement Test 1.
// ════════════════════════════════════════════════════════════════

function MovementTest2Screen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const handleARComplete = useCallback(async () => {
    // Re-enable Myra
    window.mayraCode?.setIsVisible(true);
    window.mayraCode?.enableCallback?.();

    // Push completion message with exercise name
    const completedTestName = CONST.NEGATIVE_TEST_MAP[assessmentData.probableSubtype]?.name || "the movement";
    await showMyraMessage(MYRA_MESSAGES.movementTest?.completionSummary ? resolveMessage(MYRA_MESSAGES.movementTest.completionSummary, { testName: completedTestName }) : `Great job — you completed the ${completedTestName}. Let me ask you a couple of follow-up questions.`, 800);

    onContinue({
      rom_test2_completed: true,
    });
  }, [onContinue, assessmentData.probableSubtype, showMyraMessage]);

  // Push voice indicator on mount
  useEffect(() => {
    pushToHistory({ type: "voice-indicator", id: nextId() });
  }, []);

  useEffect(() => {
    // Clear commands and hide Myra during AR
    window.currentCommandsAudio = {};
    window.mayraCode?.disableCallback();
    window.mayraCode?.setIsVisible(false);

    // AR completion handler
    window.doNextLocation = {
      push: () => {
        handleARComplete();
      },
    };

    return () => {
      window.doNextLocation = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const testTwoVideoContainer = document.getElementById("testtwovideo");
    if (testTwoVideoContainer) testTwoVideoContainer.innerHTML = "";

    localStorage.setItem("runStep", "2");

    // Modified on Aug 7th - for user tracking

    document.body.classList.remove("videoplay-expanded");

    if (window.mayraCode && window.mayraCode.setIsVisible) {
      //window.mayraCode.setIsVisible(false);
    }

    if (window.mayraCode) {
      window.mayraCode.disableCallback();
    }
    window.currentCommandsAudio = {};

    console.log("Level 2 - 1");
    document.getElementById("OnDemandPlayingRoot").style.display = "block";
    document.querySelector("html").classList = [];
    document.getElementById("assesmentDiv").style.display = "none";

    if (document.querySelector("#assesmentDiv video")) {
      document.querySelector("#assesmentDiv video").play();
      setTimeout(() => {
        document.querySelector("#assesmentDiv video").pause();
      }, 50);
    }

    console.log("Level 2 - 2");

    window.blockNextSelfCall = true;
    setTimeout(() => {
      window.blockNextSelfCall = false;
    }, 5000);
    if (window.nextActivityStart) {
      window.nextActivityStart();
    }
  }, []);

  return (
    <div className={styles.inlineUi}>
      <p style={{ fontWeight: 600, marginBottom: 8, textAlign: "center" }}>Movement Test 2 in Progress</p>
      <p style={{ color: "#6b7280", fontSize: "14px", textAlign: "center" }}>Follow Myra's voice instructions to complete the movement.</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 17: FEEDBACK 2 (Symptom Response 2)
// 2 option cards: BETTER / WORSE.
// Uses flow_symptom / symptom2.
// Normalizes SELECT_SYMPTOM values.
// No AIProcessingLoader.
// ════════════════════════════════════════════════════════════════

function Feedback2Screen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [feedback2Response, setFeedback2Response] = useState(null);
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const lockedRef = useRef(false);
  const feedback2ResponseRef = useRef(null);
  const isVoiceMode = MyraInteraction.getMode() === "voice";
  const ContainerRef = useRef(null);
  if (window?.mayraCode) {
    window.mayraCode?.setIsVisible(false);
  }
  useEffect(() => {
    const test2Skipped = window.test2WasSkipped;
    if (test2Skipped) {
      VoiceService.stopAllVoiceActivity();
      VoiceService.stopCompletely();
      console.log("[Feedback2] Test 2 was skipped - auto-advancing to Personalize");

      // Clear the flag
      window.test2WasSkipped = false;
      VoiceService._currentFlow = "flow_personalized";
      window.currentFlow = "flow_personalized";

      const { rom, dur } = parseDataAfterInfo();
      onContinue(
        {
          symptomResponseConfirm: "WORSENED",
          rom_test2_completed: false,
          test2Skipped: true,
        },
        "personalize",
      );
    }
  }, [assessmentData, onContinue]);
  const test2 = CONST.getNegativeTest(assessmentData.probableSubtype, assessmentData.problemArea)?.name || "the movement";
  useEffect(() => {
    if (ready) {
      if (isVoiceMode) {
        pushToHistory({
          type: "voice-indicator",
          text: `Great job — you completed the ${test2}. Let me ask you a couple of follow-up questions.`,
          id: nextId(),
        });
      }
      window.currnetScreen = "";
    }
  }, [pushToHistory, test2, ready]);

  // ── Push divider + Myra message on mount ──
  useEffect(() => {
    let cancelled = false;
    async function init() {
      const msg = MYRA_MESSAGES.feedback2?.prompt || "Compared to the first movement, how did this one make your symptoms feel?";
      if (!window?.test2AnnounceSkipped) {
        await showMyraMessage(msg, 800);
      }
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleContinue = useCallback(() => {
    if (!feedback2ResponseRef.current || lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);

    const { rom, dur } = parseDataAfterInfo();
    onContinue({
      symptomResponseConfirm: feedback2ResponseRef.current,
      rom2: rom,
      duration2: dur,
      rom_test2_completed: true,
    });
  }, [onContinue]);

  const handleSelect = useCallback(
    async (id) => {
      if (lockedRef.current) return;
      lockedRef.current = true;
      setLocked(true);

      const opt = CONST.FEEDBACK2_OPTIONS.find((o) => o.id === id);
      setFeedback2Response(opt.value);
      feedback2ResponseRef.current = opt.value;

      // Push frozen widget + user echo
      pushToHistory({ type: "frozen-widget", widgetType: "feedback2", id: nextId(), data: { selectedValue: opt.value } }, { type: "user", text: opt.label, id: nextId() });

      const responseText = MYRA_MESSAGES.feedback2?.transitionToPersonalize || "Great — that's really helpful. We're almost done.";
      if (isVoiceMode && window.mayraCode) {
        window.mayraCode.doSay(responseText);
      }
      await showMyraMessage(responseText, 700);

      const { rom, dur } = parseDataAfterInfo();
      onContinue({
        symptomResponseConfirm: opt.value,
        rom2: rom,
        duration2: dur,
        rom_test2_completed: true,
      });
    },
    [onContinue, showMyraMessage],
  );

  // useScreenVoice for boilerplate only — flow handled manually
  useScreenVoice({
    screenName: "symptom2",
    flowName: "flow_symptom",
    payloadKey: "symptom2",
    exitReason: "feedback2_completed",
    skipFlow: false,
    commands: {
      SELECT_SYMPTOM: (value) => {
        if (window.exitHandlerOpen) return;
        if (lockedRef.current) return;
        // Normalize: voice may send WORSENED/TOLERABLE/IMPROVED from shared command
        const normalized = value === "WORSENED" || value === "WORSE" ? "WORSE" : "BETTER";
        const opt = CONST.FEEDBACK2_OPTIONS.find((o) => o.value === normalized);
        if (opt) {
          handleSelect(opt.id);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command" && feedback2ResponseRef.current) {
          const { rom, dur } = parseDataAfterInfo();
          onContinue({
            symptomResponseConfirm: feedback2ResponseRef.current,
            rom2: rom,
            duration2: dur,
            rom_test2_completed: true,
          });
        }
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (feedback2ResponseRef.current) {
          const { rom, dur } = parseDataAfterInfo();
          onContinue({
            symptomResponseConfirm: feedback2ResponseRef.current,
            rom2: rom,
            duration2: dur,
            rom_test2_completed: true,
          });
        } else {
          if (isVoiceMode) {
            VoiceService?.doSay("Please select how this movement compared to the first.");
          }
        }
      },
    },
  });

  // Manual flow start + command registration
  useEffect(() => {
    const mayraCode = window.mayraCode;
    if (!mayraCode) return;

    window.mayraForwardCode = {
      midjourneyData: generatePayloadForScreen("symptom2", assessmentData),
    };
    if (isVoiceMode) {
      mayraCode.startFlow("flow_symptom", "symptom2");
    } else {
      VoiceService?.startFlowWithoutAnnouncement?.("flow_symptom");
    }

    window.currentCommandsAudio = {
      SELECT_SYMPTOM: (value) => {
        if (window.exitHandlerOpen) return;
        if (lockedRef.current) return;
        // Normalize: voice may send WORSENED/TOLERABLE/IMPROVED from shared command
        const normalized = value === "WORSENED" || value === "WORSE" ? "WORSE" : "BETTER";
        const opt = CONST.FEEDBACK2_OPTIONS.find((o) => o.value === normalized);
        if (opt) {
          handleSelect(opt.id);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command" && feedback2ResponseRef.current) {
          const { rom, dur } = parseDataAfterInfo();
          onContinue({
            symptomResponseConfirm: feedback2ResponseRef.current,
            rom2: rom,
            duration2: dur,
            rom_test2_completed: true,
          });
        }
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        if (feedback2ResponseRef.current) {
          const { rom, dur } = parseDataAfterInfo();
          onContinue({
            symptomResponseConfirm: feedback2ResponseRef.current,
            rom2: rom,
            duration2: dur,
            rom_test2_completed: true,
          });
        } else {
          if (isVoiceMode) {
            VoiceService?.doSay("Please select how this movement compared to the first.");
          }
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (ready && ContainerRef.current) {
      setTimeout(() => {
        ContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [ready]);

  if (!ready) return null;

  return (
    <div className={styles.inlineUi} ref={ContainerRef}>
      {!locked ? (
        <div className={styles.feedback2Options}>
          {CONST.FEEDBACK2_OPTIONS.map((o) => (
            <div key={o.id} className={`${styles.feedback2Btn} ${feedback2Response === o.value ? styles.selected : ""} ${locked ? styles.locked : ""}`} onClick={() => handleSelect(o.id)}>
              {o.label}
            </div>
          ))}
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════
// HARD STOP SCREEN
// Safety termination — hides Myra, shows empathy + recommendation.
// Triggered by: safety symptoms, Test 1 pain, "couldn't finish".
// ════════════════════════════════════════════════════════════════

function HardStopScreen({ source, pushToHistory, showMyraMessage, scrollToBottom }) {
  const [rendered, setRendered] = useState(false);
  const isVoiceMode = MyraInteraction.getMode() === "voice";

  useEffect(() => {
    window.mayraCode?.setIsVisible(false);
    window.mayraCode?.forceStop();

    // Speak empathy message
    const empathyMap = {
      safety: MYRA_MESSAGES.hardStop?.safety?.empathy || "Your safety is my top priority.",
      test1_pain: MYRA_MESSAGES.hardStop?.test1Pain?.empathy || "I'm glad you told me about the pain.",
      couldnt_finish: MYRA_MESSAGES.hardStop?.couldntFinish?.empathy || "No worries at all — that's important information.",
    };
    const speech = empathyMap[source] || empathyMap.safety;
    if (isVoiceMode) {
      VoiceService?.doSay(speech);
    }

    // Screen tracking
    MyraInteraction?.onScreenEnter?.("hard_stop");
    window.screenTracker?.enterScreen("hard_stop");

    // Push conversation entries
    let cancelled = false;
    async function init() {
      await showMyraMessage(speech, 900);
      if (cancelled) return;

      const detailMap = {
        safety: MYRA_MESSAGES.hardStop?.safety?.detail || "Based on what you've shared, I'd strongly recommend seeing a healthcare provider before starting an exercise program. They can evaluate your symptoms and let you know when it's safe to proceed.",
        test1_pain: MYRA_MESSAGES.hardStop?.test1Pain?.detail || "Experiencing pain during that movement tells me we should take a different approach. I'd recommend seeing a healthcare provider who can evaluate things in person.",
        couldnt_finish: MYRA_MESSAGES.hardStop?.couldntFinish?.detail || "Not being able to complete the movement tells me this might need a more hands-on approach. I'd recommend seeing a healthcare provider who can work with you directly.",
      };
      const detail = detailMap[source] || detailMap.safety;

      // Push hard stop card as frozen widget
      pushToHistory({ type: "frozen-widget", widgetType: "hard_stop_card", id: nextId(), data: { detail } });

      await _delay(600);
      if (cancelled) return;

      const followUp = MYRA_MESSAGES.hardStop?.followUp || "I know this isn't the outcome you were hoping for. If you have any questions about what this means or what to expect from an in-person visit, I'm here — just ask.";
      await showMyraMessage(followUp, 800);
      if (!cancelled) setRendered(true);
    }
    init();

    return () => {
      cancelled = true;
      window.screenTracker?.exitScreen("hard_stop", { exitReason: "hard_stop_viewed" });
    };
  }, [source]);

  // No live content needed — everything is in conversation history
  // Input bar stays active so the user can chat with Myra
  return null;
}

// ════════════════════════════════════════════════════════════════
// SCREEN 18: PERSONALIZE
// Grouped form fields (basics, capacity, preferences).
// Group-by-group with Skip/Next per group. Fully skippable.
// Mode-specific flow routing.
// ════════════════════════════════════════════════════════════════

function PersonalizeScreen({ assessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [groupIndex, setGroupIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [groupsCompleted, setGroupsCompleted] = useState(0);
  const [ready, setReady] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [groupLocked, setGroupLocked] = useState(false);
  const flowStartedRef = useRef(false);
  const initRef = useRef(false);
  const ContainerRef = useRef(null);
  const isVoiceMode = MyraInteraction.getMode() === "voice";
  window.test2AnnounceSkipped = false;

  // Close AR camera stream if still open
  useEffect(() => {
    window.closeStream?.();
  }, []);

  // useScreenVoice for boilerplate — flow handled manually
  // exitReason handled manually in finishPersonalize (dynamic based on skip vs complete)
  useScreenVoice({
    screenName: "personalize",
    flowName: "flow_personalized",
    payloadKey: "personalize",
    skipFlow: !isVoiceMode,
    commands: {
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        submitCurrentGroup();
      },
      FORM_QUESTION: () => {
        if (window.exitHandlerOpen) return;
        const missing = getMissingFields();
        if (isVoiceMode) {
          VoiceService?.doSay(`Please enter ${missing}.`);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;

        // Handle continue command
        if (intent === "continue_command" || value === "continue_command") {
          submitCurrentGroup();
          return;
        }

        // Handle extracted data
        if (extracted && Object.keys(extracted).length > 0) {
          console.log("[VOICE] Updating form with extracted data:", extracted);

          // Update formData state (not demographics - this screen uses formData)
          setFormData((prev) => {
            const updates = {};
            const fieldMappings = {
              age: "age",
              weightLbs: "weight",
              heightFt: "heightFt",
              heightIn: "heightIn",
              activityLevel: "activity",
              floorCapability: "floorCapability",
              balance: "balance",
              scheduleDays: "scheduleDays",
              sessionPref: "sessionPref",
              sessionLength: "scheduleTime",
              preExistingConditions: "preExistingConditions",
              currentConditions: "currentConditions",
            };

            Object.entries(fieldMappings).forEach(([extractedKey, formKey]) => {
              if (extracted[extractedKey] !== undefined && extracted[extractedKey] !== null) {
                updates[formKey] = extracted[extractedKey];
                console.log(`[VOICE] Updating ${formKey} to:`, extracted[extractedKey]);
              }
            });

            return { ...prev, ...updates };
          });

          // Handle goals - convert from string values to the expected object format
          if (extracted.goals) {
            const goalTextMap = {
              reduce_pain: "Reduce Pain",
              less_stiff: "Reduce Stiffness",
              improve_strength: "Improve Strength",
              improve_flexibility: "Improve Flexibility",
              daily_tasks: "Make Daily Tasks Easier",
              balance: "Improve Balance & Stability",
              picking_up: "Picking Up Things",
              sleep: "Improve Sleep Quality",
              walking: "Improve Walking & Endurance",
              lifting: "Make Bending & Lifting Easier",
              posture: "Improve Sitting & Standing Posture",
              rotational_sports: "Return to Sports (Golf, Tennis, etc.)",
              overhead_sports: "Return to Sports (Basketball, Swimming, etc.)",
              running: "Running, Cycling, etc.",
              fitness: "Fitness: Gym, CrossFit, etc.",
            };

            const goalsArray = Array.isArray(extracted.goals) ? extracted.goals : [extracted.goals];
            const formattedGoals = goalsArray
              .filter((goalValue) => goalTextMap[goalValue])
              .map((goalValue) => ({
                value: goalValue,
                text: goalTextMap[goalValue],
              }));

            if (formattedGoals.length > 0) {
              setSelectedGoals(formattedGoals);
              // Also mark goals as filled in formData
              setFormData((prev) => ({ ...prev, goals: true }));
              console.log("[VOICE] Updating goals to:", formattedGoals);
            }
          }
        }

        // Handle condition disclosure
        if (intent === "condition_disclosure") {
          const conditions = JSON.parse(localStorage.getItem("userCondition") || "{}");
          localStorage.setItem("userCondition", JSON.stringify(conditions));
        }
      },
    },
  });

  // Mode-specific flow start
  useEffect(() => {
    if (flowStartedRef.current) return;
    flowStartedRef.current = true;

    const mayraCode = window.mayraCode;
    if (!mayraCode) return;

    const mode = MyraInteraction?.getCurrentMode?.();
    const payload = generatePayloadForScreen("personalize", assessmentData);

    if (mode === "voice") {
      window.mayraForwardCode = { midjourneyData: payload };
      mayraCode.startFlow("flow_personalized", "personalized");
    } else if (mode === "chat") {
      const chatAnnouncement = "Let's personalize your plan. I'll need a few details about you.";
      VoiceService?.startFlowWithoutAnnouncement?.("flow_personalized");
      MyraInteraction?.onMyraMessage?.(chatAnnouncement);
    } else {
      const buttonAnnouncement = "Let's personalize your plan. I'll need a few details about you.";
      VoiceService?.startFlowWithoutAnnouncement?.("flow_personalized");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Command registration
  useEffect(() => {
    window.currentCommandsAudio = {
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        submitCurrentGroup();
      },
      FORM_QUESTION: () => {
        if (window.exitHandlerOpen) return;
        const missing = getMissingFields();
        if (isVoiceMode) {
          VoiceService?.doSay(`Please enter ${missing}.`);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;

        // Handle continue command
        if (intent === "continue_command" || value === "continue_command") {
          submitCurrentGroup();
          return;
        }

        // Handle extracted data
        if (extracted && Object.keys(extracted).length > 0) {
          console.log("[VOICE] Updating form with extracted data:", extracted);

          // Update formData state (not demographics - this screen uses formData)
          setFormData((prev) => {
            const updates = {};
            const fieldMappings = {
              age: "age",
              weightLbs: "weight",
              heightFt: "heightFt",
              heightIn: "heightIn",
              activityLevel: "activity",
              floorCapability: "floorCapability",
              balance: "balance",
              scheduleDays: "scheduleDays",
              sessionPref: "sessionPref",
              sessionLength: "scheduleTime",
              preExistingConditions: "preExistingConditions",
              currentConditions: "currentConditions",
            };

            Object.entries(fieldMappings).forEach(([extractedKey, formKey]) => {
              if (extracted[extractedKey] !== undefined && extracted[extractedKey] !== null) {
                updates[formKey] = extracted[extractedKey];
                console.log(`[VOICE] Updating ${formKey} to:`, extracted[extractedKey]);
              }
            });

            return { ...prev, ...updates };
          });

          // Handle goals - convert from string values to the expected object format
          if (extracted.goals) {
            const goalTextMap = {
              reduce_pain: "Reduce Pain",
              less_stiff: "Reduce Stiffness",
              improve_strength: "Improve Strength",
              improve_flexibility: "Improve Flexibility",
              daily_tasks: "Make Daily Tasks Easier",
              balance: "Improve Balance & Stability",
              picking_up: "Picking Up Things",
              sleep: "Improve Sleep Quality",
              walking: "Improve Walking & Endurance",
              lifting: "Make Bending & Lifting Easier",
              posture: "Improve Sitting & Standing Posture",
              rotational_sports: "Return to Sports (Golf, Tennis, etc.)",
              overhead_sports: "Return to Sports (Basketball, Swimming, etc.)",
              running: "Running, Cycling, etc.",
              fitness: "Fitness: Gym, CrossFit, etc.",
            };

            const goalsArray = Array.isArray(extracted.goals) ? extracted.goals : [extracted.goals];
            const formattedGoals = goalsArray
              .filter((goalValue) => goalTextMap[goalValue])
              .map((goalValue) => ({
                value: goalValue,
                text: goalTextMap[goalValue],
              }));

            if (formattedGoals.length > 0) {
              setSelectedGoals(formattedGoals);
              // Also mark goals as filled in formData
              setFormData((prev) => ({ ...prev, goals: true }));
              console.log("[VOICE] Updating goals to:", formattedGoals);
            }
          }
        }

        // Handle condition disclosure
        if (intent === "condition_disclosure") {
          const conditions = JSON.parse(localStorage.getItem("userCondition") || "{}");
          localStorage.setItem("userCondition", JSON.stringify(conditions));
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIndex, formData]);

  // ── Push divider + first group Myra message on mount ──
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    let cancelled = false;
    async function init() {
      const group = CONST.PERSONALIZE_GROUPS[0];
      if (group) {
        await showMyraMessage(group.myraIntro, 900);
      }
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (ready && ContainerRef.current) {
      setTimeout(() => {
        ContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [ready]);

  function getMissingFields() {
    const group = CONST.PERSONALIZE_GROUPS[groupIndex];
    if (!group) return "the remaining fields";
    const missing = group.fields.filter((f) => !formData[f.id]).map((f) => f.label);
    return missing.length > 0 ? missing.join(", ") : "the remaining fields";
  }

  async function submitCurrentGroup() {
    if (groupLocked) return;
    setGroupLocked(true);

    const group = CONST.PERSONALIZE_GROUPS[groupIndex];
    const filled = group ? group.fields.filter((f) => formData[f.id]).length : 0;
    const total = group ? group.fields.length : 0;

    // if (filled === 0) {
    //   skipCurrentGroup();
    //   return;
    // }

    // Push frozen widget + user echo
    pushToHistory({ type: "frozen-widget", widgetType: "personalize_group", id: nextId(), data: { groupId: group?.id, filled, total, result: "submitted", formData: { ...formData }, selectedGoals: [...selectedGoals] } }, { type: "user", text: `${filled} of ${total} fields provided`, id: nextId() });
    setGroupsCompleted((prev) => prev + 1);

    const nextIdx = groupIndex + 1;
    if (nextIdx < CONST.PERSONALIZE_GROUPS.length) {
      const nextGroup = CONST.PERSONALIZE_GROUPS[nextIdx];
      await showMyraMessage(nextGroup.myraIntro, 600);
      setGroupIndex(nextIdx);
      setGroupLocked(false);
    } else {
      await finishPersonalize(false);
    }
  }

  async function skipCurrentGroup() {
    if (groupLocked) return;
    setGroupLocked(true);

    const group = CONST.PERSONALIZE_GROUPS[groupIndex];
    const skipMsg = CONST.PERSONALIZE_SKIP_MESSAGES[group?.id];

    // Push frozen widget + user echo
    pushToHistory({ type: "frozen-widget", widgetType: "personalize_group", id: nextId(), data: { groupId: group?.id, filled: 0, total: group?.fields?.length || 0, result: "skipped", formData: { ...formData }, selectedGoals: [...selectedGoals] } }, { type: "user", text: "Skip", id: nextId() });

    if (isVoiceMode) {
      VoiceService?.doSay(skipMsg);
    }
    await showMyraMessage(skipMsg, 700);

    const nextIdx = groupIndex + 1;
    if (nextIdx < CONST.PERSONALIZE_GROUPS.length) {
      // Show quick replies: Continue / Skip remaining
      setShowQuickReplies(true);
    } else {
      await finishPersonalize(true);
    }
  }

  async function handleQuickReply(reply) {
    setShowQuickReplies(false);
    pushToHistory({ type: "user", text: reply, id: nextId() });

    if (reply === "Skip remaining") {
      await showMyraMessage("No problem — I'll use conservative settings across the board. You can update these anytime.", 600);
      await finishPersonalize(true);
    } else {
      // "Continue" → advance to next group
      const nextIdx = groupIndex + 1;
      const nextGroup = CONST.PERSONALIZE_GROUPS[nextIdx];
      await showMyraMessage(nextGroup.myraIntro, 600);
      setGroupIndex(nextIdx);
      setGroupLocked(false);
    }
  }

  async function handleSkipAll() {
    if (groupLocked) return;
    setGroupLocked(true);
    const group = CONST.PERSONALIZE_GROUPS[groupIndex];
    pushToHistory({ type: "frozen-widget", widgetType: "personalize_group", id: nextId(), data: { groupId: group?.id, filled: 0, total: group?.fields?.length || 0, result: "skipped", formData: { ...formData }, selectedGoals: [...selectedGoals] } }, { type: "user", text: "Skip All", id: nextId() });
    await finishPersonalize(true);
  }

  async function finishPersonalize(skipped) {
    const mode = MyraInteraction?.getMode?.();
    const transitionMsg = MYRA_MESSAGES.personalize?.finishTransition || "Alright — I'm calculating your Recovery Compass Score now.";
    if (isVoiceMode) {
      VoiceService?.doSay(transitionMsg);
    }
    await showMyraMessage(transitionMsg, 800);

    if (skipped && !formData.age) {
      window.screenTracker?.exitScreen("personalize", { exitReason: "conservative_defaults_accepted" });
      onContinue({
        useStandardPlan: true,
        personal_info_skipped: true,
        demographics_provided: false,
      });
    } else {
      window.screenTracker?.exitScreen("personalize", { exitReason: "demographics_completed" });
      onContinue({
        biometrics: {
          age: formData.age || null,
          weight: formData.weight || null,
          height: formData.heightFt && formData.heightIn ? `${formData.heightFt}'${formData.heightIn}"` : null,
          gender: formData.gender || null,
          activityLevel: formData.activity || null,
          floorCapability: formData.floorCapability || null,
          balance: formData.balance || null,
        },
        signature: new Date().toISOString(),
        demographics_provided: true,
        scheduleDays: formData.scheduleDays || null,
        scheduleTime: formData.scheduleTime || null,
        sessionPref: formData.sessionPref || null,
        goals: selectedGoals.length > 0 ? selectedGoals : null,
      });
    }
  }

  function handleFieldChange(fieldId, value) {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  }

  function toggleGoal(goalValue) {
    setSelectedGoals((prev) => {
      if (prev.includes(goalValue)) return prev.filter((g) => g !== goalValue);
      return [...prev, goalValue];
    });
    setFormData((prev) => ({ ...prev, goals: true }));
  }

  if (!ready) return null;

  // If quick replies are showing, render those instead of the group form
  if (showQuickReplies) {
    return (
      <div className={styles.quickReplies}>
        <button className={styles.quickReply} onClick={() => handleQuickReply("Continue")}>
          Continue
        </button>
        <button className={styles.quickReply} onClick={() => handleQuickReply("Skip remaining")}>
          Skip remaining
        </button>
      </div>
    );
  }

  const group = CONST.PERSONALIZE_GROUPS[groupIndex];
  if (!group) return null;

  const numberFields = group.fields.filter((f) => f.type === "number");
  const otherFields = group.fields.filter((f) => f.type !== "number");

  return (
    <>
      <div className={styles.inlineUi} ref={ContainerRef}>
        {!groupLocked ? (
          <div className={styles.pzGroup}>
            {/* Number fields in pairs */}
            {numberFields.length > 0 &&
              (() => {
                const rows = [];
                for (let i = 0; i < numberFields.length; i += 2) {
                  rows.push(
                    <div className={styles.pzFieldRow} key={numberFields[i].id}>
                      <div className={styles.pzField}>
                        <label className={styles.formLabel}>{numberFields[i].label}</label>
                        <input type="number" className={styles.formInput} placeholder={numberFields[i].placeholder} min={numberFields[i].min || ""} max={numberFields[i].max || ""} value={formData[numberFields[i].id] || ""} onChange={(e) => handleFieldChange(numberFields[i].id, e.target.value)} />
                      </div>
                      {i + 1 < numberFields.length && (
                        <div className={styles.pzField}>
                          <label className={styles.formLabel}>{numberFields[i + 1].label}</label>
                          <input type="number" className={styles.formInput} placeholder={numberFields[i + 1].placeholder} min={numberFields[i + 1].min || ""} max={numberFields[i + 1].max || ""} value={formData[numberFields[i + 1].id] || ""} onChange={(e) => handleFieldChange(numberFields[i + 1].id, e.target.value)} />
                        </div>
                      )}
                    </div>,
                  );
                }
                return rows;
              })()}

            {/* Option and goal fields */}
            {otherFields.map((field) => {
              if (field.type === "goals") {
                return (
                  <div className={styles.pzField} key={field.id}>
                    <label className={styles.formLabel}>{field.label}</label>
                    <div className={styles.pzGoalWrapper}>
                      <div className={styles.pzGoalRow}>
                        {field.options.map((opt) => (
                          <div key={opt.id} className={`${styles.pzGoalItem} ${selectedGoals.includes(opt.value) ? styles.selected : ""}`} onClick={() => toggleGoal(opt.value)}>
                            <span className={styles.pzGoalLabel}>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              if (field.type === "options") {
                return (
                  <div className={styles.pzField} key={field.id}>
                    <label className={styles.formLabel}>{field.label}</label>
                    <div className={styles.pzOptionRow}>
                      {field.options.map((opt) => (
                        <button key={opt.id} className={`${styles.pzOptBtn} ${formData[field.id] === opt.value ? styles.selected : ""}`} onClick={() => handleFieldChange(field.id, opt.value)}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })}

            <div className={styles.pzActions}>
              <button className={`${styles.videoBtn} ${styles.videoBtnSecondary}`} onClick={groupIndex === 0 ? handleSkipAll : () => skipCurrentGroup()}>
                {groupIndex === 0 ? "Skip All" : "Skip"}
              </button>
              <button className={`${styles.videoBtn} ${styles.videoBtnPrimary}`} onClick={() => submitCurrentGroup()}>
                {groupIndex < CONST.PERSONALIZE_GROUPS.length - 1 ? "Next" : "Finish"}
              </button>
            </div>
          </div>
        ) : (
          <div></div>
        )}

        <p className={styles.disclaimer} style={{ marginTop: 8 }}>
          Step {groupIndex + 1} of {CONST.PERSONALIZE_GROUPS.length}
        </p>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 19: RECOVERY SCORE
// Animated ring display. Gates voice flow on loading completion.
// Backend API call stubbed with getDummyPlan().
// ════════════════════════════════════════════════════════════════
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
async function fetchWithRetry(url, options = {}, opts = {}) {
  const { maxAttempts = 4, initialDelay = 800, factor = 2, jitter = true, timeout = 20000, signal } = opts;

  let attempt = 0;

  while (attempt < maxAttempts) {
    attempt += 1;

    // per-attempt timeout
    const controller = new AbortController();
    const combinedSignal = signal
      ? new AbortController().signal // placeholder, we'll combine below
      : controller.signal;

    // Combine signals: prefer outer signal (if provided), but ensure we can timeout.
    // If a signal was passed, we listen to it and abort our controller when it fires.
    if (signal) {
      if (signal.aborted) {
        controller.abort();
      } else {
        const onAbort = () => controller.abort();
        signal.addEventListener("abort", onAbort, { once: true });
      }
    }

    let timeoutId;
    try {
      if (timeout) {
        timeoutId = setTimeout(() => controller.abort(), timeout);
      }

      const res = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      // if server error (5xx) treat as retryable; if 4xx treat as permanent failure
      if (res.ok) {
        clearTimeout(timeoutId);
        return { ok: true, response: await res.json(), attempt };
      } else if (res.status >= 500 && res.status < 600) {
        // retryable server error
        const errText = await res.text().catch(() => "");
        throw new Error(`Server error ${res.status}: ${errText}`);
      } else {
        // client error - do not retry
        const errText = await res.text().catch(() => "");
        return { ok: false, permanentError: true, status: res.status, body: errText };
      }
    } catch (err) {
      clearTimeout(timeoutId);

      // If aborted externally, rethrow so caller can handle immediate abort
      if (signal && signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }

      // If this was the last attempt, return failure info
      if (attempt >= maxAttempts) {
        return { ok: false, error: err, attempt };
      }

      // compute backoff delay (exponential + jitter)
      let delay = initialDelay * Math.pow(factor, attempt - 1);
      if (jitter) {
        const jitterVal = Math.random() * (delay * 0.25); // up to 25% jitter
        delay = delay - jitterVal;
      }

      // wait and retry
      await sleep(delay);
      // then continue loop for next attempt
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // fallback - should never reach here because loop returns
  return { ok: false, error: new Error("Retries exhausted") };
}

function RecoveryScoreScreen({ assessmentData, updateAssessmentData, pushToHistory, showMyraMessage, scrollToBottom, onContinue }) {
  const [loadingWait, setLoadingWait] = useState(true);
  const [scoreData, setScoreData] = useState(null);
  const flowStartedRef = useRef(false);
  const initRef = useRef(false);
  const userCondition = JSON.parse(localStorage.getItem("userCondition") || "{}");
  const userConditions = userCondition?.usercondition || {};
  const problemArea = assessmentData.problemArea || "LBP";
  const isLBP = problemArea === "LBP";
  const URL = CONST.SCORE_API_URL;
  const ContainerRef = useRef(null);
  const isVoiceMode = MyraInteraction.getMode() === "voice";
  const transformedRequest = {
    painLocations: assessmentData?.selectedZones || [],
    cpgClassification: assessmentData?.cpgForBackend,
    ...(assessmentData?.subtypeQuestions && assessmentData?.selfAssessmentAnswer
      ? {
          subjectiveResponses: {
            q1: assessmentData?.subtypeQuestions?.value || "",
            q2a: assessmentData?.selfAssessmentAnswer || "",
          },
        }
      : {}),
    symptomDuration: assessmentData?.symptomDuration || "",
    analyticsId: assessmentData?.analyticsId || window.trSessionId || localStorage.getItem("trSessionId") || null,
    preTestNPRS: assessmentData?.painLevel || 0,
    probableSubtype: assessmentData?.probableSubtype || "",
    diagnosisChanged: assessmentData?.diagnosisChanged || false,
    degradation: assessmentData?.degradation ?? null,
    timing: assessmentData?.timing ?? null,
    signature: assessmentData?.signature || false,
    current_conditions: assessmentData?.biometrics?.currentConditions || userConditions?.current || "",
    pre_existing_conditions: assessmentData?.biometrics?.preExistingConditions || userConditions?.pre_existing || "",
    input_mode: MyraInteraction?.getMode?.() || "chat",

    romTest1: {
      testName: assessmentData?.testName || "",
      symptomaticResponse: assessmentData?.symptomResponse?.value || "",
      rpe: assessmentData?.rpe || 0,
      movementQuality: assessmentData?.romQuality || "",
      cvData: {
        rom: assessmentData?.rom || [],
        duration: assessmentData?.duration || [],
      },
    },
    ...(assessmentData?.testName2 && assessmentData?.symptomResponseConfirm
      ? {
          romTest2: {
            testName: assessmentData?.testName2,
            symptomaticResponse: assessmentData?.symptomResponseConfirm,
          },
        }
      : {}),

    biometrics: assessmentData?.biometrics?.skipped
      ? { skipped: true }
      : {
          skipped: false,
          age: assessmentData?.biometrics?.age || 60,
          activityLevel: assessmentData?.biometrics?.activityLevel,
          heightFt: assessmentData?.biometrics?.heightFt,
          heightIn: assessmentData?.biometrics?.heightIn,
          weightLbs: assessmentData?.biometrics?.weightLbs,
          floorCapability: assessmentData?.biometrics?.floorCapability,
          balance: assessmentData?.biometrics?.balance,
          goal: assessmentData?.biometrics?.goal || "",
          sessionPref: assessmentData?.biometrics?.sessionPref || "one",
          scheduleTime: assessmentData?.biometrics?.scheduleTime || "",
          scheduleDays: assessmentData?.biometrics?.scheduleDays || "",
          current_conditions: assessmentData?.biometrics?.currentConditions || userConditions?.current || "",
          pre_existing_conditions: assessmentData?.biometrics?.preExistingConditions || userConditions?.pre_existing || "",
          input_mode: MyraInteraction?.getMode?.() || "chat",
        },
  };

  // Backend calculation
  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
    }
    async function calculateScore() {
      if (AnalyticsService) AnalyticsService.startSession();
      try {
        const payload = JSON.stringify(transformedRequest);
        const result = await fetchWithRetry(URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
        });
        if (result.ok && result.response) {
          const apiResponse = result.response;

          // Extract data from the nested structure
          const userProfile = apiResponse.userProfile || {};
          const recoveryPlan = apiResponse.recoveryPlan || {};

          // Build the structured scoreData
          const structuredData = {
            // Score information
            finalScore: userProfile.recoveryScore || 0,
            pillar1: userProfile.pillar1 || 0,
            pillar2: userProfile.pillar2 || 0,
            pillar3: userProfile.pillar3 || 0,
            pillar4: userProfile.pillar4 || 0,
            rcsCalculated: userProfile.rcsCalculated || 0,
            totalAdjustment: userProfile.totalAdjustment || 0,
            adjustmentDetails: userProfile.adjustmentDetails || {},

            // User profile information
            level: userProfile.level || 1,
            finalPainSubtype: userProfile.finalPainSubtype || "",
            finalCpgClassification: userProfile.finalCpgClassification || "",
            planDetails: userProfile.planDetails || {},

            // Recovery plan
            recoveryPlan: recoveryPlan,
            planRationaleSpeech: recoveryPlan.rationale || "",
            estimatedTime: recoveryPlan.estimatedTime || "10-12 min",

            // Metadata
            aiLogId: `api-${Date.now()}`,
            aiLatencyMs: result.attempt ? Date.now() - performance.now() : 0,
            planGeneratedAt: new Date().toISOString(),
            exerciseCandidatesCount: recoveryPlan.exercises?.length || 0,
          };
          setScoreData(structuredData);
          updateAssessmentData({ ...assessmentData, ...structuredData });
          setLoadingWait(false);
        }
      } catch {
        const fallback = CONST.getDummyPlan(assessmentData.problemArea);
        setScoreData({
          finalScore: fallback.score,
          planDetails: fallback,
          rcsCalculated: true,
          planRationaleSpeech: "",
          aiLogId: `fallback-${Date.now()}`,
          aiLatencyMs: 0,
          planGeneratedAt: new Date().toISOString(),
          exerciseCandidatesCount: fallback.exercises.length,
        });
        setLoadingWait(false);
      }
    }
    calculateScore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useScreenVoice for boilerplate — flow handled manually
  useScreenVoice({
    screenName: "recovery_score",
    flowName: "flow_recovery_compass_score",
    payloadKey: "recovery_score",
    exitReason: "scored",
    commands: {
      READY_TO_PROCEED: (value) => {
        if (window.exitHandlerOpen) return;
        if (value === true || value === "true") {
          setTimeout(() => handleContinue(), 2000);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command") setTimeout(() => handleContinue(), 2000);
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        setTimeout(() => handleContinue(), 2000);
      },
    },
  });

  // Mode-specific flow start — only after loading completes
  useEffect(() => {
    if (loadingWait || flowStartedRef.current) return;
    flowStartedRef.current = true;

    const mayraCode = window.mayraCode;
    if (!mayraCode) return;

    const mergedData = { ...assessmentData, ...scoreData };
    const mode = MyraInteraction?.getMode?.();
    console.log("Mode is : ", mode);
    window.mayraForwardCode = { midjourneyData: generatePayloadForScreen("recovery_score", mergedData) };

    if (mode === "voice") {
      mayraCode.startFlow("flow_recovery_compass_score", "recovery_score");
    } else if (mode === "chat") {
      const chatAnnouncement = "Your recovery score is ready! Let me walk you through it.";
      VoiceService?.startFlowWithoutAnnouncement?.("flow_recovery_compass_score", "", "");
      MyraInteraction?.onMyraMessage?.(chatAnnouncement);
    } else {
      const buttonAnnouncement = "Your recovery score is ready! Let me walk you through it.";
      VoiceService?.startFlowWithoutAnnouncement?.("flow_recovery_compass_score", "", "");
    }

    if (AnalyticsService) {
      AnalyticsService.trackStep(mergedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingWait]);

  const handleContinue = useCallback(() => {
    if (!scoreData) return;
    const subtypeForFrozen = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
    const diagnosisForFrozen = CONST.MESSAGING.diagnosis[subtypeForFrozen] || CONST.MESSAGING.diagnosis.NEUTRAL_SENSITIVE;
    const scoreForFrozen = scoreData.finalScore || 0;
    pushToHistory({ type: "frozen-widget", widgetType: "rcs", id: nextId(), data: { score: scoreForFrozen, diagnosis: diagnosisForFrozen } }, { type: "user", text: "View My Week 1 Recovery Plan", id: nextId() });
    onContinue(scoreData);
  }, [scoreData, assessmentData, onContinue]);

  // Command registration — 2s delay on all continues
  useEffect(() => {
    window.currentCommandsAudio = {
      READY_TO_PROCEED: (value) => {
        if (window.exitHandlerOpen) return;
        if (value === true || value === "true") {
          setTimeout(() => handleContinue(), 2000);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command") setTimeout(() => handleContinue(), 2000);
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        setTimeout(() => handleContinue(), 2000);
      },
    };
  }, [handleContinue]);

  // Loading state — inline in chat area
  if (loadingWait || !scoreData) {
    return (
      <div className={styles.inlineUi}>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div className={styles.aiProcessingSpinner} />
          <p style={{ fontWeight: 600, marginTop: 16, color: "#1f2937" }}>Calculating Your Recovery Score</p>
          <p style={{ color: "#6b7280", fontSize: "14px", marginTop: 4 }}>This may take a moment…</p>
        </div>
      </div>
    );
  }

  const score = scoreData.finalScore || 0;
  const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
  const diagnosis = CONST.MESSAGING.diagnosis[subtype] || CONST.MESSAGING.diagnosis.NEUTRAL_SENSITIVE;
  const rm = scoreData.totalAdjustment || scoreData.planDetails?.readinessModifier || 85;
  let tier;
  if (rm >= 90) tier = CONST.MESSAGING.readinessTiers.READY_TO_GO;
  else if (rm >= 75) tier = CONST.MESSAGING.readinessTiers.CAUTIOUS_START;
  else if (rm >= 50) tier = CONST.MESSAGING.readinessTiers.GENTLE_APPROACH;
  else tier = CONST.MESSAGING.readinessTiers.STARTING_WITH_CARE;
  const circumference = 2 * Math.PI * 65;

  return (
    <div className={styles.inlineUi} ref={ContainerRef}>
      <div className={styles.rcsScreen}>
        <h2 className={styles.rcsTitle}>Recovery Compass</h2>
        <p className={styles.rcsSubtitle}>The Starting Point of Your Recovery Journey</p>

        <div className={styles.rcsScoreSection}>
          <span className={styles.rcsScoreLabel}>Your Score</span>
          <div className={styles.rcsRingContainer}>
            <svg viewBox="0 0 160 160" className={styles.rcsRingSvg}>
              <defs>
                <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#16a34a" />
                  <stop offset="100%" stopColor="#15803d" />
                </linearGradient>
              </defs>
              <circle cx="80" cy="80" r="65" fill="none" stroke="#e5e7eb" strokeWidth="8" />
              <circle cx="80" cy="80" r="65" fill="none" stroke="url(#scoreGrad)" strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference * (1 - score / 100)} transform="rotate(-90 80 80)" className={styles.rcsRingFill} />
            </svg>
            <span className={styles.rcsScoreNumber}>{score}</span>
          </div>
        </div>

        <p className={styles.rcsExplanation}>Your Recovery Compass Score starts at {score}, reflecting your current recovery stage. As you follow your personalized plan over the next 8 weeks, this score is designed to improve steadily, with a goal of reaching closer to 100.</p>

        <div className={styles.rcsDiagnosisCard}>
          <h3 className={styles.rcsDiagnosisHeadline}>{diagnosis.headline}</h3>
          <p className={styles.rcsDiagnosisRelief}>{diagnosis.relief}</p>
        </div>

        <div className={styles.rcsPersonalizationCard}>
          <p className={styles.rcsPersonalizationTitle}>How We've Personalized Your Plan</p>
          <div className={styles.rcsFactors}>
            <div className={styles.rcsFactor}>
              <span>📊</span>
              <span>Your Score</span>
            </div>
            <span className={styles.rcsPlus}>+</span>
            <div className={styles.rcsFactor}>
              <span>📋</span>
              <span>Your History</span>
            </div>
            <span className={styles.rcsPlus}>+</span>
            <div className={styles.rcsFactor}>
              <span>👤</span>
              <span>Your Info</span>
            </div>
          </div>
        </div>

        <button className={styles.btnSafe} onClick={handleContinue} style={{ marginTop: 12 }}>
          View My Week 1 Recovery Plan
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SCREEN 20: PLAN SCREEN
// Exercise plan display with QnA flow. Sign-up CTA.
// Fires dual gtag events, saveFullData, saveUserJourney.
// ════════════════════════════════════════════════════════════════

function PlanScreen({ assessmentData, qaRef, onOpenQnA, pushToHistory, showMyraMessage, scrollToBottom }) {
  const [showRationale, setShowRationale] = useState({});
  const [usedQaButtons, setUsedQaButtons] = useState({});
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [ready, setReady] = useState(false);
  const rationaleSpeech = assessmentData?.planRationaleSpeech;
  const [showQaPrompt, setShowQaPrompt] = useState(false);
  const [isTypingQaPrompt, setIsTypingQaPrompt] = useState(false);
  let userName = localStorage.getItem("self_name") || "";
  console.log("Rationale speech is : ", rationaleSpeech);
  let fullSpeech = userName ? `${userName} | ${rationaleSpeech}` : rationaleSpeech;

  // ── Data-driven Q&A buttons — extensible by adding items ──
  const PLAN_QA_ITEMS = useMemo(
    () => [
      { key: "why", label: "Why these exercises?", question: "Why these exercises?", answer: CONST.PLAN_QA_ANSWERS?.why || "Each exercise was chosen based on your pain pattern, movement results, and subtype classification. They target the specific areas that need strengthening or mobility work for your recovery." },
      { key: "often", label: "How often should I do this?", question: "How often should I do this?", answer: CONST.PLAN_QA_ANSWERS?.often || "Your plan is designed for 3 sessions per week. Each session takes about 15–20 minutes. Consistency matters more than intensity — even 2 sessions is better than none." },
      { key: "hurts", label: "What if it hurts?", question: "What if it hurts?", answer: CONST.PLAN_QA_ANSWERS?.hurts || "Mild discomfort during exercises can be normal, but sharp or increasing pain is not. If an exercise causes pain above a 4 out of 10 or gets worse as you go, stop and let me know. I can swap it for an alternative." },
      // More can be added here without code changes
    ],
    [],
  );

  const plan = assessmentData.recoveryPlan || assessmentData.planDetails || CONST.getDummyPlan(assessmentData.problemArea);
  const exercises = plan.exercises || [];
  const subtype = assessmentData.probableSubtype || "NEUTRAL_SENSITIVE";
  const planFocus = CONST.MESSAGING.planFocus[subtype] || "Build Core Support";
  const estTime = plan.estimatedTime || "15-20";
  const latestAssessmentData = JSON.parse(localStorage.getItem("assessmentData") || "{}");
  const finalGSADuration = window.timeTracker.getGSADuration();
  const updatedAssessmentData = { ...latestAssessmentData, exercises, final_conversion_type: "signup", gsaDurationSec: finalGSADuration, viewed_plan: true, completed_assessment: true, session_last_update: new Date().toISOString() };

  useScreenVoice({
    screenName: "plan_screen",
    flowName: "flow_plan_v2",
    payloadKey: "plan_screen",
    exitReason: "sign-up",
    commands: {
      ANSWER_QNA_QUESTION: (value, speech, intent) => {
        if (window.exitHandlerOpen) return;
        if (intent === "signup_request") {
          setTimeout(() => handleSignUp(), 3000);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command") setTimeout(() => handleSignUp(), 3000);
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        setTimeout(() => handleSignUp(), 3000);
      },
      SIGN_UP: () => {
        if (window.exitHandlerOpen) return;
        setTimeout(() => handleSignUp(), 3000);
      },
    },
  });

  // Helper function to safely encode UTF-8 to base64
  const utf8ToBase64 = (str) => {
    try {
      const utf8Bytes = new TextEncoder().encode(str);
      let binaryString = "";
      utf8Bytes.forEach((byte) => {
        binaryString += String.fromCharCode(byte);
      });
      return btoa(binaryString);
    } catch (error) {
      console.error("Failed to encode data to base64:", error);
      const cleaned = str.replace(/[^\x00-\x7F]/g, "");
      return btoa(cleaned);
    }
  };

  // ── Push divider + Myra messages + plan card on mount ──
  useEffect(() => {
    let cancelled = false;
    async function init() {
      pushToHistory({ type: "voice-indicator", id: nextId() });
      await showMyraMessage(MYRA_MESSAGES.plan?.intro || "Here's your personalized Week 1 Recovery Plan.", 1000);
      if (!cancelled) setReady(true);
    }
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Push Myra Q&A prompt after plan card is visible ──
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    async function showPromptWithTyping() {
      // Show typing animation first
      setIsTypingQaPrompt(true);
      await new Promise((resolve) => setTimeout(resolve, 800));
      if (!cancelled) {
        setIsTypingQaPrompt(false);
        setShowQaPrompt(true);
      }
    }
    showPromptWithTyping();
    return () => {
      cancelled = true;
    };
  }, [ready]);

  // Analytics + flow start on mount
  useEffect(() => {
    // gtag conversion — plan reached
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18002481001/Kf-vCLqv7YUcEOmeoIhD",
      });
    }

    // Screen tracking
    MyraInteraction?.onScreenEnter?.("plan_screen");
    window.screenTracker?.enterScreen("plan_screen");
    window.initFromUserGesture?.();
    window.mayraCode?.setIsVisible(false);

    // Send payload BEFORE startFlow
    const payload = generatePayloadForScreen("plan_screen", assessmentData);
    VoiceService?.sendMidJourneyData?.(payload);

    // Send QnA data bundle
    VoiceService?.setQnAData?.({
      userProfile: assessmentData.biometrics,
      currentPlan: assessmentData.planDetails,
      planRationale: assessmentData.planRationaleSpeech,
      sessionId: window.trSessionId,
    });

    VoiceService._currentFlow = "flow_plan_v2";
    window.currentFlow = "flow_plan_v2";
    window.mayraCode?.startFlow?.("flow_plan_v2");

    // Save full data
    if (AnalyticsService && AnalyticsService.saveFullData) {
      try {
        const sanitizedData = { ...assessmentData };
        if (sanitizedData.planRationaleSpeech) {
          sanitizedData.planRationaleSpeech = sanitizedData.planRationaleSpeech.substring(0, 5000);
        }
        const encoded = utf8ToBase64(JSON.stringify(sanitizedData));
        AnalyticsService.saveFullData(encoded);
      } catch (error) {
        console.error("Failed to save full data to analytics:", error);
        try {
          const { planRationaleSpeech, ...dataWithoutRationale } = assessmentData;
          const encoded = utf8ToBase64(JSON.stringify(dataWithoutRationale));
          AnalyticsService.saveFullData(encoded);
        } catch (fallbackError) {
          console.error("Fallback encoding also failed:", fallbackError);
        }
      }
    }

    // Save user journey
    saveUserJourney(assessmentData);
    if (window.mayraCode && rationaleSpeech) {
      window.mayraCode.doSay(fullSpeech);
    }

    return () => {
      window.screenTracker?.exitScreen("plan_screen", { exitReason: "plan_viewed" });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Command registration
  useEffect(() => {
    window.currentCommandsAudio = {
      ANSWER_QNA_QUESTION: (value, speech, intent) => {
        if (window.exitHandlerOpen) return;
        if (intent === "signup_request") {
          setTimeout(() => handleSignUp(), 3000);
        }
      },
      ANSWER_MidJourneyFlow_QUESTION: (value, speech, intent, extracted) => {
        if (window.exitHandlerOpen) return;
        if (value === "continue_command") setTimeout(() => handleSignUp(), 3000);
      },
      MOVE_ON: () => {
        if (window.exitHandlerOpen) return;
        setTimeout(() => handleSignUp(), 3000);
      },
      SIGN_UP: () => {
        if (window.exitHandlerOpen) return;
        setTimeout(() => handleSignUp(), 3000);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSignUp() {
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18002481001/mZpjCLO7gYYcEOmeoIhD",
      });
    }
    await saveUserJourney(updatedAssessmentData);
    var subDomain = "live";
    if (window.location.host !== "www.startfreept.com" && window.location.host !== "startfreept.com" && window.location.host !== "virdiohealth.com" && window.location.host !== "www.virdiohealth.com") {
      subDomain = "uat";
    }
    window.location.replace("https://" + subDomain + ".virdiohealth.com/register?self-assessment=true&fromsite=" + window.location.host + "&data=" + window.trSessionId);
  }

  function toggleRationale(index) {
    setShowRationale((prev) => ({ ...prev, [index]: !prev[index] }));
  }

  function handlePlanQa(key, questionText, answer) {
    // Mark button as used
    setUsedQaButtons((prev) => ({ ...prev, [key]: true }));
    // Open Q&A overlay with pre-loaded answer
    qaRef?.current?.addExchange?.(questionText, answer, "plan_qa");
    onOpenQnA?.();
  }

  if (!ready) return null;

  return (
    <>
      {/* Plan card */}
      <div className={styles.inlineUi}>
        <div className={styles.planScreen}>
          <div className={styles.planMissionCard}>
            <span className={styles.planMissionLabel}>Your Focus</span>
            <span className={styles.planMissionText}>{planFocus}</span>
          </div>

          <p className={styles.planProgramSummary}>
            <strong>{exercises.length} exercises</strong> · ~<strong>{estTime} min</strong> · <strong>3× per week</strong>
          </p>

          <div className={styles.planExerciseScrollWrapper}>
            <div className={styles.planExerciseList}>
              {exercises.map((ex, i) => {
                const sideLabel = ex.performance_side ? ` — ${ex.performance_side} Leg` : "";
                const holdText = ex.hold ? ` | ${ex.hold}s hold` : "";
                const hasValidImage = typeof ex.image === "string" && ex.image.trim().length > 0;
                return (
                  <React.Fragment key={i}>
                    <div className={styles.planExerciseCard}>
                      {/* <div className={styles.planExThumbnail}>
                        
                        <span>{ex.name.substring(0, 2).toUpperCase()}</span>
                      </div> */}

                      <div className={styles.planExThumbnail} style={{ backgroundColor: hasValidImage ? "transparent" : "#e2e8f0" }}>
                        {hasValidImage ? <img src={ex.image} alt={ex.name} className={styles.exerciseThumbnailImage} /> : <span className={styles.exerciseThumbnailLabel}>{ex.name.substring(0, 2).toUpperCase()}</span>}
                      </div>
                      <div className={styles.planExText}>
                        <h4 className={styles.planExName}>
                          {ex.name}
                          {sideLabel}
                        </h4>
                        <p className={styles.planExDesc}>
                          {ex.sets} Sets of {ex.reps} Reps{holdText}
                        </p>
                      </div>
                      <button className={styles.planExInfo} onClick={() => toggleRationale(i)}>
                        ℹ️
                      </button>
                    </div>
                    {showRationale[i] && (
                      <div className={styles.planExRationale}>
                        <p>
                          <strong>Why it helps:</strong> {ex.rationale}
                        </p>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <button className={styles.planEmailLink} onClick={() => setShowEmailForm((prev) => !prev)}>
            Send me the plan via email
          </button>
          {showEmailForm && (
            <div className={styles.planEmailForm}>
              <input type="text" className={styles.formInput} placeholder="Your first name" style={{ marginBottom: 8 }} />
              <input type="email" className={styles.formInput} placeholder="Enter your email" style={{ marginBottom: 8 }} />
              <button
                className={`${styles.videoBtn} ${styles.videoBtnPrimary}`}
                onClick={() => {
                  /* email send logic */
                }}
              >
                Send Plan
              </button>
            </div>
          )}
        </div>
      </div>
      <div className={styles.planQaPrompt}>
        <div className={styles.planQaPromptAvatar}>M</div>
        {isTypingQaPrompt ? (
          <div className={styles.typingDotsInline}>
            <span />
            <span />
            <span />
          </div>
        ) : (
          showQaPrompt && <div className={styles.planQaPromptBubble}>Ask me anything about your plan — I'm here.</div>
        )}
      </div>

      {/* Q&A buttons — data-driven, extensible */}
      <div className={styles.inlineUi}>
        <div className={styles.planQaRow} id="planQaBtns">
          {PLAN_QA_ITEMS.map((item) => (
            <button key={item.key} className={`${styles.quickReply} ${usedQaButtons[item.key] ? styles.quickReplyUsed : ""}`} onClick={() => handlePlanQa(item.key, item.question, item.answer)} disabled={!!usedQaButtons[item.key]}>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Signup CTA — always last */}
      <div className={styles.inlineUi}>
        <div className={styles.planCtaCard}>
          <h3 className={styles.planCtaHeading}>Ready to continue your recovery?</h3>
          <button className={styles.btnSafe} onClick={handleSignUp} style={{ marginTop: 8, width: "100%" }}>
            Sign Up — It's Free
          </button>
        </div>
      </div>
    </>
  );
}

function saveUserJourney(assessmentData) {
  const screenFlow = window.screenTracker?.getScreenFlow() || [];
  const arEntry1 = screenFlow.find((s) => s.screen === "movement_test_1");
  const arEntry2 = screenFlow.find((s) => s.screen === "movement_test_2");

  const payload = {
    session_id: window.trSessionId,
    screen_flow: screenFlow,
    myra_engagement: {
      landing: {
        engaged: window.myraEngagedGsa || false,
        messageCount: window.landingMessageCount || 0,
        topic: window.landingTopic || null,
      },
      gsa: {
        engaged: window.myraEngagedGsa || false,
        messageCount: window.gsaMessageCount || 0,
        topic: window.flowGsaTopic || null,
      },
      post_plan: {
        engaged: window.myraEngagedPlanV2 || false,
        messageCount: window.planV2MessageCount || 0,
        topic: window.planV2Topic || null,
      },
    },
    test1: {
      videoWatched: assessmentData.test1VideoWatched,
      videoDuration: assessmentData.test1VideoDuration,
      arDuration: arEntry1?.duration || null,
      rom: assessmentData.rom,
      symptomResponse: assessmentData.symptomResponse,
    },
    test2: {
      videoWatched: assessmentData.test2VideoWatched,
      videoDuration: assessmentData.test2VideoDuration,
      arDuration: arEntry2?.duration || null,
      rom: assessmentData.rom2,
      symptomResponse: assessmentData.symptomResponseConfirm,
    },
    plan: {
      aiLogId: assessmentData.aiLogId,
      latencyMs: assessmentData.aiLatencyMs,
    },
  };

  fetch("/api/v1/assessment/updateUserJourneyData", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

// ════════════════════════════════════════════════════════════════
// CROSS-CUTTING: EXIT HANDLER
// Context-aware exit overlay with structured reason capture.
// Myra-routed reasons open QnA overlay with deterministic message.
// ════════════════════════════════════════════════════════════════

function ExitHandler({ currentStep, assessmentData, onClose, onOpenQnA }) {
  const [showLeaving, setShowLeaving] = useState(false);
  const [showPainReferral, setShowPainReferral] = useState(false);
  const [showModePicker, setShowModePicker] = useState(false);
  const [pendingReason, setPendingReason] = useState(null);
  const [feedback, setFeedback] = useState("");

  // Set exit handler flag
  useEffect(() => {
    window.exitHandlerOpen = true;
    return () => {
      window.exitHandlerOpen = false;
    };
  }, []);

  // Determine context
  function getContext() {
    const step = currentStep || "";
    if (step.includes("personalize")) return "personalize";
    if (step.includes("movement_test") || step.includes("self_report") || step.includes("test1_demo") || step.includes("test2_demo") || step.includes("camera") || step.includes("rpe") || step.includes("feel_affect") || step.includes("feedback2")) return "movement_test";
    return "gsa";
  }

  const ctx = getContext();
  const config = CONST.EXIT_CONFIGS[ctx];

  function handleTile(reason) {
    if (reason === "leave") {
      setShowLeaving(true);
      return;
    }

    if (reason === "pain") {
      setShowPainReferral(true);
      // Analytics
      if (AnalyticsService) {
        AnalyticsService.trackStep({
          ...assessmentData,
          exitReason: reason,
          exitStep: currentStep,
          context_type: ctx,
          outcome: "PAIN_REFERRAL",
          comm_mode: MyraInteraction?.getCurrentMode?.() || "button",
          screenFlow: window.screenTracker?.getScreenFlow() || [],
        });
      }
      return;
    }

    // Myra-routed: check if communication mode is selected
    const currentMode = MyraInteraction?.getCurrentMode?.();
    if (!currentMode) {
      setPendingReason(reason);
      setShowModePicker(true);
      return;
    }

    routeToMyra(reason);
  }

  async function routeToMyra(reason, mode = "chat") {
    const msg = CONST.EXIT_MYRA_MESSAGES[reason];
    const tileTitle = config.tiles.find((t) => t.reason === reason)?.title || reason;
    const flowToStart = window.currentFlow || VoiceService._currentFlow;
    window.audioPlayer?.ttsPcmAbort?.();
    if (window?.videoController) {
      window?.videoController?.pause();
    }

    // Analytics
    if (AnalyticsService) {
      AnalyticsService.trackStep({
        ...assessmentData,
        exitReason: reason,
        exitStep: currentStep,
        context_type: ctx,
        outcome: "MYRA_CHAT",
        comm_mode: MyraInteraction?.getCurrentMode?.() || "button",
        screenFlow: window.screenTracker?.getScreenFlow() || [],
      });
    }
    if (mode === "voice") {
      const granted = await window.requestMicrophonePermission({
        onGrant: () => {
          if (VoiceService._flowReady) {
            VoiceService.startFlowWithoutAnnouncement(flowToStart, "", msg);
          } else {
            VoiceService.startFlowWithoutAnnouncement(flowToStart, "", msg);
            VoiceService._ws.send(JSON.stringify({ type: "say", text: msg }));
          }
        },
        onDeny: () => {
          MyraInteraction.switchMode("chat");
          onOpenQnA(tileTitle, msg, "exit_" + reason);
        },
      });
    } else if (mode === "chat") {
      onOpenQnA(tileTitle, msg, "exit_" + reason);
    }
    onClose();
  }

  function handleModeSelected(mode) {
    MyraInteraction?.switchMode?.(mode);
    setShowModePicker(false);
    if (pendingReason) {
      routeToMyra(pendingReason, mode);
    }
  }

  function confirmExit() {
    // Analytics
    if (AnalyticsService) {
      AnalyticsService.trackStep({
        ...assessmentData,
        exitReason: "want_to_leave",
        exitStep: currentStep,
        context_type: ctx,
        outcome: "EXIT",
        feedback: feedback.trim() || null,
        comm_mode: MyraInteraction?.getCurrentMode?.() || "button",
        screenFlow: window.screenTracker?.getScreenFlow() || [],
      });
    }

    // Goodbye message
    const goodbye = MYRA_MESSAGES.exit?.confirmExitGoodbye || "Thanks for giving the assessment a try. If you ever want to come back, I'll be here. Take care.";
    VoiceService?.doSay(goodbye);

    // Full cleanup
    window.audioPlayer?.ttsPcmAbort?.();
    window.mayraCode?.forceStop();
    window.mayraCode?.setIsVisible(false);
    window.destroyOwnedResources?.();
    window.destroyFlutterResources?.();
    window.exitHandlerOpen = false;
    localStorage.setItem("loadAfterReloadAutoHome", "1");
    localStorage.removeItem("trSessionId");
    localStorage.removeItem("assessmentData");
    localStorage.removeItem("userCondition");
    window.trSessionId = null;

    onClose();
    window.location.href = "/?gsa=v2";
  }

  if (!config) return null;

  // Determine which sub-screen to show
  let content;

  if (showModePicker) {
    content = (
      <>
        <h2 className={styles.exitHeading}>Choose How to Chat with Myra</h2>
        <p className={styles.exitSubheading}>Pick your preferred way to communicate before we connect you.</p>
        <div className={styles.modeSelectOptions}>
          <button className={styles.modeSelectBtn} onClick={() => handleModeSelected("voice")}>
            <span className={styles.modeSelectIcon}>🎤</span>
            <div>
              <span className={styles.modeSelectLabel}>Talk</span>
              <span className={styles.modeSelectDesc}>Voice — recommended</span>
            </div>
          </button>
          <button className={styles.modeSelectBtn} onClick={() => handleModeSelected("chat")}>
            <span className={styles.modeSelectIcon}>💬</span>
            <div>
              <span className={styles.modeSelectLabel}>Chat</span>
              <span className={styles.modeSelectDesc}>Type your responses</span>
            </div>
          </button>
          <button className={styles.modeSelectBtn} onClick={() => handleModeSelected("button")}>
            <span className={styles.modeSelectIcon}>👆</span>
            <div>
              <span className={styles.modeSelectLabel}>Tap Responses</span>
              <span className={styles.modeSelectDesc}>Use on-screen buttons</span>
            </div>
          </button>
        </div>
        <button
          className={styles.ehBack}
          onClick={() => {
            setShowModePicker(false);
            setPendingReason(null);
          }}
        >
          Go back
        </button>
      </>
    );
  } else if (showPainReferral) {
    content = (
      <>
        <div style={{ textAlign: "center", fontSize: "32px", marginBottom: 8 }}>🩺</div>
        <h2 className={styles.exitHeading}>We Recommend Seeing a Provider</h2>
        <p className={styles.exitSubheading} style={{ textAlign: "left" }}>
          If you're experiencing increased pain, we'd strongly recommend consulting with a healthcare provider before continuing. They can evaluate your symptoms and let you know when it's safe to proceed with an exercise program.
        </p>
        <p className={styles.exitSubheading} style={{ textAlign: "left", marginBottom: 16 }}>
          You can always come back and restart the assessment when you're ready.
        </p>
        <button
          className={styles.btnSafe}
          onClick={() => {
            window.location.href = "/";
          }}
          style={{ width: "100%" }}
        >
          Find a Provider
        </button>
        <button className={styles.ehBack} onClick={() => setShowPainReferral(false)}>
          Go back
        </button>
      </>
    );
  } else if (showLeaving) {
    content = (
      <>
        <h2 className={styles.ehLeavingHeading}>Before you go</h2>
        <p className={styles.ehLeavingSub}>Your progress won't be saved, but you can restart anytime.</p>
        <textarea className={styles.ehFeedbackArea} placeholder="Any feedback on how we can improve? (optional)" value={feedback} onChange={(e) => setFeedback(e.target.value)} />
        <button className={styles.ehExitBtn} onClick={confirmExit}>
          Exit to landing page
        </button>
        <button
          className={styles.ehBack}
          onClick={() => {
            setShowLeaving(false);
          }}
        >
          Stay here
        </button>
      </>
    );
  } else {
    content = (
      <>
        <h2 className={styles.exitHeading}>{config.heading}</h2>
        <p className={styles.exitSubheading}>{config.subheading}</p>
        {config.tiles.map((tile) => (
          <div key={tile.reason} className={styles.ehTile} onClick={() => handleTile(tile.reason)}>
            <div className={styles.ehTileTitle}>{tile.title}</div>
            <div className={styles.ehTileDesc}>{tile.desc}</div>
          </div>
        ))}
        <button className={styles.ehBack} onClick={onClose}>
          I changed my mind — take me back
        </button>
      </>
    );
  }

  return (
    <div
      className={styles.exitOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.exitCard}>
        <div className={styles.exitCardInner}>{content}</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// CROSS-CUTTING: INACTIVITY MODAL
// Triggered by AUTO_PAUSE command from backend.
// Resume or exit options.
// ════════════════════════════════════════════════════════════════

function InactivityModal({ onResume, onExit }) {
  return (
    <div
      className={styles.exitOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onResume();
      }}
    >
      <div className={styles.exitCard}>
        <div className={styles.exitCardInner} style={{ textAlign: "center" }}>
          <h2 className={styles.exitHeading}>Still there?</h2>
          <p className={styles.exitSubheading}>Take your time — I've paused the assessment. When you're ready, just tap below to continue.</p>
          <button className={styles.btnSafe} onClick={onResume} style={{ width: "100%", marginTop: 12 }}>
            I'm ready — let's continue
          </button>
          <button className={styles.ehBack} onClick={onExit}>
            I want to exit
          </button>
        </div>
      </div>
    </div>
  );
}
